import {
  AlignLeft,
  ArrowLeft,
  Bell,
  Bike,
  CalendarClock,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronRight,
  Dumbbell,
  Edit3,
  Ellipsis,
  Eye,
  Footprints,
  Heart,
  Link2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Maximize2,
  MessageCircle,
  Mountain,
  PlusCircle,
  Repeat,
  Route as RouteIcon,
  Search,
  Send,
  Share,
  Share2,
  ShieldCheck,
  Snowflake,
  SquarePlus,
  Trash2,
  User,
  UserPlus,
  Users,
  UsersRound,
  Waves,
  X,
  Zap,
  type LucideIcon
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlanStravaActivity, StravaIndicator, StravaRoutePicker, StravaSettings } from "./components/Strava";
import RouteMap from "./components/RouteMap";
import { clearStravaDrafts, consumePlanDraft, normalizeRoute, savePlanDraft } from "./lib/strava";
import type { RouteSuggestion } from "../../shared/strava";
import type { Session } from "@supabase/supabase-js";
import {
  formatMonthLabel,
  formatPlanDate,
  formatPlanTime,
  formatRelativeTime,
  initials,
  monthBucketKey,
  relationshipLabel
} from "./lib/format";
import { isActivityTitleReady, resolveActivityPlanTitle } from "./lib/activityTitle";
import { canUseWebPush, requestAndRegisterWebPush, syncGrantedWebPush } from "./lib/push";
import {
  authRedirectPath,
  nextPathFromSearch,
  parseRoute,
  resolveGuestPublicLinkTarget,
  type AppRoute
} from "./lib/routes";
import {
  consumePendingInvite,
  forgetGuestGjeng,
  forgetGuestPlan,
  gjengUrl,
  inviteUrl,
  peekGuestGjeng,
  peekGuestPlan,
  peekPendingInvite,
  planUrl,
  rememberGuestPlan,
  rememberPendingInvite,
  shareOrCopy
} from "./lib/share";
import {
  encodeCommentBody,
  findActiveMention,
  matchFriends,
  parseCommentSegments,
  type Mention
} from "./lib/mentions";
import {
  currentHomeScreenPromptEnvironment,
  dismissHomeScreenPrompt,
  isInAppBrowser,
  isIosSafari,
  isStandalonePwa,
  shouldShowHomeScreenPrompt
} from "./lib/installPrompt";
import {
  currentInviteNudgeEnvironment,
  markInviteNudgeSeen,
  shouldShowInviteNudge
} from "./lib/inviteNudge";
import {
  dismissGettingStarted,
  hasDismissedGettingStarted,
  shouldShowGettingStarted
} from "./lib/gettingStarted";
import {
  currentGjengIntroEnvironment,
  markGjengIntroSeen,
  shouldShowGjengIntro
} from "./lib/gjengIntro";
import {
  planLocationPayload,
  searchPlaces as searchPlaceOptions,
  type PlaceOption
} from "./lib/places";
import {
  buildTimeSuggestionOptions,
  formatSuggestedTime,
  fromLocalDateTimeValue,
  toLocalDateTimeValue,
  validateSuggestedTime
} from "./lib/timeSuggestions";
import {
  getInitialSession,
  isSupabaseConfigured,
  requireSupabase,
  supabase
} from "./lib/supabase";
import type {
  Friend,
  FriendList,
  FriendRequest,
  FriendSuggestion,
  GjengDetail,
  GjengInvite,
  GjengMember,
  GjengMemberPreview,
  GjengPendingInvite,
  GjengSummary,
  NameSearchResult,
  NotificationItem,
  PlanActivityId,
  PlanDetail,
  PlanParticipant,
  PlanSummary,
  PlanTimeSuggestion,
  Profile,
  ReactionActor,
  ReactionKey,
  ReactionSummaryItem,
  PublicGjengInvitePreview,
  PublicPlanPreview,
  PublicProfile,
  RelationshipState
} from "./types";

type LoadState = "idle" | "loading" | "ready" | "error";

const AVATAR_BUCKET = "avatars";
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_URL_TTL_SECONDS = 60 * 60;
const PROFILE_COLUMNS = "id,full_name,invite_code,avatar_path,onboarding_completed,is_guest";
const SUPPORTED_AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/heic", "image/heif"]);
const PUSH_PROMPT_STORAGE_PREFIX = "join_push_prompted";
const APP_REFRESH_DEBOUNCE_MS = 350;
const avatarUrlCache = new Map<string, { url: string; expiresAt: number }>();
const planDetailDateFormatter = new Intl.DateTimeFormat("nb-NO", {
  weekday: "short",
  day: "numeric",
  month: "short"
});
const planDetailTimeFormatter = new Intl.DateTimeFormat("nb-NO", {
  hour: "2-digit",
  minute: "2-digit"
});

type ActivityOption = {
  id: PlanActivityId;
  label: string;
  title: string;
  hint: string;
  icon: LucideIcon;
  requiresCustomTitle?: boolean;
};

const activityOptions = [
  { id: "run", label: "Løping", title: "Løpetur", hint: "Rolig, tempo eller intervall", icon: RouteIcon },
  { id: "ride", label: "Sykling", title: "Sykkeltur", hint: "Landevei, grus eller by", icon: Bike },
  { id: "bouldering", label: "Buldring", title: "Buldring", hint: "Hall eller utefelt", icon: Mountain },
  { id: "strength", label: "Styrke", title: "Styrkeøkt", hint: "Gym eller egenvekt", icon: Dumbbell },
  { id: "walk", label: "Gåtur", title: "Gåtur", hint: "Kort runde eller markatur", icon: Footprints },
  { id: "swim", label: "Svømming", title: "Svømming", hint: "Basseng eller ute", icon: Waves },
  { id: "ski", label: "Ski", title: "Skitur", hint: "Rolig tur eller drag", icon: Snowflake },
  { id: "other", label: "Annen", title: "Annen", hint: "Skriv selv", icon: Edit3, requiresCustomTitle: true }
] as const satisfies readonly ActivityOption[];
const fallbackActivityOption = activityOptions.find((activity) => activity.id === "other") ?? activityOptions[0];

type ActivityId = PlanActivityId;

const durationHourOptions = Array.from({ length: 13 }, (_, hour) => hour);
const durationMinuteOptions = Array.from({ length: 12 }, (_, index) => index * 5);
// One-tap durations for the common cases; anything else lives behind
// "Egendefinert" (the timer/minute dropdowns). Matches the time-chip row above.
const durationPresets = [
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 t" },
  { minutes: 90, label: "1,5 t" },
  { minutes: 120, label: "2 t" },
  { minutes: 180, label: "3 t" }
] as const;
const createPlanTimePresets = [
  { id: "tonight", label: "I kveld" },
  { id: "afterWork", label: "Etter jobb" },
  { id: "nowPlus30", label: "Nå +30 min" }
] as const;

const reactionOptions: Array<{ key: ReactionKey; emoji: string }> = [
  { key: "heart", emoji: "❤️" },
  { key: "fire", emoji: "🔥" },
  { key: "yes", emoji: "🙌" },
  { key: "laugh", emoji: "😂" },
  { key: "cry", emoji: "😢" },
  { key: "eyes", emoji: "👀" }
];

// Keep the old surprised face renderable in historical summaries without
// reinterpreting it as the newly introduced crying reaction.
const reactionOptionByKey = new Map<ReactionKey, { key: ReactionKey; emoji: string }>([
  ...reactionOptions.map((option) => [option.key, option] as const),
  ["wow", { key: "wow", emoji: "😮" }]
]);

type CreatePlanTimePresetId = (typeof createPlanTimePresets)[number]["id"];

type FriendListEditorState =
  | { mode: "create" }
  | { mode: "edit"; list: FriendList }
  | null;

// Gjenger holds the tab spot Varsler had; notifications moved to a bell with
// an unread badge (top-right cluster on mobile pages, sidebar on desktop).
// A pull surface fits "notify for action, not attention" better than a tab.
const navItems = [
  { path: "/", label: "Hjem", icon: CalendarDays, route: "home" },
  { path: "/friends", label: "Venner", icon: Users, route: "friends" },
  { path: "/plans/new", label: "Ny plan", icon: PlusCircle, route: "createPlan" },
  { path: "/gjenger", label: "Gjenger", icon: UsersRound, route: "gjenger" },
  { path: "/profile", label: "Profil", icon: User, route: "profile" }
] as const;

export default function App() {
  const [route, setRoute] = useState<AppRoute>(() => parseRoute(window.location.pathname));
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendSuggestions, setFriendSuggestions] = useState<FriendSuggestion[]>([]);
  const [friendLists, setFriendLists] = useState<FriendList[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [recentPastPlans, setRecentPastPlans] = useState<PlanSummary[]>([]);
  const [gjenger, setGjenger] = useState<GjengSummary[]>([]);
  const [gjengInvites, setGjengInvites] = useState<GjengInvite[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [pushPrompt, setPushPrompt] = useState<string | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const syncedPushProfileRef = useRef<string | null>(null);
  const entryPushPromptProfileRef = useRef<string | null>(null);

  const unreadCount = notifications.filter((item) => !item.read_at).length;

  const navigate = useCallback((path: string) => {
    window.history.pushState({}, "", path);
    setRoute(parseRoute(path));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const refreshApp = useCallback(
    async (activeSession?: Session | null) => {
      const resolvedSession = activeSession ?? sessionRef.current;
      if (!isSupabaseConfigured || !resolvedSession) return;
      setLoadState("loading");
      try {
        const client = requireSupabase();
        const userId = resolvedSession.user.id;
        const nextProfile = await loadProfile(userId);
        if (sessionRef.current?.user.id !== userId) return;
        setProfile(nextProfile);

        if (nextProfile?.onboarding_completed) {
          // Lazily extend recurring gjeng series before reading the feeds, so
          // newly-due occurrences are materialized. Non-fatal.
          await client.rpc("maintain_plan_series").then(undefined, () => undefined);
          const [friendData, listData, planData, pastPlanData, gjengData, gjengInviteData, notificationData] = await Promise.all([
            loadFriends(userId),
            loadFriendLists(userId),
            loadPlans(),
            // Non-fatal: a missing/failing past-plans RPC must not break the home feed.
            loadPastPlans(null, 4).catch(() => [] as PlanSummary[]),
            // Non-fatal for the same reason: the crews list must never take Home down.
            loadGjenger().catch(() => [] as GjengSummary[]),
            loadGjengInvites().catch(() => [] as GjengInvite[]),
            loadNotifications()
          ]);
          if (sessionRef.current?.user.id !== userId) return;
          setFriends(friendData.friends);
          setFriendSuggestions(friendData.suggestions);
          setFriendLists(listData);
          setFriendRequests(friendData.requests);
          setPlans(planData);
          setRecentPastPlans(pastPlanData);
          setGjenger(gjengData);
          setGjengInvites(gjengInviteData);
          setNotifications(notificationData);
        } else {
          setFriends([]);
          setFriendSuggestions([]);
          setFriendLists([]);
          setFriendRequests([]);
          setPlans([]);
          setRecentPastPlans([]);
          setGjenger([]);
          setGjengInvites([]);
          setNotifications([]);
        }

        setLoadState("ready");
      } catch (error) {
        if (sessionRef.current?.user.id !== resolvedSession.user.id) return;
        setLoadState("error");
        setMessage(errorMessage(error));
      }
    },
    []
  );

  const refreshVisibleApp = useCallback(() => {
    const activeSession = sessionRef.current;
    if (!activeSession) return;
    void refreshApp(activeSession);
  }, [refreshApp]);

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    // Remember the invite token before sign-in redirects drop it from the URL,
    // so we can return the new user to the invite welcome screen after onboarding.
    if (route.name === "invite") rememberPendingInvite(route.token);
  }, [route]);

  useEffect(() => {
    // After a magic link lands on /auth/callback, forward an already-onboarded
    // user to the invite/plan they came from: prefer the token carried in the
    // link (works cross-device), then fall back to the locally remembered one.
    if (!session || !profile?.onboarding_completed || route.name !== "auth") return;

    const nextPath = nextPathFromSearch(window.location.search);
    if (nextPath) {
      navigate(nextPath);
      return;
    }

    const pendingInvite = consumePendingInvite();
    if (pendingInvite) navigate(`/i/${pendingInvite}`);
  }, [navigate, profile?.onboarding_completed, route.name, session]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoadState("ready");
      return;
    }

    let mounted = true;
    getInitialSession()
      .then((initialSession) => {
        if (!mounted) return;
        sessionRef.current = initialSession;
        setSession(initialSession);
        if (initialSession) void refreshApp(initialSession);
        else setLoadState("ready");
      })
      .catch((error) => {
        setLoadState("error");
        setMessage(errorMessage(error));
      });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      const previousUser = sessionRef.current?.user.id;
      const changedUser = previousUser !== nextSession?.user.id;
      sessionRef.current = nextSession;
      setSession(nextSession);
      if (changedUser || !nextSession) {
        if (!nextSession || previousUser) clearStravaDrafts();
        syncedPushProfileRef.current = null;
        setProfile(null);
        setFriends([]);
        setFriendSuggestions([]);
        setFriendLists([]);
        setFriendRequests([]);
        setPlans([]);
        setRecentPastPlans([]);
        setGjenger([]);
        setGjengInvites([]);
        setNotifications([]);
        setLoadState("ready");
      }
      if (nextSession) void refreshApp(nextSession);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [refreshApp]);

  useEffect(() => {
    if (!session || !profile?.onboarding_completed) return;

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshVisibleApp();
    };

    window.addEventListener("focus", refreshVisibleApp);
    window.addEventListener("join:strava-updated", refreshVisibleApp);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refreshVisibleApp);
      window.removeEventListener("join:strava-updated", refreshVisibleApp);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [profile?.onboarding_completed, refreshVisibleApp, session]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase || !session || !profile?.onboarding_completed) return;

    const client = supabase;
    const userId = session.user.id;
    let refreshTimer: number | null = null;
    const scheduleRefresh = () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        refreshVisibleApp();
      }, APP_REFRESH_DEBOUNCE_MS);
    };
    const scheduleNotificationRefresh = (payload: { new: Record<string, unknown> }) => {
      const record = payload.new as Partial<NotificationItem>;
      if (record.kind === "friend_request" && record.body) {
        setMessage(record.body);
      }
      scheduleRefresh();
    };

    client.realtime.setAuth(session.access_token);
    const channel = client
      .channel(`join-user-updates:${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        scheduleNotificationRefresh
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "friend_requests", filter: `addressee_id=eq.${userId}` },
        scheduleRefresh
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "friend_requests", filter: `requester_id=eq.${userId}` },
        scheduleRefresh
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.warn("Could not subscribe to realtime app updates");
        }
      });

    return () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      void client.removeChannel(channel);
    };
  }, [profile?.onboarding_completed, refreshVisibleApp, session]);

  const afterMeaningfulAction = useCallback((reason: string) => {
    if (!canUseWebPush()) return;
    if (Notification.permission === "granted") {
      void syncGrantedWebPush().catch((error: unknown) => {
        console.warn("Could not sync web push subscription", error);
      });
      return;
    }
    if (hasCurrentPushPromptBeenShown()) return;
    setPushPrompt(reason);
  }, []);

  useEffect(() => {
    if (!session || !profile?.onboarding_completed) return;
    if (syncedPushProfileRef.current === profile.id) return;
    if (!canUseWebPush() || Notification.permission !== "granted") return;

    syncedPushProfileRef.current = profile.id;
    syncGrantedWebPush().catch((error: unknown) => {
      syncedPushProfileRef.current = null;
      console.warn("Could not sync web push subscription", error);
    });
  }, [profile, session]);

  useEffect(() => {
    if (!session || !profile?.onboarding_completed) return;
    if (!canUseWebPush() || Notification.permission !== "default") return;
    if (hasCurrentPushPromptBeenShown()) return;
    if (entryPushPromptProfileRef.current === profile.id) return;

    entryPushPromptProfileRef.current = profile.id;
    setPushPrompt("Slå på varsler så får du beskjed når venner inviterer deg eller planer endres.");
  }, [profile, session]);

  const content = useMemo(() => {
    if (!isSupabaseConfigured) return <SetupPage />;
    // Legal pages are plain public documents: reachable logged in or out, and
    // referenced from the auth screen, so they must render before any gating.
    if (route.name === "legal") {
      return <LegalPage page={route.page} navigate={navigate} />;
    }
    if (loadState === "loading" && !profile) return <LoadingScreen />;
    if (!session) {
      // A logged-out person who opens an open-plan link sees the real public
      // preview and can join as a guest -- no account required up front.
      if (route.name === "planLink") {
        return <PublicPlanLinkPage token={route.token} navigate={navigate} onMessage={setMessage} />;
      }
      if (route.name === "gjengInvite") {
        return <PublicGjengInvitePage token={route.token} navigate={navigate} onMessage={setMessage} />;
      }
      // A cold visitor to the root sees the marketing page, which explains Join
      // before asking them to sign in. Invite/plan links and /auth keep going
      // straight to the auth screen, where the intent context already exists.
      if (route.name === "home") {
        return <LandingPage navigate={navigate} />;
      }
      return <AuthPage route={route} navigate={navigate} onMessage={setMessage} />;
    }
    if (session.user.is_anonymous) {
      // A guest is an anonymous auth user. Route on the session itself, not the
      // profile.is_guest flag: signInAnonymously fires an auth-state change that
      // reloads the profile *before* join_plan_via_link writes is_guest, so the
      // flag races and a fresh guest would briefly look like a normal new user
      // and get pushed into the onboarding form (which then fails -- an anonymous
      // user has no verified email). Guests live entirely in the open-plan
      // experience reached through a share link.
      //
      // The one exception: a guest who chooses "Opprett konto" must be able to
      // reach the auth screen to upgrade. Without this, peekGuestPlan() below
      // would always route them back to the plan preview -- a dead end where a
      // guest could never become a real user. AuthPage links the new identity to
      // this anonymous session (see isGuestUpgrade), so the plan they already
      // joined is preserved.
      if (route.name === "auth") {
        return <AuthPage route={route} navigate={navigate} onMessage={setMessage} isGuestUpgrade />;
      }
      const guestLinkTarget = resolveGuestPublicLinkTarget(route, {
        planToken: peekGuestPlan
      });
      if (guestLinkTarget?.name === "planLink") {
        return <PublicPlanLinkPage token={guestLinkTarget.token} navigate={navigate} onMessage={setMessage} />;
      }
      if (guestLinkTarget?.name === "gjengInvite") {
        return <PublicGjengInvitePage token={guestLinkTarget.token} navigate={navigate} onMessage={setMessage} />;
      }
      return <GuestLanding navigate={navigate} />;
    }
    if (!profile?.onboarding_completed) {
      const arrivedViaLink =
        route.name === "invite" ||
        route.name === "gjengInvite" ||
        peekPendingInvite() !== null ||
        peekGuestGjeng() !== null ||
        nextPathFromSearch(window.location.search) !== null;
      return (
        <OnboardingPage
          userId={session.user.id}
          promptInvite={!arrivedViaLink}
          navigate={navigate}
          onCompleted={(nextProfile) => {
            setProfile(nextProfile);
            void refreshApp(session);
            if (route.name === "invite") return;
            const nextPath = nextPathFromSearch(window.location.search);
            if (nextPath) {
              navigate(nextPath);
              return;
            }
            const pendingInvite = consumePendingInvite();
            if (pendingInvite) {
              navigate(`/i/${pendingInvite}`);
              return;
            }
            // A guest who just upgraded and onboarded: send them to the plan they
            // joined as a guest, then drop the remembered token.
            const guestPlan = peekGuestPlan();
            if (guestPlan) {
              forgetGuestPlan();
              navigate(`/p/${guestPlan}`);
              return;
            }
            const guestGjeng = peekGuestGjeng();
            if (guestGjeng) {
              forgetGuestGjeng();
              navigate(`/g/${guestGjeng}`);
              return;
            }
            if (route.name === "auth" || route.name === "onboarding") navigate("/");
          }}
          onMessage={setMessage}
        />
      );
    }

    return (
      <Shell
        key={session.user.id}
        route={route}
        profile={profile}
        hasEngagement={
          friends.length > 0 ||
          plans.some((plan) => plan.host_id === profile.id) ||
          recentPastPlans.some((plan) => plan.host_id === profile.id)
        }
        unreadCount={unreadCount}
        navigate={navigate}
        onMessage={setMessage}
      >
        {route.name === "home" && (
          <HomePage profile={profile} plans={plans} recentPastPlans={recentPastPlans} friends={friends} gjenger={gjenger} unreadCount={unreadCount} navigate={navigate} refresh={() => refreshApp(session)} onMessage={setMessage} />
        )}
        {route.name === "friends" && (
          <FriendsPage
            currentUserId={session.user.id}
            currentUserName={profile.full_name || "Noen"}
            friends={friends}
            friendSuggestions={friendSuggestions}
            friendLists={friendLists}
            friendRequests={friendRequests}
            navigate={navigate}
            refresh={() => refreshApp(session)}
            afterMeaningfulAction={afterMeaningfulAction}
            onMessage={setMessage}
          />
        )}
        {route.name === "createPlan" && (
          <CreatePlanPage
            key={session.user.id}
            currentUserId={session.user.id}
            friends={friends}
            friendLists={friendLists}
            gjenger={gjenger}
            initialGjengId={route.gjengId ?? null}
            navigate={navigate}
            refresh={() => refreshApp(session)}
            afterMeaningfulAction={afterMeaningfulAction}
            onMessage={setMessage}
          />
        )}
        {route.name === "gjenger" && (
          <GjengerPage
            gjenger={gjenger}
            gjengInvites={gjengInvites}
            friends={friends}
            unreadCount={unreadCount}
            navigate={navigate}
            refresh={() => refreshApp(session)}
            onMessage={setMessage}
          />
        )}
        {route.name === "gjeng" && (
          <GjengDetailPage
            gjengId={route.id}
            currentUserId={session.user.id}
            friends={friends}
            navigate={navigate}
            refresh={() => refreshApp(session)}
            afterMeaningfulAction={afterMeaningfulAction}
            onMessage={setMessage}
          />
        )}
        {route.name === "pastPlans" && (
          <PastPlansPage navigate={navigate} onMessage={setMessage} />
        )}
        {route.name === "plan" && (
          <PlanDetailPage
            planId={route.id}
            currentUserId={session.user.id}
            currentUserName={profile.full_name || "Noen"}
            currentUserIsGuest={profile.is_guest}
            friends={friends}
            initialPlan={plans.find((plan) => plan.id === route.id)}
            navigate={navigate}
            refresh={() => refreshApp(session)}
            afterMeaningfulAction={afterMeaningfulAction}
            onMessage={setMessage}
          />
        )}
        {route.name === "invite" && (
          <InviteRoute
            token={route.token}
            refresh={() => refreshApp(session)}
            afterMeaningfulAction={afterMeaningfulAction}
            onMessage={setMessage}
          />
        )}
        {route.name === "planLink" && (
          <PublicPlanLinkPage token={route.token} navigate={navigate} onMessage={setMessage} />
        )}
        {route.name === "gjengInvite" && (
          <PublicGjengInvitePage token={route.token} navigate={navigate} onMessage={setMessage} />
        )}
        {route.name === "notifications" && (
          <NotificationsPage
            notifications={notifications}
            refresh={() => refreshApp(session)}
            navigate={navigate}
            onMessage={setMessage}
          />
        )}
        {route.name === "profile" && (
          <ProfilePage
            profile={profile}
            onMessage={setMessage}
            onProfileUpdated={(nextProfile) => setProfile(nextProfile)}
            onSignedOut={() => navigate("/auth")}
          />
        )}
        {route.name === "userProfile" && (
          route.userId === session.user.id ? (
            <ProfilePage
              profile={profile}
              onMessage={setMessage}
              onProfileUpdated={(nextProfile) => setProfile(nextProfile)}
              onSignedOut={() => navigate("/auth")}
            />
          ) : (
            <UserProfilePage
              userId={route.userId}
              navigate={navigate}
              refresh={() => refreshApp(session)}
              afterMeaningfulAction={afterMeaningfulAction}
              onMessage={setMessage}
            />
          )
        )}
        {route.name === "auth" || route.name === "onboarding" ? (
          <HomePage profile={profile} plans={plans} recentPastPlans={recentPastPlans} friends={friends} gjenger={gjenger} unreadCount={unreadCount} navigate={navigate} refresh={() => refreshApp(session)} onMessage={setMessage} />
        ) : null}
      </Shell>
    );
  }, [
    afterMeaningfulAction,
    friendLists,
    friendRequests,
    friendSuggestions,
    friends,
    gjenger,
    gjengInvites,
    loadState,
    navigate,
    notifications,
    plans,
    recentPastPlans,
    profile,
    refreshApp,
    route,
    session,
    unreadCount
  ]);

  return (
    <>
      {content}
      {message && (
        <Toast message={message} onDismiss={() => setMessage(null)} />
      )}
      {pushPrompt && (
        <PushPrompt
          reason={pushPrompt}
          onClose={() => {
            markCurrentPushPromptShown();
            setPushPrompt(null);
          }}
          onEnabled={(text, registered) => {
            if (registered || Notification.permission === "denied") {
              markCurrentPushPromptShown();
            }
            setPushPrompt(null);
            setMessage(text);
          }}
        />
      )}
    </>
  );
}

function currentPushPromptStorageKey() {
  return `${PUSH_PROMPT_STORAGE_PREFIX}:${import.meta.env.VITE_VAPID_PUBLIC_KEY || "unconfigured"}`;
}

function hasCurrentPushPromptBeenShown() {
  return window.localStorage.getItem(currentPushPromptStorageKey()) === "1";
}

function markCurrentPushPromptShown() {
  window.localStorage.setItem(currentPushPromptStorageKey(), "1");
}

async function loadFriends(currentUserId: string) {
  const client = requireSupabase();
  const [
    { data: friendshipRows, error: friendshipsError },
    { data: requestRows, error: requestsError },
    { data: suggestionRows, error: suggestionsError }
  ] =
    await Promise.all([
      client.from("friendships").select("user_low_id,user_high_id"),
      client.rpc("list_friend_requests"),
      client.rpc("suggest_friends", { result_limit: 10 })
    ]);
  if (friendshipsError) throw friendshipsError;
  if (requestsError) throw requestsError;
  if (suggestionsError) throw suggestionsError;

  const friendIds = new Set<string>();
  (friendshipRows || []).forEach((row: { user_low_id: string; user_high_id: string }) => {
    friendIds.add(row.user_low_id === currentUserId ? row.user_high_id : row.user_low_id);
  });

  let friends: Friend[] = [];
  if (friendIds.size > 0) {
    const { data: profileRows, error } = await client
      .from("profiles")
      .select("id,full_name,invite_code,avatar_path")
      .in("id", [...friendIds]);
    if (error) throw error;
    friends = ((profileRows || []) as Profile[])
      .map((row) => ({
        id: row.id,
        full_name: row.full_name || "Join-bruker",
        invite_code: row.invite_code,
        avatar_path: row.avatar_path
      }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, "nb"));
  }

  const requests: FriendRequest[] = ((requestRows || []) as Array<{
    request_id: string;
    direction: "incoming" | "outgoing";
    profile_id: string;
    full_name: string;
    invite_code: string;
  }>).map((row) => ({
    id: row.profile_id,
    request_id: row.request_id,
    direction: row.direction,
    relationship_state: row.direction === "incoming" ? "incoming_request" : "outgoing_request",
    full_name: row.full_name,
    invite_code: row.invite_code,
    avatar_path: null
  }));

  const suggestions = ((suggestionRows || []) as FriendSuggestion[]).map((row) => ({
    ...row,
    full_name: row.full_name || "Join-bruker"
  }));

  return { friends, requests, suggestions };
}

async function loadProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await requireSupabase()
    .from("profiles")
    .select(PROFILE_COLUMNS)
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return normalizeProfile(data);
}

async function loadFriendLists(ownerId: string): Promise<FriendList[]> {
  const client = requireSupabase();
  const [
    { data: listRows, error: listsError },
    { data: memberRows, error: membersError }
  ] = await Promise.all([
    client
      .from("friend_lists")
      .select("id,owner_id,name")
      .eq("owner_id", ownerId)
      .order("name", { ascending: true }),
    client
      .from("friend_list_members")
      .select("list_id,friend_id")
  ]);
  if (listsError) throw listsError;
  if (membersError) throw membersError;

  const membersByList = new Map<string, string[]>();
  ((memberRows || []) as Array<{ list_id: string; friend_id: string }>).forEach((row) => {
    membersByList.set(row.list_id, [...(membersByList.get(row.list_id) || []), row.friend_id]);
  });

  return ((listRows || []) as Array<{ id: string; owner_id: string; name: string }>).map((row) => ({
    id: row.id,
    owner_id: row.owner_id,
    name: row.name,
    member_ids: membersByList.get(row.id) || []
  }));
}

async function insertFriendListMembers(listId: string, memberIds: string[]) {
  if (memberIds.length === 0) return;

  const { error } = await requireSupabase()
    .from("friend_list_members")
    .insert(memberIds.map((friendId) => ({ list_id: listId, friend_id: friendId })));
  if (error) throw error;
}

async function loadPlans(): Promise<PlanSummary[]> {
  const { data, error } = await requireSupabase().rpc("list_visible_plans", {
    cursor_starts_at: null,
    cursor_id: null,
    page_size: 30
  });
  if (error) throw error;
  return ((data || []) as PlanSummaryResult[]).map(normalizePlanSummary);
}

// Cursor for the past-plans feed: the (ends_at, id) of the last row received,
// passed back to page further into history. null means start from the newest.
type PastPlanCursor = { ends_at: string; id: string } | null;

async function loadPastPlans(
  cursor: PastPlanCursor = null,
  pageSize = 20
): Promise<PlanSummary[]> {
  const { data, error } = await requireSupabase().rpc("list_past_plans", {
    cursor_ends_at: cursor?.ends_at ?? null,
    cursor_id: cursor?.id ?? null,
    page_size: pageSize
  });
  if (error) throw error;
  return ((data || []) as PlanSummaryResult[]).map(normalizePlanSummary);
}

async function loadProfilePlans(targetUserId: string, pageSize = 30): Promise<PlanSummary[]> {
  const { data, error } = await requireSupabase().rpc("list_profile_plans", {
    target_user_id: targetUserId,
    page_size: pageSize
  });
  if (error) throw error;
  return ((data || []) as PlanSummaryResult[]).map(normalizePlanSummary);
}

async function loadNotifications(): Promise<NotificationItem[]> {
  const { data, error } = await requireSupabase().rpc("list_notifications", {
    page_size: 50
  });
  if (error) throw error;
  return (data || []) as NotificationItem[];
}

type GjengSummaryResult = Omit<GjengSummary, "activity_id" | "member_preview" | "next_plan"> & {
  activity_id: string | null;
  member_preview: unknown;
  next_plan: unknown;
};

async function loadGjenger(): Promise<GjengSummary[]> {
  const { data, error } = await requireSupabase().rpc("list_gjenger");
  if (error) throw error;
  return ((data || []) as GjengSummaryResult[]).map(normalizeGjengSummary);
}

function normalizeGjengSummary(row: GjengSummaryResult): GjengSummary {
  return {
    ...row,
    activity_id: normalizeActivityId(row.activity_id),
    member_preview: normalizeGjengMemberPreviews(row.member_preview),
    next_plan: normalizeGjengNextPlan(row.next_plan)
  };
}

function normalizeActivityId(value: string | null | undefined): PlanActivityId | null {
  const match = activityOptions.find((activity) => activity.id === value);
  return match ? match.id : null;
}

function normalizeGjengMemberPreviews(value: unknown): GjengMemberPreview[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = stringValue(item.id);
    const fullName = stringValue(item.full_name);
    if (!id || !fullName) return [];
    return [{ id, full_name: fullName, avatar_path: nullableString(item.avatar_path) }];
  });
}

function normalizeGjengNextPlan(value: unknown): GjengSummary["next_plan"] {
  if (!isRecord(value)) return null;
  const id = stringValue(value.id);
  const title = stringValue(value.title);
  const startsAt = stringValue(value.starts_at);
  if (!id || !title || !startsAt) return null;
  return { id, title, starts_at: startsAt };
}

function normalizeGjengMembers(value: unknown): GjengMember[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = stringValue(item.id);
    const fullName = stringValue(item.full_name);
    if (!id || !fullName) return [];
    return [{
      id,
      full_name: fullName,
      avatar_path: nullableString(item.avatar_path),
      is_creator: booleanValue(item.is_creator),
      relationship_state: relationshipStateValue(item.relationship_state),
      request_id: nullableString(item.request_id)
    }];
  });
}

// Pending invites in the detail view: everyone in the crew sees who has been
// asked, so nobody double-invites; only the inviter can withdraw.
export function normalizeGjengPendingInvites(value: unknown): GjengPendingInvite[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const inviteId = stringValue(item.invite_id);
    const inviteeId = stringValue(item.invitee_id);
    const fullName = stringValue(item.full_name);
    if (!inviteId || !inviteeId || !fullName) return [];
    return [{
      invite_id: inviteId,
      invitee_id: inviteeId,
      full_name: fullName,
      avatar_path: nullableString(item.avatar_path),
      inviter_id: stringValue(item.inviter_id) ?? "",
      inviter_name: stringValue(item.inviter_name) ?? "Join-bruker",
      viewer_is_inviter: booleanValue(item.viewer_is_inviter),
      invited_at: stringValue(item.invited_at) ?? ""
    }];
  });
}

type GjengDetailResult = Omit<GjengDetail, "activity_id" | "members" | "pending_invites"> & {
  activity_id: string | null;
  members: unknown;
  pending_invites: unknown;
};

async function loadGjengDetail(gjengId: string): Promise<GjengDetail | null> {
  const { data, error } = await requireSupabase().rpc("get_gjeng_detail", {
    target_gjeng_id: gjengId
  });
  if (error) throw error;
  const row = ((data || []) as GjengDetailResult[])[0];
  if (!row) return null;
  return {
    ...row,
    activity_id: normalizeActivityId(row.activity_id),
    members: normalizeGjengMembers(row.members),
    pending_invites: normalizeGjengPendingInvites(row.pending_invites)
  };
}

type GjengInviteResult = Omit<GjengInvite, "activity_id" | "member_preview"> & {
  activity_id: string | null;
  member_preview: unknown;
};

async function loadGjengInvites(): Promise<GjengInvite[]> {
  const { data, error } = await requireSupabase().rpc("list_gjeng_invites");
  if (error) throw error;
  return ((data || []) as GjengInviteResult[]).map((row) => ({
    ...row,
    activity_id: normalizeActivityId(row.activity_id),
    member_preview: normalizeGjengMemberPreviews(row.member_preview)
  }));
}

async function loadGjengPlans(gjengId: string, pageSize = 30): Promise<PlanSummary[]> {
  const { data, error } = await requireSupabase().rpc("list_gjeng_plans", {
    target_gjeng_id: gjengId,
    page_size: pageSize
  });
  if (error) throw error;
  return ((data || []) as PlanSummaryResult[]).map(normalizePlanSummary);
}

type ReactionTarget = "plan" | "comment";
type ReactionMutationResult = {
  viewer_reaction: string | null;
  reaction_summary: unknown;
};

async function toggleTargetReaction(
  target: ReactionTarget,
  targetId: string,
  reactionKey: ReactionKey
): Promise<{ viewerReaction: ReactionKey | null; summary: ReactionSummaryItem[] }> {
  const rpcName = target === "plan" ? "toggle_plan_reaction" : "toggle_plan_comment_reaction";
  const payload =
    target === "plan"
      ? { target_plan_id: targetId, reaction_key: reactionKey }
      : { target_comment_id: targetId, reaction_key: reactionKey };
  const { data, error } = await requireSupabase().rpc(rpcName, payload);
  if (error) throw error;
  const row = ((data || []) as ReactionMutationResult[])[0];
  return {
    viewerReaction: reactionKeyValue(row?.viewer_reaction),
    summary: normalizeReactionSummary(row?.reaction_summary)
  };
}

async function setTargetReaction(
  target: ReactionTarget,
  targetId: string,
  reactionKey: ReactionKey,
  message: string
): Promise<{ viewerReaction: ReactionKey | null; summary: ReactionSummaryItem[] }> {
  const rpcName = target === "plan" ? "set_plan_reaction" : "set_plan_comment_reaction";
  const payload =
    target === "plan"
      ? { target_plan_id: targetId, reaction_key: reactionKey, reaction_message: message }
      : { target_comment_id: targetId, reaction_key: reactionKey, reaction_message: message };
  const { data, error } = await requireSupabase().rpc(rpcName, payload);
  if (error) throw error;
  const row = ((data || []) as ReactionMutationResult[])[0];
  return {
    viewerReaction: reactionKeyValue(row?.viewer_reaction),
    summary: normalizeReactionSummary(row?.reaction_summary)
  };
}

function normalizeProfile(value: unknown): Profile | null {
  const row = firstRecord(value);
  if (!row) return null;

  const id = stringValue(row.id);
  const inviteCode = stringValue(row.invite_code);
  if (!id || !inviteCode) return null;

  return {
    id,
    full_name: nullableString(row.full_name),
    invite_code: inviteCode,
    avatar_path: nullableString(row.avatar_path),
    onboarding_completed: row.onboarding_completed === true,
    is_guest: row.is_guest === true
  };
}

function firstRecord(value: unknown): Record<string, unknown> | null {
  const item = Array.isArray(value) ? value[0] : value;
  if (!isRecord(item)) return null;

  if (isRecord(item.complete_pwa_profile)) return item.complete_pwa_profile;
  if (isRecord(item.complete_pwa_profile_impl)) return item.complete_pwa_profile_impl;
  return item;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function booleanValue(value: unknown): boolean {
  return value === true;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function reactionKeyValue(value: unknown): ReactionKey | null {
  if (
    value === "heart" ||
    value === "fire" ||
    value === "yes" ||
    value === "laugh" ||
    value === "wow" ||
    value === "cry" ||
    value === "eyes"
  ) {
    return value;
  }

  return null;
}

function normalizeAvatarPath(path?: string | null): string | null {
  const trimmed = path?.trim();
  if (!trimmed) return null;
  const withoutLeadingSlash = trimmed.replace(/^\/+/, "");
  return withoutLeadingSlash.startsWith(`${AVATAR_BUCKET}/`)
    ? withoutLeadingSlash.slice(AVATAR_BUCKET.length + 1)
    : withoutLeadingSlash;
}

function avatarFileExtension(file: File): string {
  if (file.type === "image/png") return "png";
  if (file.type === "image/heic") return "heic";
  if (file.type === "image/heif") return "heif";
  return "jpg";
}

function validateAvatarFile(file: File) {
  if (!SUPPORTED_AVATAR_TYPES.has(file.type)) {
    throw new Error("Velg et bilde i JPEG, PNG, HEIC eller HEIF-format.");
  }

  if (file.size > AVATAR_MAX_BYTES) {
    throw new Error("Profilbildet kan maks være 5 MB.");
  }
}

async function uploadAvatarFile(userId: string, file: File): Promise<string> {
  validateAvatarFile(file);
  const path = `${userId.toLowerCase()}/avatar.${avatarFileExtension(file)}`;
  const { error } = await requireSupabase()
    .storage
    .from(AVATAR_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type,
      upsert: true
    });
  if (error) throw error;
  avatarUrlCache.delete(path);
  return path;
}

async function signedAvatarUrl(path: string): Promise<string> {
  const normalizedPath = normalizeAvatarPath(path);
  if (!normalizedPath) throw new Error("Avatar path is missing.");

  const cached = avatarUrlCache.get(normalizedPath);
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  const { data, error } = await requireSupabase()
    .storage
    .from(AVATAR_BUCKET)
    .createSignedUrl(normalizedPath, AVATAR_URL_TTL_SECONDS);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error("Avatar URL could not be created.");

  avatarUrlCache.set(normalizedPath, {
    url: data.signedUrl,
    expiresAt: Date.now() + (AVATAR_URL_TTL_SECONDS - 60) * 1000
  });
  return data.signedUrl;
}

function SetupPage() {
  return (
    <main className="setup-page">
      <section className="brand-panel">
        <BrandBlock />
        <div className="proof-list">
          <Feature icon={ShieldCheck} title="Privat som standard" text="Lenker viser aldri planinnhold offentlig." />
          <Feature icon={Zap} title="Raskt å planlegge" text="Del en plan med venner på få sekunder." />
          <Feature icon={Lock} title="Venner først" text="Planer er bundet til vennskap og valgt publikum." />
        </div>
      </section>
      <section className="auth-card wide">
        <h1>Sett opp PWA-miljøet</h1>
        <p>
          Legg til Supabase-verdiene i Netlify eller en lokal <code>.env.local</code> før
          autentisering og dataflyt kan testes.
        </p>
        <pre>{`VITE_SUPABASE_URL=...
VITE_SUPABASE_ANON_KEY=...
VITE_SITE_URL=https://join-a.no
VITE_VAPID_PUBLIC_KEY=...`}</pre>
        <p>
          Uten disse viser appen denne skjermen i stedet for å gjøre uautoriserte
          API-kall.
        </p>
      </section>
    </main>
  );
}

function LandingPage({ navigate }: { navigate: (path: string) => void }) {
  const goToAuth = () => navigate("/auth");
  return (
    <main className="landing">
      <header className="landing-nav">
        <div className="landing-wordmark">join</div>
        <div className="landing-nav-actions">
          <button className="landing-nav-login" onClick={goToAuth}>
            Logg inn
          </button>
          <button className="primary-action compact" onClick={goToAuth}>
            Kom i gang
          </button>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-copy">
          <h1>Enklere å gjøre ting sammen.</h1>
          <p className="landing-lead">
            Del hva du skal gjøre med vennene dine. De blir med om det passer.
            Ingen gruppechat, ingen mas.
          </p>
          <div className="landing-cta-row">
            <button className="primary-action" onClick={goToAuth}>
              Kom i gang
            </button>
            <a className="landing-ghost-link" href="#slik-funker">
              Se hvordan det funker
            </a>
          </div>
        </div>
        <div className="landing-hero-visual">
          <PhoneMock />
        </div>
      </section>

      <section className="landing-section" aria-labelledby="landing-what">
        <h2 id="landing-what">Hva Join er</h2>
        <div className="landing-pillars">
          <LandingPillar
            icon={Footprints}
            title="Lavterskel"
            text="Å bli med skal være lett. Å la være er helt greit."
          />
          <LandingPillar
            icon={Zap}
            title="Spontant"
            text="For løpeturen etter jobb, ikke arrangementet om tre uker."
          />
          <LandingPillar
            icon={Users}
            title="De rette folkene"
            text="Del med alle venner eller bare noen få. Du bestemmer hver gang."
          />
        </div>
      </section>

      <section className="landing-section landing-steps" id="slik-funker">
        <h2>Slik funker det</h2>
        <ol className="landing-step-list">
          <LandingStep
            n={1}
            icon={SquarePlus}
            title="Lag en plan"
            text="«Løpetur 17:00, rolig tempo.» Ferdig på under et halvt minutt."
          />
          <LandingStep
            n={2}
            icon={Share2}
            title="Del med venner"
            text="Alle venner, eller bare de du velger. Ingen ser hvem andre du delte med."
          />
          <LandingStep
            n={3}
            icon={CheckCircle2}
            title="Bli med"
            text="Venner ser planen og blir med om det passer. Du ser hvem som kommer."
          />
        </ol>
      </section>

      <section className="landing-section landing-compare">
        <h2>Bedre enn gruppechatten</h2>
        <div className="landing-compare-grid">
          <div className="landing-compare-col landing-compare-col--muted">
            <span className="landing-compare-head">I gruppechatten</span>
            <ul>
              <li>Du må spørre direkte, og lurer på om du maser</li>
              <li>Stillhet føles som nei</li>
              <li>Bare de i chatten ser det</li>
              <li>Planer drukner i meldinger</li>
            </ul>
          </div>
          <div className="landing-compare-col landing-compare-col--join">
            <span className="landing-compare-head">På Join</span>
            <ul>
              <li>Du deler bare hva du skal gjøre</li>
              <li>Stillhet er helt greit</li>
              <li>Alle vennene dine kan se det</li>
              <li>Én plan, med tid, sted og hvem som blir med</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="landing-privacy">
        <div className="landing-privacy-inner">
          <div className="landing-privacy-head">
            <Lock size={22} />
            <h2>Privat som standard</h2>
          </div>
          <ul className="landing-privacy-list">
            <li>Bare inviterte kan se en plan. En delt lenke gir ikke tilgang av seg selv.</li>
            <li>Møtedetaljer vises først når du blir med.</li>
            <li>Ingen offentlige profiler, ingen følgere, ingen «0 blir med».</li>
          </ul>
        </div>
      </section>

      <section className="landing-final">
        <h2>Enklere å gjøre ting sammen.</h2>
        <button className="primary-action" onClick={goToAuth}>
          Kom i gang
        </button>
        <p className="landing-fineprint">Gratis. Ingen algoritme, ingen reklame.</p>
      </section>

      <footer className="landing-footer">
        <div className="landing-wordmark small">join</div>
        <nav className="landing-footer-nav">
          <a
            href="/personvern"
            onClick={(event) => {
              event.preventDefault();
              navigate("/personvern");
            }}
          >
            Personvern
          </a>
          <a
            href="/vilkar"
            onClick={(event) => {
              event.preventDefault();
              navigate("/vilkar");
            }}
          >
            Vilkår
          </a>
          <a href="mailto:hei@join-a.no">Kontakt</a>
        </nav>
      </footer>
    </main>
  );
}

function LandingPillar({
  icon: Icon,
  title,
  text
}: {
  icon: LucideIcon;
  title: string;
  text: string;
}) {
  return (
    <article className="landing-pillar">
      <span className="landing-pillar-icon">
        <Icon size={22} />
      </span>
      <strong>{title}</strong>
      <p>{text}</p>
    </article>
  );
}

function LandingStep({
  n,
  icon: Icon,
  title,
  text
}: {
  n: number;
  icon: LucideIcon;
  title: string;
  text: string;
}) {
  return (
    <li className="landing-step">
      <div className="landing-step-top">
        <span className="landing-step-num">{n}</span>
        <span className="landing-step-icon">
          <Icon size={20} />
        </span>
      </div>
      <strong>{title}</strong>
      <p>{text}</p>
    </li>
  );
}

function PhoneMock() {
  return (
    <div className="lp-phone" aria-hidden="true">
      <div className="lp-phone-frame">
        <div className="lp-phone-notch" />
        <div className="lp-phone-screen">
          <div className="mock-appbar">
            <span className="mock-appbar-title">Hjem</span>
            <span className="mock-avatar mock-avatar--me">M</span>
          </div>
          <div className="mock-eyebrow">Skjer snart</div>
          <div className="mock-card">
            <div className="mock-card-body">
              <div className="mock-meta">I dag 17:00 · rolig tempo</div>
              <div className="mock-title">Løpetur langs elva</div>
              <div className="mock-host">Maja · Sognsvann</div>
            </div>
            <div className="mock-map">
              <span className="mock-pin">
                <MapPin size={16} />
              </span>
            </div>
            <div className="mock-card-foot">
              <div className="mock-avatars">
                <span className="mock-avatar">T</span>
                <span className="mock-avatar">A</span>
                <span className="mock-avatar">J</span>
                <span className="mock-avatar mock-avatar--more">+3</span>
              </div>
              <span className="mock-join">Bli med</span>
            </div>
          </div>
          <div className="mock-eyebrow">Senere</div>
          <div className="mock-row">
            <span className="mock-row-icon">
              <Waves size={18} />
            </span>
            <div className="mock-row-copy">
              <div className="mock-row-title">Morgenbad</div>
              <div className="mock-row-sub">I morgen 07:00 · 5 venner</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegalPage({
  page,
  navigate
}: {
  page: "privacy" | "terms";
  navigate: (path: string) => void;
}) {
  return (
    <main className="legal-page">
      <div className="legal-inner">
        <button
          type="button"
          className="text-button"
          onClick={() => (window.history.length > 1 ? window.history.back() : navigate("/"))}
        >
          <ArrowLeft size={16} />
          Tilbake
        </button>
        {page === "privacy" ? (
          <>
            <h1>Personvernerklæring</h1>
            <p className="muted">Sist oppdatert 3. juli 2026</p>
            <h2>Hva vi lagrer</h2>
            <p>
              Join lagrer det du selv legger inn: navn, eventuelt profilbilde, e-postadresse,
              vennskap, planer, kommentarer og reaksjoner. Vi bruker dette utelukkende til å vise
              innholdet til deg og de du deler med.
            </p>
            <h2>Hvem ser hva</h2>
            <p>
              Join er privat som standard. Planer er bare synlige for dem du deler dem med. Det
              finnes ingen offentlige profiler og ingen følgere.
            </p>
            <h2>Tredjeparter</h2>
            <p>
              Vi bruker Supabase (database og innlogging, servere i EU), Netlify (hosting) og
              Resend (utsending av e-post). Vi selger aldri data og viser ikke reklame.
            </p>
            <h2>Sletting og innsyn</h2>
            <p>
              Vil du slette kontoen din eller få innsyn i dataene dine? Send en e-post til{" "}
              <a href="mailto:hei@join-a.no">hei@join-a.no</a>, så ordner vi det.
            </p>
          </>
        ) : (
          <>
            <h1>Vilkår for bruk</h1>
            <p className="muted">Sist oppdatert 3. juli 2026</p>
            <h2>Tjenesten</h2>
            <p>
              Join lar deg dele planer med venner. Tjenesten er gratis og leveres som den er, uten
              garantier for oppetid.
            </p>
            <h2>Ditt innhold</h2>
            <p>
              Du eier det du legger inn og er ansvarlig for at det er lovlig og ikke krenker
              andre. Vi kan fjerne innhold og stenge kontoer som misbruker tjenesten.
            </p>
            <h2>Kontakt</h2>
            <p>
              Spørsmål om vilkårene? Ta kontakt på{" "}
              <a href="mailto:hei@join-a.no">hei@join-a.no</a>.
            </p>
          </>
        )}
      </div>
    </main>
  );
}

function AuthPage({
  route,
  navigate,
  onMessage,
  isGuestUpgrade = false
}: {
  route: AppRoute;
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
  // True when the caller already has an anonymous (guest) session. We then link
  // the chosen identity to that session instead of creating a brand-new user, so
  // the plan the guest already joined (keyed on their auth.uid()) survives.
  isGuestUpgrade?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Server-side OTP send has a cooldown; surface it as a countdown instead of
  // letting a quick second tap produce a raw rate-limit error toast.
  const [resendCooldown, setResendCooldown] = useState(0);
  // Google blocks OAuth inside in-app browsers (Gmail/Outlook/Facebook webviews),
  // so we hide it there and steer to email, which works everywhere.
  const inAppBrowser = useMemo(() => isInAppBrowser(window.navigator.userAgent), []);
  // Validate shape before enabling send: a typo'd address would silently create
  // a phantom user (shouldCreateUser) and leave the person waiting for an email
  // that never comes.
  const normalizedEmail = email.trim().toLowerCase();
  const emailLooksValid = /^\S+@\S+\.\S+$/.test(normalizedEmail);
  const otpCodeLength = 8;
  const normalizedCode = code.trim();
  const canVerifyCode = normalizedCode.length === otpCodeLength;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);
  const nextPath = nextPathFromSearch(window.location.search);
  const linkContext = isGuestUpgrade
    ? nextPath?.startsWith("/g/")
      ? "Opprett en konto for å bli med i gjengen og få varsler om planene."
      : "Opprett en konto for å beholde planen du ble med på, få varsler og lage egne planer."
    : nextPath?.startsWith("/g/")
      ? "Logg inn eller opprett konto for å bli med i gjengen."
    : route.name === "invite"
      ? "Logg inn for å se hvem som inviterte deg."
      : route.name === "planLink"
        ? "Logg inn for å se hva vennene dine planlegger"
        : route.name === "gjengInvite"
          ? "Logg inn for å bli med i gjengen."
          : "Logg inn eller opprett konto.";

  function buildCallbackUrl(): string {
    const callbackUrl = new URL("/auth/callback", window.location.origin);
    const callbackNextPath = authRedirectPath(route) ?? nextPath;
    if (callbackNextPath) callbackUrl.searchParams.set("next", callbackNextPath);
    return callbackUrl.toString();
  }

  async function sendMagicLink() {
    setBusy(true);
    try {
      // Upgrading a guest: attach the email to the existing anonymous user. This
      // sends a confirmation link and, once confirmed, makes the same user
      // permanent -- no new account, so their joined plan is kept.
      // Note: the guest-upgrade path stays on the email *link* flow. updateUser
      // sends an email_change confirmation, which has different token semantics
      // than the sign-in OTP; guests overwhelmingly live in Safari (they arrived
      // through a share link), so the iOS PWA storage-jar split rarely applies.
      const { error } = isGuestUpgrade
        ? await requireSupabase().auth.updateUser(
            { email: normalizedEmail },
            { emailRedirectTo: buildCallbackUrl() }
          )
        : await requireSupabase().auth.signInWithOtp({
            email: normalizedEmail,
            options: {
              emailRedirectTo: buildCallbackUrl(),
              shouldCreateUser: true
            }
          });
      if (error) throw error;
      setSent(true);
      setResendCooldown(60);
      onMessage(
        isGuestUpgrade
          ? "Vi sendte deg en bekreftelseslenke på e-post."
          : "Vi sendte deg en 8-sifret engangskode på e-post."
      );
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  // Verifying the emailed code completes sign-in *inside* this browsing context.
  // That is the whole point on iOS: a magic link opens in Safari, a separate
  // storage jar from the installed PWA, so the PWA never receives the session.
  // Entering the code here lands the session in the PWA itself, which fixes the
  // "logged out on the home screen" bug. onAuthStateChange then routes away.
  async function verifyCode() {
    if (!canVerifyCode) {
      onMessage(`Skriv inn alle ${otpCodeLength} sifrene i koden.`);
      return;
    }
    setBusy(true);
    try {
      const { error } = await requireSupabase().auth.verifyOtp({
        email: normalizedEmail,
        token: normalizedCode,
        type: "email"
      });
      if (error) throw error;
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function signInWithGoogle() {
    // Guest upgrade links Google to the anonymous user; otherwise it is a normal
    // sign-in. linkIdentity requires manual linking to be enabled in Supabase
    // auth config (enable_manual_linking).
    const { error } = isGuestUpgrade
      ? await requireSupabase().auth.linkIdentity({
          provider: "google",
          options: { redirectTo: buildCallbackUrl() }
        })
      : await requireSupabase().auth.signInWithOAuth({
          provider: "google",
          options: { redirectTo: buildCallbackUrl() }
        });
    if (error) onMessage(errorMessage(error));
  }

  return (
    <main className="auth-page">
      <section className="brand-panel">
        <BrandBlock />
        <div className="proof-list">
          <Feature icon={ShieldCheck} title="Privat som standard" text="Bare inviterte kan se planer og innhold." />
          <Feature icon={Zap} title="Raskt å planlegge" text="Lag og del planer uten gruppechat-støy." />
        </div>
      </section>
      <section className="auth-card">
        <div className="app-mark">join</div>
        <h1>Fortsett til join</h1>
        <p>{linkContext}</p>
        {sent ? (
          <div className="auth-sent">
            {isGuestUpgrade ? (
              <>
                <p>
                  Vi sendte en bekreftelseslenke til <strong>{email}</strong>. Åpne e-posten og trykk på
                  lenken for å fortsette.
                </p>
                <button className="secondary-action" disabled={busy} onClick={sendMagicLink}>
                  <Mail size={18} />
                  Send lenken på nytt
                </button>
              </>
            ) : (
              <>
                <p>
                  Vi sendte en 8-sifret engangskode til <strong>{normalizedEmail}</strong>. Skriv den inn her
                  for å logge inn.
                </p>
                <label>
                  Engangskode
                  <input
                    className="auth-code-input"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    enterKeyHint="done"
                    maxLength={otpCodeLength}
                    pattern="[0-9]*"
                    autoFocus
                    value={code}
                    onChange={(event) => {
                      setCode(event.target.value.replace(/\D/g, "").slice(0, otpCodeLength));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && canVerifyCode && !busy) void verifyCode();
                    }}
                    placeholder="••••••••"
                    aria-label="Engangskode fra e-post"
                  />
                </label>
                <button
                  className="primary-action"
                  disabled={!canVerifyCode || busy}
                  onClick={verifyCode}
                >
                  Logg inn
                </button>
                <button
                  className="secondary-action"
                  disabled={busy || resendCooldown > 0}
                  onClick={sendMagicLink}
                >
                  <Mail size={18} />
                  {resendCooldown > 0 ? `Send på nytt (${resendCooldown} s)` : "Send koden på nytt"}
                </button>
                <p className="auth-hint">
                  Finner du ikke e-posten? Sjekk søppelposten. Du kan også åpne lenken i
                  e-posten fra en vanlig nettleser.
                </p>
              </>
            )}
            <button
              className="text-button"
              onClick={() => {
                setSent(false);
                setCode("");
              }}
            >
              Bruk en annen e-post
            </button>
          </div>
        ) : (
          <>
            <label>
              E-post
              <input
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="navn@eksempel.no"
              />
            </label>
            <button className="primary-action" disabled={!emailLooksValid || busy} onClick={sendMagicLink}>
              <Mail size={18} />
              Fortsett med e-post
            </button>
            {email.trim().length > 3 && !emailLooksValid && (
              <p className="auth-field-hint">Det ser ut som e-postadressen mangler noe.</p>
            )}
            {inAppBrowser ? (
              <p className="auth-hint">
                Vil du bruke Google? Åpne denne lenken i Safari eller Chrome – Google tillater ikke
                innlogging i innebygde nettlesere.
              </p>
            ) : (
              <button className="secondary-action" onClick={signInWithGoogle}>
                <span className="google-dot">G</span>
                Fortsett med Google
              </button>
            )}
            <p className="privacy-copy">
              Ved å fortsette godtar du{" "}
              <a
                href="/vilkar"
                onClick={(event) => {
                  event.preventDefault();
                  navigate("/vilkar");
                }}
              >
                vilkårene
              </a>{" "}
              og{" "}
              <a
                href="/personvern"
                onClick={(event) => {
                  event.preventDefault();
                  navigate("/personvern");
                }}
              >
                personvernerklæringen
              </a>
              .
            </p>
          </>
        )}
      </section>
    </main>
  );
}

function OnboardingPage({
  userId,
  promptInvite,
  navigate,
  onCompleted,
  onMessage
}: {
  userId: string;
  promptInvite: boolean;
  navigate: (path: string) => void;
  onCompleted: (profile: Profile) => void;
  onMessage: (message: string) => void;
}) {
  const [name, setName] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [completedProfile, setCompletedProfile] = useState<Profile | null>(null);
  // Starter suggestions for the connect step: fetched once the profile is
  // complete, so a brand-new user can send their first friend requests before
  // ever seeing an empty home feed.
  const [suggestions, setSuggestions] = useState<FriendSuggestion[]>([]);
  const [sentSuggestionIds, setSentSuggestionIds] = useState<string[]>([]);
  const [suggestionBusyId, setSuggestionBusyId] = useState<string | null>(null);
  const { shareInvite } = useInviteShare(name.trim() || "En venn", onMessage);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(avatarFile);
    setAvatarPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  // OAuth sign-ins (Google) already carry a name and picture in user_metadata;
  // prefill both so onboarding shrinks to a single confirming tap. Email users
  // keep the empty form.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await requireSupabase().auth.getUser();
        if (cancelled) return;
        const metadata = (data.user?.user_metadata ?? {}) as Record<string, unknown>;
        const metaName =
          typeof metadata.full_name === "string" && metadata.full_name.trim()
            ? metadata.full_name.trim()
            : typeof metadata.name === "string"
              ? metadata.name.trim()
              : "";
        if (metaName) setName((current) => current || metaName);

        const pictureUrl =
          typeof metadata.avatar_url === "string"
            ? metadata.avatar_url
            : typeof metadata.picture === "string"
              ? metadata.picture
              : null;
        if (!pictureUrl) return;
        // Best effort: pull the provider avatar into a local file so the
        // profile picture is prefilled too. CORS may block the fetch -- then
        // the manual picker is the fallback, silently.
        const response = await fetch(pictureUrl);
        if (!response.ok) return;
        const blob = await response.blob();
        if (cancelled || !blob.type.startsWith("image/")) return;
        const file = new File([blob], "profile.jpg", { type: blob.type });
        validateAvatarFile(file);
        setAvatarFile((current) => current ?? file);
      } catch {
        // Prefill is a bonus, never a blocker.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!completedProfile) return;
    let cancelled = false;
    requireSupabase()
      .rpc("suggest_friends", { result_limit: 5 })
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setSuggestions(
          ((data || []) as FriendSuggestion[]).filter((row) => row.relationship_state === "none")
        );
      });
    return () => {
      cancelled = true;
    };
  }, [completedProfile]);

  async function addSuggestedFriend(targetId: string) {
    setSuggestionBusyId(targetId);
    try {
      const { error } = await requireSupabase().rpc("send_friend_request", {
        target_user_id: targetId
      });
      if (error) throw error;
      setSentSuggestionIds((current) => [...current, targetId]);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setSuggestionBusyId(null);
    }
  }

  function chooseAvatar(file: File | null) {
    if (!file) {
      setAvatarFile(null);
      return;
    }

    try {
      validateAvatarFile(file);
      setAvatarFile(file);
    } catch (error) {
      onMessage(errorMessage(error));
    }
  }

  async function complete() {
    setBusy(true);
    try {
      const avatarPath = avatarFile ? await uploadAvatarFile(userId, avatarFile) : null;
      const { data, error } = await requireSupabase().rpc("complete_pwa_profile", {
        profile_full_name: name,
        profile_avatar_path: avatarPath
      });
      if (error) throw error;
      const profile = normalizeProfile(data) ?? await loadProfile(userId);
      if (!profile?.onboarding_completed) {
        throw new Error("Profilen ble ikke fullført. Logg ut og inn igjen, og prøv på nytt.");
      }
      if (promptInvite) setCompletedProfile(profile);
      else onCompleted(profile);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (completedProfile) {
    return (
      <main className="onboarding-page">
        <section className="phone-frame solo">
          <div className="screen-header">
            <span />
            <strong>Kom i gang · 2 av 2</strong>
            <span />
          </div>
          <span className="onboarding-hero-icon" aria-hidden="true">
            <Users size={26} />
          </span>
          <h2>Få inn vennene dine</h2>
          <p className="micro-copy">
            En plan på Join er også en invitasjon: del lenken, så kan venner bli med selv om de
            ikke har Join ennå.
          </p>
          {suggestions.length > 0 && (
            <div className="onboarding-suggestions">
              <span className="onboarding-suggestions-title">Kanskje du kjenner</span>
              {suggestions.map((person) => (
                <div key={person.id} className="onboarding-suggestion-row">
                  <Avatar name={person.full_name} avatarPath={person.avatar_path} />
                  <strong>{person.full_name}</strong>
                  {sentSuggestionIds.includes(person.id) ? (
                    <span className="state-chip">Sendt</span>
                  ) : (
                    <button
                      className="secondary-action compact"
                      disabled={suggestionBusyId !== null}
                      onClick={() => void addSuggestedFriend(person.id)}
                    >
                      Legg til
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <button
            className="primary-action"
            onClick={() => {
              onCompleted(completedProfile);
              navigate("/plans/new");
            }}
          >
            <PlusCircle size={18} />
            Lag en plan og del lenken
          </button>
          <button
            className="text-button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const shared = await shareInvite();
              setBusy(false);
              // Advance only on an actual share/copy; a cancelled share sheet
              // keeps the user here so the step never feels like a dead end.
              if (shared) onCompleted(completedProfile);
            }}
          >
            <Share2 size={16} />
            Del invitasjonslenken din
          </button>
          <button className="text-button quiet" onClick={() => onCompleted(completedProfile)}>
            Hopp over for nå
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="onboarding-page">
      <section className="phone-frame solo">
        <div className="screen-header">
          <span />
          <strong>Profil · 1 av 2</strong>
          <span />
        </div>
        <label className="avatar-upload">
          <input
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
            onChange={(event) => {
              chooseAvatar(event.target.files?.[0] ?? null);
              event.currentTarget.value = "";
            }}
          />
          <span className="avatar-picker">
            {avatarPreviewUrl ? <img src={avatarPreviewUrl} alt="" /> : <Camera size={34} />}
          </span>
          <span>{avatarFile ? "Bytt profilbilde" : "Velg profilbilde"}</span>
        </label>
        <label>
          Navn
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Kari Nilsen" />
        </label>
        <p className="micro-copy">Slik vises du for vennene dine på Join.</p>
        <button className="primary-action bottom" disabled={name.trim().length < 2 || busy} onClick={complete}>
          Kom i gang
        </button>
      </section>
    </main>
  );
}

function Shell({
  route,
  profile,
  hasEngagement,
  unreadCount,
  navigate,
  onMessage,
  children
}: {
  route: AppRoute;
  profile: Profile;
  // A friend or an own plan. Cold users see the Home getting-started checklist
  // instead of the banner pair, so the banners wait for engagement -- this also
  // means the install banner appears right when its payoff ("get notified when
  // someone joins") first exists.
  hasEngagement: boolean;
  unreadCount: number;
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
  children: React.ReactNode;
}) {
  const homeScreenPromptEligible = useMemo(
    () => shouldShowHomeScreenPrompt(currentHomeScreenPromptEnvironment()),
    []
  );
  const [homeScreenPromptDismissed, setHomeScreenPromptDismissed] = useState(false);
  const showHomeScreenPrompt =
    homeScreenPromptEligible && hasEngagement && !homeScreenPromptDismissed;
  const [inviteNudgeEligible] = useState(() =>
    shouldShowInviteNudge(currentInviteNudgeEnvironment(false))
  );
  const [inviteNudgeDismissed, setInviteNudgeDismissed] = useState(false);
  const showInviteNudge =
    inviteNudgeEligible && hasEngagement && !showHomeScreenPrompt && !inviteNudgeDismissed;
  const { shareInvite } = useInviteShare(profile.full_name || "Noen", onMessage);

  function closeHomeScreenPrompt() {
    dismissHomeScreenPrompt();
    setHomeScreenPromptDismissed(true);
  }

  function closeInviteNudge() {
    markInviteNudgeSeen();
    setInviteNudgeDismissed(true);
  }

  // Show the invite nudge to every user exactly once: the moment it surfaces we
  // record it as seen so it never returns in a later session.
  useEffect(() => {
    if (showInviteNudge) markInviteNudgeSeen();
  }, [showInviteNudge]);

  return (
    <div className="app-shell">
      <aside className="desktop-sidebar">
        <div className="sidebar-brand-row">
          <button className="wordmark" onClick={() => navigate("/")}>join</button>
          <HeaderBell unreadCount={unreadCount} navigate={navigate} />
        </div>
        <nav>
          {navItems.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              active={route.name === item.route || (item.route === "gjenger" && route.name === "gjeng")}
              unreadCount={0}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>
        <div className="sidebar-profile">
          <Avatar name={profile.full_name} avatarPath={profile.avatar_path} />
          <div>
            <strong>{profile.full_name}</strong>
            <span>@{profile.invite_code.toLowerCase()}</span>
          </div>
        </div>
      </aside>
      <main className="content">
        {showHomeScreenPrompt ? <HomeScreenPrompt onDismiss={closeHomeScreenPrompt} /> : null}
        {showInviteNudge ? (
          <InviteNudge
            onInvite={() => {
              void shareInvite();
              closeInviteNudge();
            }}
            onDismiss={closeInviteNudge}
          />
        ) : null}
        {children}
      </main>
      <nav className="mobile-tabbar">
        {navItems.map((item) => (
          <NavButton
            key={item.path}
            item={item}
            active={route.name === item.route || (item.route === "gjenger" && route.name === "gjeng")}
            unreadCount={0}
            onClick={() => navigate(item.path)}
          />
        ))}
      </nav>
    </div>
  );
}

function HomeScreenPrompt({ onDismiss }: { onDismiss: () => void }) {
  return (
    <section className="home-screen-prompt" role="note" aria-label="Legg Join på Hjem-skjermen">
      <span className="home-screen-prompt-icon">
        <Ellipsis size={20} />
      </span>
      <div className="home-screen-prompt-copy">
        <strong>Legg Join på Hjem-skjermen</strong>
        <p>Da åpnes Join som en app, og du kan få varsler når venner blir med på planene dine. Trykk prikkene nederst til høyre, trykk Del, velg Legg til på Hjem-skjerm, og trykk Legg til.</p>
        <div className="home-screen-prompt-steps" aria-label="Fremgangsmåte">
          <span><Ellipsis size={14} /> 1 Trykk prikkene</span>
          <ChevronRight size={14} aria-hidden="true" />
          <span><Share size={14} /> 2 Del</span>
          <ChevronRight size={14} aria-hidden="true" />
          <span><SquarePlus size={14} /> 3 Legg til</span>
        </div>
      </div>
      <button className="home-screen-prompt-dismiss" onClick={onDismiss} aria-label="Skjul tips" title="Skjul tips">
        <X size={17} />
      </button>
    </section>
  );
}

function InviteNudge({ onInvite, onDismiss }: { onInvite: () => void; onDismiss: () => void }) {
  return (
    <section className="invite-nudge" role="note" aria-label="Inviter vennene dine">
      <span className="invite-nudge-icon">
        <Users size={20} />
      </span>
      <div className="invite-nudge-copy">
        <strong>Mer gøy med venner</strong>
        <p>Join blir bedre når gjengen er med. Inviter noen du vil gjøre ting med.</p>
        <button className="invite-nudge-cta" onClick={onInvite}>
          <Share2 size={16} />
          Inviter venner
        </button>
      </div>
      <button className="home-screen-prompt-dismiss" onClick={onDismiss} aria-label="Skjul tips" title="Skjul tips">
        <X size={17} />
      </button>
    </section>
  );
}

function useInviteShare(inviterName: string, onMessage: (message: string) => void) {
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  // Resolves true only when the invite was actually shared or copied, so callers
  // can advance the flow on success and stay put when the share sheet is
  // cancelled (an AbortError, which is not worth an error toast).
  const shareInvite = useCallback(async (): Promise<boolean> => {
    try {
      let url = inviteLink;
      if (!url) {
        const { data, error } = await requireSupabase().rpc("create_app_invite");
        if (error) throw error;
        const token = (data as Array<{ invite_token: string }>)[0]?.invite_token;
        url = inviteUrl(token);
        setInviteLink(url);
      }
      await shareOrCopy({
        title: `${inviterName} inviterte deg til å joine`,
        text: `${inviterName} inviterte deg til å joine. Bli med!`,
        url
      });
      onMessage("Invitasjonslenken er klar.");
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return false;
      onMessage(errorMessage(error));
      return false;
    }
  }, [inviteLink, inviterName, onMessage]);
  return { inviteLink, shareInvite };
}

function HeaderBell({
  unreadCount,
  navigate
}: {
  unreadCount: number;
  navigate: (path: string) => void;
}) {
  return (
    <button
      className="icon-only header-bell"
      onClick={() => navigate("/notifications")}
      aria-label={unreadCount > 0 ? `Varsler (${unreadCount} uleste)` : "Varsler"}
      title="Varsler"
    >
      <Bell size={19} />
      {unreadCount > 0 && <i className="header-bell-badge">{unreadCount}</i>}
    </button>
  );
}

function HomePage({
  profile,
  plans,
  recentPastPlans,
  friends,
  gjenger,
  unreadCount,
  navigate,
  refresh,
  onMessage
}: {
  profile: Profile;
  plans: PlanSummary[];
  recentPastPlans: PlanSummary[];
  friends: Friend[];
  gjenger: GjengSummary[];
  unreadCount: number;
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  onMessage: (message: string) => void;
}) {
  const { shareInvite } = useInviteShare(profile.full_name || "Noen", onMessage);
  const now = Date.now();
  const upcoming = plans.filter(
    (plan) => plan.status === "scheduled" && new Date(plan.ends_at).getTime() > now
  );
  const hasOwnPlan =
    plans.some((plan) => plan.host_id === profile.id) ||
    recentPastPlans.some((plan) => plan.host_id === profile.id);
  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(() =>
    hasDismissedGettingStarted()
  );
  const showGettingStarted = shouldShowGettingStarted({
    dismissed: gettingStartedDismissed,
    friendsCount: friends.length,
    hasOwnPlan
  });
  // The home-screen step only exists where it applies: iOS Safari in a browser
  // tab. Installed PWAs and other platforms never see it.
  const showHomeScreenStep = useMemo(
    () =>
      isIosSafari(window.navigator.userAgent, window.navigator.platform) && !isStandalonePwa(),
    []
  );
  // Newest-first completed plans (own + friends') come from list_past_plans; the
  // home section shows a bounded handful, with the full history behind "Vis alle".
  const recent = recentPastPlans;
  return (
    <section className="page-grid">
      <div className="main-column">
        <PageTitle
          title="Hjem"
          action={
            <span className="header-cluster">
              <HeaderBell unreadCount={unreadCount} navigate={navigate} />
              <button
                className="icon-only profile-shortcut"
                onClick={() => navigate("/profile")}
                aria-label="Profil"
                title="Profil"
              >
                <Avatar name={profile.full_name} avatarPath={profile.avatar_path} />
              </button>
            </span>
          }
        />
        <div className="home-prompt">
          <div>
            <h2>Hva skjer i dag?</h2>
            <p>Se hva vennene dine skal gjøre.</p>
          </div>
          <button className="icon-only filled" onClick={() => navigate("/plans/new")} aria-label="Ny plan">
            <PlusCircle size={21} />
          </button>
        </div>
        {showGettingStarted && (
          <section className="getting-started" aria-label="Kom i gang">
            <h3>Kom i gang</h3>
            <button
              type="button"
              className={`getting-started-row ${friends.length > 0 ? "done" : ""}`}
              onClick={() => navigate("/friends")}
            >
              <span className="step-check">
                {friends.length > 0 ? <CheckCircle2 size={14} /> : "1"}
              </span>
              <span>Legg til venner</span>
            </button>
            <button
              type="button"
              className={`getting-started-row ${hasOwnPlan ? "done" : ""}`}
              onClick={() => navigate("/plans/new")}
            >
              <span className="step-check">{hasOwnPlan ? <CheckCircle2 size={14} /> : "2"}</span>
              <span>Lag din første plan</span>
            </button>
            {showHomeScreenStep && (
              <div className="getting-started-row">
                <span className="step-check">3</span>
                <span>
                  Legg Join på Hjem-skjermen
                  <small>Trykk Del i nettleseren, så «Legg til på Hjem-skjerm»</small>
                </span>
              </div>
            )}
            <button
              type="button"
              className="getting-started-dismiss"
              aria-label="Skjul kom i gang"
              title="Skjul kom i gang"
              onClick={() => {
                dismissGettingStarted();
                setGettingStartedDismissed(true);
              }}
            >
              <X size={15} />
            </button>
          </section>
        )}
        {upcoming.length === 0 ? (
          friends.length ? (
            <EmptyState
              title="Ingen planer akkurat nå"
              text="Fortell vennene dine hva du skal gjøre."
              action={<button className="primary-action compact" onClick={() => navigate("/plans/new")}>Ny plan</button>}
            />
          ) : (
            <EmptyState
              title="Lag din første plan"
              text="Del noe du skal gjøre – og send lenken til vennene dine. De kan bli med selv om de ikke har Join ennå."
              action={
                <div className="empty-actions">
                  <button className="primary-action compact" onClick={() => navigate("/plans/new")}>
                    <PlusCircle size={17} />
                    Lag en plan
                  </button>
                  <button className="secondary-action compact" onClick={shareInvite}>
                    <Share2 size={17} />
                    Inviter venner
                  </button>
                </div>
              }
            />
          )
        ) : (
          <>
            <section className="home-section">
              <SectionHeading title="Skjer snart" />
              <FeaturedPlanCard
                plan={upcoming[0]}
                currentUserId={profile.id}
                currentUserIsGuest={profile.is_guest}
                onOpen={() => navigate(`/plans/${upcoming[0].id}`)}
                onPlanChanged={refresh}
                onMessage={onMessage}
              />
            </section>
            {upcoming.length > 1 && (
              <section className="home-section">
                <SectionHeading title="Senere" />
                <div className="plan-list">
                  {upcoming.slice(1).map((plan) => (
                    <PlanRow key={plan.id} plan={plan} onOpen={() => navigate(`/plans/${plan.id}`)} onMessage={onMessage} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
        {recent.length > 0 && (
          <section className="home-section">
            <SectionHeading
              title="Nylig"
              action="Vis alle"
              onAction={() => navigate("/plans/past")}
            />
            <div className="plan-list">
              {recent.slice(0, 3).map((plan) => (
                <MemoryPlanRow
                  key={plan.id}
                  plan={plan}
                  onOpen={() => navigate(`/plans/${plan.id}`)}
                  onMessage={onMessage}
                />
              ))}
            </div>
          </section>
        )}
      </div>
      <aside className="side-panel">
        {gjenger.length > 0 && (
          <>
            <div className="panel-heading">
              <h2>Gjenger</h2>
              <button className="icon-only" onClick={() => navigate("/gjenger")} aria-label="Åpne gjenger">
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="friend-stack">
              {gjenger.slice(0, 3).map((gjeng) => (
                <button key={gjeng.id} className="gjeng-mini" onClick={() => navigate(`/gjenger/${gjeng.id}`)}>
                  <GjengAvatarStack members={gjeng.member_preview} />
                  <div>
                    <strong>{gjeng.name}</strong>
                    <span>{gjengHint(gjeng)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="panel-heading">
          <h2>Venner</h2>
          <button className="icon-only" onClick={() => navigate("/friends")} aria-label="Åpne venner">
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="friend-stack">
          {friends.slice(0, 5).map((friend) => (
            <FriendMini key={friend.id} friend={friend} />
          ))}
          {friends.length === 0 && <p className="muted">Lag en plan og del lenken, eller inviter venner.</p>}
        </div>
        {friends.length === 0 ? (
          <button className="primary-action compact" onClick={() => navigate("/plans/new")}>
            <PlusCircle size={17} />
            Lag en plan
          </button>
        ) : (
          <button className="primary-action compact" onClick={() => navigate("/plans/new")}>
            <PlusCircle size={17} />
            Ny plan
          </button>
        )}
      </aside>
    </section>
  );
}

function FriendsPage({
  currentUserId,
  currentUserName,
  friends,
  friendSuggestions,
  friendLists,
  friendRequests,
  navigate,
  refresh,
  afterMeaningfulAction,
  onMessage
}: {
  currentUserId: string;
  currentUserName: string;
  friends: Friend[];
  friendSuggestions: FriendSuggestion[];
  friendLists: FriendList[];
  friendRequests: FriendRequest[];
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  afterMeaningfulAction: (reason: string) => void;
  onMessage: (message: string) => void;
}) {
  const openProfile = (userId: string) => navigate(`/u/${userId}`);
  const [query, setQuery] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [results, setResults] = useState<NameSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const { inviteLink, shareInvite } = useInviteShare(currentUserName, onMessage);
  const [busy, setBusy] = useState(false);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [listBusy, setListBusy] = useState(false);
  const [listEditor, setListEditor] = useState<FriendListEditorState>(null);
  const [showAllFriends, setShowAllFriends] = useState(false);
  const incoming = friendRequests.filter((request) => request.direction === "incoming");
  const outgoing = friendRequests.filter((request) => request.direction === "outgoing");
  const visibleFriends = showAllFriends ? friends : friends.slice(0, 5);

  async function searchByName() {
    setBusy(true);
    try {
      const { data, error } = await requireSupabase().rpc("search_profiles_by_name", {
        search_query: query,
        result_limit: 8
      });
      if (error) throw error;
      setResults((data || []) as NameSearchResult[]);
      setHasSearched(true);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function findByInviteCode() {
    setBusy(true);
    try {
      const { data, error } = await requireSupabase().rpc("find_profile_by_invite_code", {
        exact_invite_code: inviteCode
      });
      if (error) throw error;
      setResults(((data || []) as NameSearchResult[]).map((row) => ({ ...row, avatar_path: null })));
      setHasSearched(true);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function sendRequest(userId: string) {
    setActionBusyId(userId);
    try {
      const { error } = await requireSupabase().rpc("send_friend_request", {
        target_user_id: userId
      });
      if (error) throw error;
      onMessage("Venneforespørsel sendt.");
      await refresh();
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setActionBusyId(null);
    }
  }

  async function acceptRequest(requestId: string, targetId?: string) {
    if (targetId) setActionBusyId(targetId);
    try {
      const { error } = await requireSupabase().rpc("accept_friend_request", {
        target_request_id: requestId
      });
      if (error) throw error;
      onMessage("Dere er venner på Join.");
      afterMeaningfulAction("Få beskjed når venner inviterer deg til planer.");
      await refresh();
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      if (targetId) setActionBusyId(null);
    }
  }

  async function saveFriendList(listId: string | null, name: string, memberIds: string[]) {
    const trimmedName = name.trim();
    const acceptedFriendIds = new Set(friends.map((friend) => friend.id));
    const validMemberIds = [...new Set(memberIds)].filter((id) => acceptedFriendIds.has(id));
    const client = requireSupabase();
    setListBusy(true);
    try {
      if (listId) {
        const { error: updateError } = await client
          .from("friend_lists")
          .update({ name: trimmedName })
          .eq("id", listId);
        if (updateError) throw updateError;

        const { error: deleteMembersError } = await client
          .from("friend_list_members")
          .delete()
          .eq("list_id", listId);
        if (deleteMembersError) throw deleteMembersError;

        await insertFriendListMembers(listId, validMemberIds);
      } else {
        const { data, error: createError } = await client
          .from("friend_lists")
          .insert({ owner_id: currentUserId, name: trimmedName })
          .select("id,owner_id,name")
          .single();
        if (createError) throw createError;

        const createdList = data as { id: string };
        try {
          await insertFriendListMembers(createdList.id, validMemberIds);
        } catch (error) {
          await client.from("friend_lists").delete().eq("id", createdList.id);
          throw error;
        }
      }

      setListEditor(null);
      await refresh();
      onMessage(listId ? "Vennelisten er oppdatert." : "Vennelisten er opprettet.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setListBusy(false);
    }
  }

  async function deleteFriendList(listId: string) {
    setListBusy(true);
    try {
      const { error } = await requireSupabase()
        .from("friend_lists")
        .delete()
        .eq("id", listId);
      if (error) throw error;
      setListEditor(null);
      await refresh();
      onMessage("Vennelisten er slettet.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setListBusy(false);
    }
  }

  return (
    <section className="stack-page">
      <PageTitle
        title="Venner"
        action={<IconButton label="Ny venneliste" icon={PlusCircle} onClick={() => setListEditor({ mode: "create" })} />}
      />

      <div className="split-list">
        <section>
          <h2>Finn venner</h2>
          <div className="search-row">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setHasSearched(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") void searchByName();
              }}
              placeholder="Søk etter navn"
            />
            <button disabled={query.trim().length < 2 || busy} onClick={searchByName}>Søk</button>
          </div>
          <button className="secondary-action" onClick={shareInvite}>
            <Share2 size={18} />
            Inviter en venn
          </button>
          {results.map((result) => (
            <PersonRow
              key={result.id}
              person={result}
              onOpen={() => openProfile(result.id)}
              action={
                <RelationshipAction
                  state={result.relationship_state}
                  onClick={() => sendRequest(result.id)}
                />
              }
            />
          ))}
          {hasSearched && results.length === 0 && !busy && (
            <InviteFallback onCreate={shareInvite} inviteLink={inviteLink} />
          )}
        </section>

        <section>
          <h2>Venneforespørsler</h2>
          {incoming.map((request) => (
            <PersonRow
              key={request.request_id}
              person={request}
              onOpen={() => openProfile(request.id)}
              action={<button className="secondary-action compact" onClick={() => acceptRequest(request.request_id)}>Godta</button>}
            />
          ))}
          {outgoing.map((request) => (
            <PersonRow key={request.request_id} person={request} onOpen={() => openProfile(request.id)} action={<span className="state-chip">Sendt</span>} />
          ))}
          {incoming.length + outgoing.length === 0 && <p className="muted">Ingen ventende forespørsler.</p>}
        </section>
      </div>

      {friendSuggestions.length > 0 && (
        <section className="list-section">
          <h2>Kanskje du kjenner</h2>
          {friendSuggestions.map((suggestion) => (
            <PersonRow
              key={suggestion.id}
              person={suggestion}
              subtitle={friendSuggestionLabel(suggestion)}
              onOpen={() => openProfile(suggestion.id)}
              action={
                <ProfileRelationshipAction
                  state={suggestion.relationship_state}
                  requestId={suggestion.request_id ?? null}
                  busy={actionBusyId === suggestion.id}
                  onSend={() => sendRequest(suggestion.id)}
                  onAccept={(requestId) => acceptRequest(requestId, suggestion.id)}
                />
              }
            />
          ))}
        </section>
      )}

      <section className="list-section">
        <SectionHeading
          title="Venner"
          action={
            friends.length > 5
              ? showAllFriends
                ? "Vis færre"
                : `Vis alle (${friends.length})`
              : undefined
          }
          onAction={friends.length > 5 ? () => setShowAllFriends((current) => !current) : undefined}
        />
        {friends.length === 0 ? (
          <EmptyState
            title="Ingen venner ennå"
            text="Del invitasjonslenken din med noen du vil gjøre ting med, eller søk dem opp med navn."
            action={
              <button className="primary-action compact" onClick={shareInvite}>
                <Share2 size={17} />
                Inviter venner
              </button>
            }
          />
        ) : (
          visibleFriends.map((friend) => (
            <PersonRow key={friend.id} person={{ ...friend, relationship_state: "friends" }} onOpen={() => openProfile(friend.id)} />
          ))
        )}
      </section>

      <section className="list-section">
        <h2>Vennelister</h2>
        <p className="muted">
          Bare du kan se navnene og hvem som er i listene – en privat snarvei når du
          deler planer. Vil dere ha et felles sted med delte planer og historikk,
          lag heller en gjeng under Gjenger.
        </p>
        {friendLists.length === 0 ? (
          <div className="empty-action-stack">
            <p className="muted">Lag en liste for venner du ofte inviterer sammen.</p>
            <button className="primary-action compact" onClick={() => setListEditor({ mode: "create" })}>
              <PlusCircle size={17} />
              Lag liste
            </button>
          </div>
        ) : (
          friendLists.map((list) => (
            <FriendListRow
              key={list.id}
              list={list}
              friends={friends}
              onEdit={() => setListEditor({ mode: "edit", list })}
            />
          ))
        )}
      </section>

      <details className="fallback-panel">
        <summary>Eksakt invitasjonskode</summary>
        <div className="inline-form">
          <input
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value)}
            placeholder="A1B2C3D4E5"
          />
          <button onClick={findByInviteCode} disabled={inviteCode.trim().length < 6 || busy}>Finn</button>
        </div>
      </details>
      {listEditor && (
        <FriendListEditor
          state={listEditor}
          friends={friends}
          busy={listBusy}
          onClose={() => setListEditor(null)}
          onSave={saveFriendList}
          onDelete={deleteFriendList}
        />
      )}
    </section>
  );
}

function FriendListRow({
  list,
  friends,
  onEdit
}: {
  list: FriendList;
  friends: Friend[];
  onEdit: () => void;
}) {
  const friendById = new Map(friends.map((friend) => [friend.id, friend]));
  const members = list.member_ids
    .map((memberId) => friendById.get(memberId))
    .filter((friend): friend is Friend => Boolean(friend));
  const summary = members.length === 0
    ? "Ingen venner valgt"
    : members.map((friend) => friend.full_name).join(", ");

  return (
    <button className="friend-list-row" onClick={onEdit}>
      <div className="friend-list-avatars" aria-hidden="true">
        {members.slice(0, 3).map((friend) => (
          <Avatar key={friend.id} name={friend.full_name} avatarPath={friend.avatar_path} />
        ))}
        {members.length === 0 && <Users size={18} />}
      </div>
      <div>
        <strong>{list.name}</strong>
        <span>{members.length} venner</span>
        <small>{summary}</small>
      </div>
      <Edit3 size={17} />
    </button>
  );
}

function FriendListEditor({
  state,
  friends,
  busy,
  onClose,
  onSave,
  onDelete
}: {
  state: Exclude<FriendListEditorState, null>;
  friends: Friend[];
  busy: boolean;
  onClose: () => void;
  onSave: (listId: string | null, name: string, memberIds: string[]) => Promise<void>;
  onDelete: (listId: string) => Promise<void>;
}) {
  const existingList = state.mode === "edit" ? state.list : null;
  const [name, setName] = useState(existingList?.name || "");
  const [memberIds, setMemberIds] = useState<string[]>(existingList?.member_ids || []);
  const canSave = name.trim().length >= 2;

  function toggleMember(friendId: string, selected: boolean) {
    setMemberIds((current) =>
      selected
        ? [...new Set([...current, friendId])]
        : current.filter((id) => id !== friendId)
    );
  }

  return (
    <div className="modal-backdrop">
      <section className="modal friend-list-modal">
        <div className="modal-header">
          <div>
            <h2>{existingList ? "Rediger venneliste" : "Ny venneliste"}</h2>
            <p>Bare du kan se navnet og hvem som er i listen.</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk">x</button>
        </div>
        <label>
          Navn
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="For eksempel Nære venner"
          />
        </label>
        <div className="editor-list">
          <span className="field-label">Venner</span>
          {friends.length === 0 ? (
            <EmptyState title="Ingen venner ennå" text="Vennelister kan bare inneholde venner." />
          ) : (
            friends.map((friend) => (
              <label key={friend.id} className="check-row">
                <input
                  type="checkbox"
                  checked={memberIds.includes(friend.id)}
                  onChange={(event) => toggleMember(friend.id, event.target.checked)}
                />
                <Avatar name={friend.full_name} avatarPath={friend.avatar_path} />
                <span>{friend.full_name}</span>
              </label>
            ))
          )}
        </div>
        <div className="modal-actions split">
          {existingList && (
            <button
              className="danger-action compact"
              disabled={busy}
              onClick={() => {
                if (window.confirm("Slett vennelisten?")) void onDelete(existingList.id);
              }}
            >
              <Trash2 size={17} />
              Slett
            </button>
          )}
          <button className="secondary-action compact" disabled={busy} onClick={onClose}>Avbryt</button>
          <button
            className="primary-action compact"
            disabled={!canSave || busy}
            onClick={() => void onSave(existingList?.id || null, name, memberIds)}
          >
            {busy ? "Lagrer..." : "Lagre"}
          </button>
        </div>
      </section>
    </div>
  );
}

// --- Gjenger (crews) ---------------------------------------------------------
// A gjeng is the standing crew: a shared audience for plans with a shared
// memory. The copy keeps the distinction from friend lists explicit: a list is
// a private shortcut only its owner sees; a gjeng is something the crew shares.

function gjengHint(gjeng: GjengSummary): string {
  if (gjeng.next_plan) {
    return `Neste: ${gjeng.next_plan.title} · ${formatPlanTime(gjeng.next_plan.starts_at)}`;
  }
  if (gjeng.last_done_at) {
    return `Sist sammen ${formatPlanDate(gjeng.last_done_at)}`;
  }
  return "Stille for tiden";
}

function GjengAvatarStack({ members }: { members: GjengMemberPreview[] }) {
  return (
    <span className="avatar-cluster gjeng-avatar-stack" aria-hidden="true">
      {members.slice(0, 3).map((member) => (
        <Avatar key={member.id} name={member.full_name} avatarPath={member.avatar_path} />
      ))}
      {members.length === 0 && <UsersRound size={18} />}
    </span>
  );
}

// First-time explainer for gjenger. Mirrors the invite-nudge/install-prompt
// pattern: a dismissible note with a persistent seen-state, never a blocker.
// It also disappears on its own once the person is in a gjeng.
function GjengIntroCard({ onDismiss }: { onDismiss: () => void }) {
  return (
    <section className="invite-nudge gjeng-intro" role="note" aria-label="Hva er en gjeng?">
      <span className="invite-nudge-icon">
        <UsersRound size={20} />
      </span>
      <div className="invite-nudge-copy">
        <strong>Nytt: Gjenger</strong>
        <p>
          En gjeng er den faste gjengen du gjør ting med – løpegjengen, klatregjengen,
          kollektivet. Alle i gjengen ser hverandre, kan legge ut planer til gjengen,
          og gjengen husker det dere har gjort sammen.
        </p>
        <p>
          Ikke det samme som en venneliste: listene er bare din private snarvei.
          En gjeng deler dere.
        </p>
      </div>
      <button className="home-screen-prompt-dismiss" onClick={onDismiss} aria-label="Skjul forklaring" title="Skjul forklaring">
        <X size={17} />
      </button>
    </section>
  );
}

function GjengerPage({
  gjenger,
  gjengInvites,
  friends,
  unreadCount,
  navigate,
  refresh,
  onMessage
}: {
  gjenger: GjengSummary[];
  gjengInvites: GjengInvite[];
  friends: Friend[];
  unreadCount: number;
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  onMessage: (message: string) => void;
}) {
  const [showIntro, setShowIntro] = useState(() =>
    shouldShowGjengIntro(currentGjengIntroEnvironment(gjenger.length > 0))
  );
  const [creating, setCreating] = useState(false);
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);

  function dismissIntro() {
    markGjengIntroSeen();
    setShowIntro(false);
  }

  // Mirrors the friend-request flow: the invited person answers with one tap,
  // accept lands them straight in the crew, decline is silent.
  async function acceptInvite(invite: GjengInvite) {
    setInviteBusyId(invite.id);
    try {
      const { error } = await requireSupabase().rpc("accept_gjeng_invite", {
        target_invite_id: invite.id
      });
      if (error) throw error;
      markGjengIntroSeen();
      await refresh();
      onMessage(`Du er med i «${invite.gjeng_name}».`);
      navigate(`/gjenger/${invite.gjeng_id}`);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setInviteBusyId(null);
    }
  }

  async function declineInvite(invite: GjengInvite) {
    setInviteBusyId(invite.id);
    try {
      const { error } = await requireSupabase().rpc("decline_gjeng_invite", {
        target_invite_id: invite.id
      });
      if (error) throw error;
      await refresh();
      onMessage("Invitasjonen er avslått.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setInviteBusyId(null);
    }
  }

  return (
    <section className="stack-page">
      <PageTitle
        title="Gjenger"
        action={
          <span className="header-cluster">
            <HeaderBell unreadCount={unreadCount} navigate={navigate} />
            <IconButton label="Lag en gjeng" icon={PlusCircle} onClick={() => setCreating(true)} />
          </span>
        }
      />
      {gjengInvites.length > 0 && (
        <section className="list-section">
          <h2>Invitasjoner</h2>
          {gjengInvites.map((invite) => (
            <div key={invite.id} className="friend-list-row gjeng-row gjeng-invite-row">
              <GjengAvatarStack members={invite.member_preview} />
              <div>
                <strong>{invite.gjeng_name}</strong>
                <span>
                  {invite.inviter_name} har invitert deg
                  {invite.member_count === 1 ? "" : ` · ${invite.member_count} i gjengen`}
                </span>
              </div>
              <span className="gjeng-invite-actions">
                <button
                  className="primary-action compact"
                  disabled={inviteBusyId === invite.id}
                  onClick={() => void acceptInvite(invite)}
                >
                  {inviteBusyId === invite.id ? "..." : "Bli med"}
                </button>
                <button
                  className="secondary-action compact"
                  disabled={inviteBusyId === invite.id}
                  onClick={() => void declineInvite(invite)}
                >
                  Avslå
                </button>
              </span>
            </div>
          ))}
        </section>
      )}
      {showIntro && gjenger.length === 0 && <GjengIntroCard onDismiss={dismissIntro} />}
      {gjenger.length === 0 ? (
        <EmptyState
          title="Samle gjengen du gjør ting med"
          text="Lag en gjeng, legg til vennene dine, og del planene på ett sted – i stedet for å velge de samme folkene hver gang."
          action={
            <button className="primary-action compact" onClick={() => setCreating(true)}>
              <PlusCircle size={17} />
              Lag en gjeng
            </button>
          }
        />
      ) : (
        <div className="plan-list">
          {gjenger.map((gjeng) => {
            const activity = activityForPlan(gjeng.activity_id);
            const Icon = activity.icon;
            return (
              <button key={gjeng.id} className="friend-list-row gjeng-row" onClick={() => navigate(`/gjenger/${gjeng.id}`)}>
                <GjengAvatarStack members={gjeng.member_preview} />
                <div>
                  <strong>{gjeng.name}</strong>
                  <span>
                    {gjeng.member_count === 1 ? "Bare deg så langt" : `${gjeng.member_count} i gjengen`}
                  </span>
                  <small>{gjengHint(gjeng)}</small>
                </div>
                <span className="plan-activity-icon compact" aria-hidden="true">
                  <Icon size={18} />
                </span>
              </button>
            );
          })}
        </div>
      )}
      {gjenger.length > 0 && (
        <p className="muted gjeng-list-footnote">
          En gjeng deler dere: alle i den ser hverandre og planene. Vennelistene dine
          under Venner er fortsatt bare din private snarvei.
        </p>
      )}
      {creating && (
        <CreateGjengModal
          friends={friends}
          onClose={() => setCreating(false)}
          onCreated={async (gjengId) => {
            setCreating(false);
            markGjengIntroSeen();
            await refresh();
            onMessage("Gjengen er klar. Legg ut en plan når dere skal noe.");
            navigate(`/gjenger/${gjengId}`);
          }}
          onMessage={onMessage}
        />
      )}
    </section>
  );
}

function CreateGjengModal({
  friends,
  onClose,
  onCreated,
  onMessage
}: {
  friends: Friend[];
  onClose: () => void;
  onCreated: (gjengId: string) => Promise<void>;
  onMessage: (message: string) => void;
}) {
  const [name, setName] = useState("");
  const [activityId, setActivityId] = useState<ActivityId>("other");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const canCreate = name.trim().length >= 2;

  function toggleMember(friendId: string, selected: boolean) {
    setMemberIds((current) =>
      selected ? [...new Set([...current, friendId])] : current.filter((id) => id !== friendId)
    );
  }

  async function create() {
    setBusy(true);
    try {
      const { data, error } = await requireSupabase().rpc("create_gjeng", {
        gjeng_name: name.trim(),
        gjeng_activity_id: activityId,
        member_ids: memberIds
      });
      if (error) throw error;
      const gjengId = typeof data === "string" ? data : null;
      if (!gjengId) throw new Error("Gjengen ble opprettet uten id.");
      await onCreated(gjengId);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <section className="modal friend-list-modal">
        <div className="modal-header">
          <div>
            <h2>Lag en gjeng</h2>
            <p>Alle i gjengen ser hverandre og kan legge ut planer til gjengen.</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk">
            <X size={17} />
          </button>
        </div>
        <label>
          Hva heter gjengen?
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="For eksempel Løpegjengen"
            autoFocus
          />
        </label>
        <div className="form-field">
          <span className="field-label">Hva gjør dere mest?</span>
          <div className="gjeng-activity-row" role="group" aria-label="Aktivitet">
            {activityOptions.map((activity) => {
              const Icon = activity.icon;
              const isSelected = activity.id === activityId;
              return (
                <button
                  key={activity.id}
                  type="button"
                  className={isSelected ? "gjeng-activity-chip selected" : "gjeng-activity-chip"}
                  aria-pressed={isSelected}
                  title={activity.label}
                  onClick={() => setActivityId(activity.id)}
                >
                  <Icon size={17} />
                  <span>{activity.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="editor-list">
          <span className="field-label">Inviter venner</span>
          {friends.length === 0 ? (
            <p className="muted">
              Du har ingen venner på Join ennå. Du kan lage gjengen nå og invitere folk senere.
            </p>
          ) : (
            friends.map((friend) => (
              <label key={friend.id} className="check-row">
                <input
                  type="checkbox"
                  checked={memberIds.includes(friend.id)}
                  onChange={(event) => toggleMember(friend.id, event.target.checked)}
                />
                <Avatar name={friend.full_name} avatarPath={friend.avatar_path} />
                <span>{friend.full_name}</span>
              </label>
            ))
          )}
          <p className="muted compact">
            De du velger får en invitasjon og blir med når de sier ja. Alle i gjengen kan
            invitere sine egne venner etterpå – de trenger ikke kjenne alle fra før.
          </p>
        </div>
        <div className="modal-actions">
          <button className="secondary-action compact" disabled={busy} onClick={onClose}>Avbryt</button>
          <button className="primary-action compact" disabled={!canCreate || busy} onClick={create}>
            {busy ? "Lager gjengen..." : "Lag gjeng"}
          </button>
        </div>
      </section>
    </div>
  );
}

function GjengDetailPage({
  gjengId,
  currentUserId,
  friends,
  navigate,
  refresh,
  afterMeaningfulAction,
  onMessage
}: {
  gjengId: string;
  currentUserId: string;
  friends: Friend[];
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  afterMeaningfulAction: (reason: string) => void;
  onMessage: (message: string) => void;
}) {
  const [gjeng, setGjeng] = useState<GjengDetail | null>(null);
  const [gjengPlans, setGjengPlans] = useState<PlanSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [memberBusyId, setMemberBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Top up any due recurring series in this crew before reading its plans.
    await requireSupabase().rpc("maintain_plan_series").then(undefined, () => undefined);
    const [detail, planRows] = await Promise.all([
      loadGjengDetail(gjengId),
      loadGjengPlans(gjengId).catch(() => [] as PlanSummary[])
    ]);
    setGjeng(detail);
    setGjengPlans(planRows);
    setLoaded(true);
  }, [gjengId]);

  useEffect(() => {
    load().catch((error) => {
      setLoaded(true);
      onMessage(errorMessage(error));
    });
  }, [load, onMessage]);

  async function leaveGjeng() {
    if (!gjeng) return;
    if (!window.confirm(`Forlate «${gjeng.name}»? Du kan bli lagt til igjen av en venn.`)) return;
    setBusy(true);
    try {
      const { error } = await requireSupabase().rpc("leave_gjeng", { target_gjeng_id: gjengId });
      if (error) throw error;
      await refresh();
      onMessage("Du har forlatt gjengen.");
      navigate("/gjenger");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function renameGjeng(nextName: string) {
    setBusy(true);
    try {
      const { error } = await requireSupabase().rpc("rename_gjeng", {
        target_gjeng_id: gjengId,
        gjeng_name: nextName.trim()
      });
      if (error) throw error;
      setRenaming(false);
      await Promise.all([load(), refresh()]);
      onMessage("Gjengen har fått nytt navn.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  // Nobody is added directly anymore: friends get an invitation and join when
  // they say yes, mirroring the friend-request consent flow.
  async function inviteMembers(memberIds: string[]) {
    setBusy(true);
    try {
      const { error } = await requireSupabase().rpc("invite_to_gjeng", {
        target_gjeng_id: gjengId,
        member_ids: memberIds
      });
      if (error) throw error;
      setAddingMembers(false);
      await Promise.all([load(), refresh()]);
      onMessage(
        memberIds.length === 1
          ? "Invitasjonen er sendt. De blir med når de sier ja."
          : `${memberIds.length} invitasjoner er sendt. De blir med når de sier ja.`
      );
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function cancelInvite(invite: GjengPendingInvite) {
    setMemberBusyId(invite.invitee_id);
    try {
      const { error } = await requireSupabase().rpc("cancel_gjeng_invite", {
        target_invite_id: invite.invite_id
      });
      if (error) throw error;
      await load();
      onMessage("Invitasjonen er trukket tilbake.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setMemberBusyId(null);
    }
  }

  async function connectWithMember(member: GjengMember) {
    if (member.relationship_state === "none") {
      setMemberBusyId(member.id);
      try {
        const { error } = await requireSupabase().rpc("send_friend_request", {
          target_user_id: member.id
        });
        if (error) throw error;
        afterMeaningfulAction("Få beskjed når venneforespørsler blir godtatt.");
        await load();
        onMessage("Venneforespørsel sendt.");
      } catch (error) {
        onMessage(errorMessage(error));
      } finally {
        setMemberBusyId(null);
      }
      return;
    }
    if (member.relationship_state === "incoming_request" && member.request_id) {
      setMemberBusyId(member.id);
      try {
        const { error } = await requireSupabase().rpc("accept_friend_request", {
          target_request_id: member.request_id
        });
        if (error) throw error;
        await Promise.all([load(), refresh()]);
        onMessage("Dere er venner på Join.");
      } catch (error) {
        onMessage(errorMessage(error));
      } finally {
        setMemberBusyId(null);
      }
    }
  }

  async function shareGjengInvite() {
    if (!gjeng) return;
    setBusy(true);
    try {
      const { data, error } = await requireSupabase().rpc("create_gjeng_invite", {
        target_gjeng_id: gjeng.id
      });
      if (error) throw error;
      const token = (data as Array<{ gjeng_token: string }>)[0]?.gjeng_token;
      if (!token) throw new Error("Gjenglenken ble opprettet uten token.");
      const url = gjengUrl(token);
      await shareOrCopy({
        title: `Bli med i ${gjeng.name} på Join`,
        text: `Bli med i «${gjeng.name}» på Join. Her legger vi planene våre.`,
        url
      });
      onMessage("Gjenglenken er klar.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <LoadingScreen label="Henter gjengen..." />;
  if (!gjeng) {
    return (
      <section className="stack-page">
        <div className="profile-topbar">
          <button type="button" className="icon-only" onClick={() => navigate("/gjenger")} aria-label="Tilbake">
            <ArrowLeft size={18} />
          </button>
        </div>
        <EmptyState
          title="Gjengen er ikke tilgjengelig"
          text="Den finnes ikke, eller så er du ikke i den lenger."
        />
      </section>
    );
  }

  const now = Date.now();
  const upcoming = gjengPlans
    .filter((plan) => plan.status === "scheduled" && new Date(plan.ends_at).getTime() > now)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  const past = gjengPlans.filter(
    (plan) => plan.status !== "cancelled" && new Date(plan.ends_at).getTime() <= now
  );
  const activity = activityForPlan(gjeng.activity_id);
  const ActivityIcon = activity.icon;
  const memberIdsInGjeng = new Set(gjeng.members.map((member) => member.id));
  const invitedIds = new Set(gjeng.pending_invites.map((invite) => invite.invitee_id));
  const addableFriends = friends.filter(
    (friend) => !memberIdsInGjeng.has(friend.id) && !invitedIds.has(friend.id)
  );

  return (
    <section className="stack-page gjeng-detail">
      <div className="profile-topbar">
        <button type="button" className="icon-only" onClick={() => navigate("/gjenger")} aria-label="Tilbake">
          <ArrowLeft size={18} />
        </button>
      </div>
      <header className="gjeng-header">
        <span className="plan-activity-icon" aria-hidden="true">
          <ActivityIcon size={21} />
        </span>
        <div>
          <h1>{gjeng.name}</h1>
          <p className="muted">
            {gjeng.member_count === 1
              ? "Bare deg så langt – legg til noen du gjør ting med."
              : `${gjeng.member_count} i gjengen`}
          </p>
        </div>
      </header>
      <div className="detail-actions">
        <button className="primary-action compact" onClick={() => navigate(`/plans/new/gjeng/${gjeng.id}`)}>
          <PlusCircle size={17} />
          Ny plan i gjengen
        </button>
        <button className="secondary-action compact" onClick={() => setAddingMembers(true)}>
          <UserPlus size={17} />
          Inviter til gjengen
        </button>
        <button className="secondary-action compact" disabled={busy} onClick={() => void shareGjengInvite()}>
          <Share2 size={17} />
          Inviter hele gjengen
        </button>
      </div>

      <section className="home-section">
        <SectionHeading title="Skjer i gjengen" />
        {upcoming.length === 0 ? (
          past.length === 0 ? (
            <EmptyState
              title="Ingen planer i gjengen ennå"
              text="Hva skal dere finne på?"
              action={
                <div className="empty-action-stack">
                  {gjeng.member_count <= 1 && (
                    <button className="primary-action compact" disabled={busy} onClick={() => void shareGjengInvite()}>
                      <Share2 size={17} />
                      Inviter hele gjengen
                    </button>
                  )}
                  <button className={gjeng.member_count <= 1 ? "secondary-action compact" : "primary-action compact"} onClick={() => navigate(`/plans/new/gjeng/${gjeng.id}`)}>
                    <PlusCircle size={17} />
                    Ny plan
                  </button>
                </div>
              }
            />
          ) : (
            <p className="muted">Ingen kommende planer. Hva skal dere finne på?</p>
          )
        ) : (
          <div className="plan-list">
            {upcoming.map((plan) => (
              <PlanRow key={plan.id} plan={plan} onOpen={() => navigate(`/plans/${plan.id}`)} onMessage={onMessage} />
            ))}
          </div>
        )}
      </section>

      {past.length > 0 && (
        <section className="home-section">
          <SectionHeading title="Tidligere" />
          <p className="muted compact">Det gjengen har gjort sammen.</p>
          <div className="plan-list">
            {past.map((plan) => (
              <MemoryPlanRow key={plan.id} plan={plan} onOpen={() => navigate(`/plans/${plan.id}`)} onMessage={onMessage} />
            ))}
          </div>
        </section>
      )}

      <section className="list-section">
        <SectionHeading title="I gjengen" />
        {gjeng.members.map((member) => (
          <PersonRow
            key={member.id}
            person={{
              id: member.id,
              full_name: member.full_name,
              avatar_path: member.avatar_path,
              relationship_state: member.relationship_state
            }}
            subtitle={member.is_creator ? "Startet gjengen" : undefined}
            onOpen={member.id === currentUserId ? undefined : () => navigate(`/u/${member.id}`)}
            action={
              member.id === currentUserId ? (
                <span className="state-chip">Deg</span>
              ) : (
                <ProfileRelationshipAction
                  state={member.relationship_state}
                  requestId={member.request_id}
                  busy={memberBusyId === member.id}
                  onSend={() => connectWithMember(member)}
                  onAccept={() => connectWithMember(member)}
                />
              )
            }
          />
        ))}
        {gjeng.pending_invites.map((invite) => (
          <PersonRow
            key={invite.invite_id}
            person={{
              id: invite.invitee_id,
              full_name: invite.full_name,
              avatar_path: invite.avatar_path,
              relationship_state: "none"
            }}
            subtitle={
              invite.viewer_is_inviter
                ? "Invitert av deg – venter på svar"
                : `Invitert av ${invite.inviter_name} – venter på svar`
            }
            action={
              invite.viewer_is_inviter ? (
                <button
                  className="secondary-action compact"
                  disabled={memberBusyId === invite.invitee_id}
                  onClick={() => void cancelInvite(invite)}
                >
                  Trekk tilbake
                </button>
              ) : (
                <span className="state-chip">Invitert</span>
              )
            }
          />
        ))}
        <p className="muted compact">
          Alle i gjengen ser hverandre og gjengens planer – dere trenger ikke være venner på Join.
        </p>
      </section>

      <div className="gjeng-footer-actions">
        <button className="text-button" disabled={busy} onClick={() => setRenaming(true)}>
          <Edit3 size={16} />
          Gi nytt navn
        </button>
        <button className="text-button danger" disabled={busy} onClick={leaveGjeng}>
          <LogOut size={16} />
          Forlat gjengen
        </button>
      </div>

      {addingMembers && (
        <AddGjengMembersModal
          gjengName={gjeng.name}
          addableFriends={addableFriends}
          busy={busy}
          onClose={() => setAddingMembers(false)}
          onAdd={inviteMembers}
        />
      )}
      {renaming && (
        <RenameGjengModal
          currentName={gjeng.name}
          busy={busy}
          onClose={() => setRenaming(false)}
          onSave={renameGjeng}
        />
      )}
    </section>
  );
}

function AddGjengMembersModal({
  gjengName,
  addableFriends,
  busy,
  onClose,
  onAdd
}: {
  gjengName: string;
  addableFriends: Friend[];
  busy: boolean;
  onClose: () => void;
  onAdd: (memberIds: string[]) => Promise<void>;
}) {
  const [memberIds, setMemberIds] = useState<string[]>([]);

  function toggleMember(friendId: string, selected: boolean) {
    setMemberIds((current) =>
      selected ? [...new Set([...current, friendId])] : current.filter((id) => id !== friendId)
    );
  }

  return (
    <div className="modal-backdrop">
      <section className="modal friend-list-modal">
        <div className="modal-header">
          <div>
            <h2>Inviter til «{gjengName}»</h2>
            <p>Du kan invitere dine egne venner. De blir med når de sier ja til invitasjonen.</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk">
            <X size={17} />
          </button>
        </div>
        <div className="editor-list">
          {addableFriends.length === 0 ? (
            <p className="muted">Alle vennene dine er allerede i gjengen eller invitert.</p>
          ) : (
            addableFriends.map((friend) => (
              <label key={friend.id} className="check-row">
                <input
                  type="checkbox"
                  checked={memberIds.includes(friend.id)}
                  onChange={(event) => toggleMember(friend.id, event.target.checked)}
                />
                <Avatar name={friend.full_name} avatarPath={friend.avatar_path} />
                <span>{friend.full_name}</span>
              </label>
            ))
          )}
        </div>
        <div className="modal-actions">
          <button className="secondary-action compact" disabled={busy} onClick={onClose}>Avbryt</button>
          <button
            className="primary-action compact"
            disabled={memberIds.length === 0 || busy}
            onClick={() => void onAdd(memberIds)}
          >
            {busy ? "Inviterer..." : "Inviter"}
          </button>
        </div>
      </section>
    </div>
  );
}

function RenameGjengModal({
  currentName,
  busy,
  onClose,
  onSave
}: {
  currentName: string;
  busy: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(currentName);
  const canSave = name.trim().length >= 2 && name.trim() !== currentName;

  return (
    <div className="modal-backdrop">
      <section className="modal">
        <div className="modal-header">
          <div>
            <h2>Gi gjengen nytt navn</h2>
            <p>Alle i gjengen ser det nye navnet.</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk">
            <X size={17} />
          </button>
        </div>
        <label>
          Navn
          <input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </label>
        <div className="modal-actions">
          <button className="secondary-action compact" disabled={busy} onClick={onClose}>Avbryt</button>
          <button className="primary-action compact" disabled={!canSave || busy} onClick={() => void onSave(name)}>
            {busy ? "Lagrer..." : "Lagre"}
          </button>
        </div>
      </section>
    </div>
  );
}

export function selectedAudienceFriendIds(
  friendIds: string[],
  friendListIds: string[],
  friendLists: FriendList[],
  friends: Friend[]
): string[] {
  const acceptedFriendIds = new Set(friends.map((friend) => friend.id));
  const recipients = new Set(friendIds.filter((friendId) => acceptedFriendIds.has(friendId)));
  friendLists
    .filter((list) => friendListIds.includes(list.id))
    .forEach((list) => {
      list.member_ids.forEach((friendId) => {
        if (acceptedFriendIds.has(friendId)) recipients.add(friendId);
      });
    });
  return [...recipients];
}

type PlanDetailResult = Omit<
  PlanDetail,
  | "activity_id"
  | "participant_preview"
  | "comments"
  | "participants"
  | "allow_guest_join"
  | "comment_count"
  | "reaction_summary"
  | "viewer_reaction"
> & Partial<
  Pick<
    PlanDetail,
    "activity_id" | "participant_preview" | "comments" | "participants" | "allow_guest_join" | "comment_count"
  >
> & {
  reaction_summary?: unknown;
  viewer_reaction?: unknown;
};

type PlanSummaryResult = Omit<
  PlanSummary,
  "activity_id" | "participant_preview" | "comment_count" | "reaction_summary" | "viewer_reaction"
> &
  Partial<Pick<PlanSummary, "activity_id" | "participant_preview" | "comment_count">> & {
    reaction_summary?: unknown;
    viewer_reaction?: unknown;
  };

function normalizePlanSummary(row: PlanSummaryResult): PlanSummary {
  return {
    ...row,
    planned_route: normalizeRoute(row.planned_route),
    strava_activity_count: row.strava_activity_count ?? 0,
    activity_id: row.activity_id ?? null,
    gjeng_id: row.gjeng_id ?? null,
    gjeng_name: row.gjeng_name ?? null,
    comment_count: numberValue(row.comment_count) ?? 0,
    participant_preview: normalizePlanParticipants(row.participant_preview),
    reaction_summary: normalizeReactionSummary(row.reaction_summary),
    viewer_reaction: reactionKeyValue(row.viewer_reaction)
  };
}

export function normalizePlanDetail(row: PlanDetailResult | null | undefined): PlanDetail | null {
  if (!row) return null;
  const participants = normalizePlanParticipants(row.participants);
  const participantPreview = normalizePlanParticipants(row.participant_preview);
  const comments = normalizePlanComments(row.comments);

  return {
    ...row,
    planned_route: normalizeRoute(row.planned_route),
    strava_activity_count: row.strava_activity_count ?? 0,
    activity_id: row.activity_id ?? null,
    gjeng_id: row.gjeng_id ?? null,
    gjeng_name: row.gjeng_name ?? null,
    allow_guest_join: row.allow_guest_join ?? false,
    comment_count: numberValue(row.comment_count) ?? comments.length,
    reaction_summary: normalizeReactionSummary(row.reaction_summary),
    viewer_reaction: reactionKeyValue(row.viewer_reaction),
    comments,
    participant_preview: participantPreview.length > 0 ? participantPreview : participants.slice(0, 3),
    participants
  };
}

function planDetailFromSummary(initialPlan: PlanSummary, currentUserId: string): PlanDetail {
  const participantPreview = normalizePlanParticipants(initialPlan.participant_preview);

  return {
    ...initialPlan,
    allow_guest_join: initialPlan.allow_guest_join ?? false,
    participant_preview: participantPreview,
    comment_count: initialPlan.comment_count ?? 0,
    reaction_summary: initialPlan.reaction_summary ?? [],
    viewer_reaction: initialPlan.viewer_reaction ?? null,
    comments: [],
    participants: participantPreview.length > 0 ? participantPreview : [{
      id: initialPlan.host_id,
      full_name: initialPlan.host_name,
      avatar_path: null,
      relationship_state: initialPlan.host_id === currentUserId ? "self" : "friends",
      request_id: null,
      joined_at: null,
      is_host: true
    }]
  };
}

function normalizePlanParticipants(value: unknown): PlanParticipant[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = stringValue(item.id);
    const fullName = stringValue(item.full_name);
    if (!id || !fullName) return [];

    return [{
      id,
      full_name: fullName,
      avatar_path: nullableString(item.avatar_path),
      relationship_state: relationshipStateValue(item.relationship_state),
      request_id: nullableString(item.request_id),
      joined_at: nullableString(item.joined_at),
      is_host: booleanValue(item.is_host)
    }];
  });
}

function normalizePlanComments(value: unknown): PlanDetail["comments"] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = stringValue(item.id);
    const authorId = stringValue(item.author_id);
    const authorName = stringValue(item.author_name);
    const body = typeof item.body === "string" ? item.body : null;
    const createdAt = stringValue(item.created_at);
    if (!id || !authorId || !authorName || body === null || !createdAt) return [];

    return [{
      id,
      author_id: authorId,
      author_name: authorName,
      author_avatar_path: nullableString(item.author_avatar_path),
      body,
      created_at: createdAt,
      reaction_summary: normalizeReactionSummary(item.reaction_summary),
      viewer_reaction: reactionKeyValue(item.viewer_reaction)
    }];
  });
}

function normalizeReactionSummary(value: unknown): ReactionSummaryItem[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const reactionKey = reactionKeyValue(item.reaction_key);
    const count = numberValue(item.count);
    if (!reactionKey || count === null || count <= 0) return [];

    return [{
      reaction_key: reactionKey,
      count,
      viewer_message: nullableString(item.viewer_message),
      reactors: normalizeReactionActors(item.reactors, reactionKey)
    }];
  });
}

function normalizeReactionActors(value: unknown, fallbackReactionKey: ReactionKey): ReactionActor[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = stringValue(item.id);
    const fullName = stringValue(item.full_name);
    if (!id || !fullName) return [];
    return [{
      id,
      full_name: fullName,
      avatar_path: nullableString(item.avatar_path),
      reaction_key: reactionKeyValue(item.reaction_key) ?? fallbackReactionKey,
      message: nullableString(item.message),
      reacted_at: stringValue(item.reacted_at) ?? ""
    }];
  });
}

function relationshipStateValue(value: unknown): RelationshipState {
  if (
    value === "none" ||
    value === "self" ||
    value === "friends" ||
    value === "pending" ||
    value === "incoming_request" ||
    value === "outgoing_request"
  ) {
    return value;
  }

  return "none";
}

async function createPlanWithActivityFallback(payload: Record<string, unknown>): Promise<string> {
  const { data, error } = await requireSupabase().rpc("create_plan", payload);
  if (!error) return createdPlanId(data);
  if (!isLegacyCreatePlanSignatureError(error)) throw error;

  const legacyPayload = { ...payload };
  // Audience and a selected route must survive creation. An older signature
  // cannot represent them, so surface the error instead of degrading the plan.
  if (legacyPayload.plan_gjeng_id || legacyPayload.plan_route_suggestion_id) throw error;
  delete legacyPayload.plan_activity_id;
  delete legacyPayload.plan_allow_guest_join;
  delete legacyPayload.plan_gjeng_id;

  const { data: legacyData, error: legacyError } = await requireSupabase().rpc("create_plan", legacyPayload);
  if (legacyError) throw legacyError;
  return createdPlanId(legacyData);
}

function createdPlanId(data: unknown): string {
  if (typeof data === "string" && data.length > 0) return data;
  throw new Error("Planen ble opprettet uten plan-id.");
}

function isLegacyCreatePlanSignatureError(error: unknown): boolean {
  if (!isRecord(error)) return false;
  const text = [error.code, error.message, error.details, error.hint]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toLowerCase();

  return text.includes("pgrst202") || (text.includes("create_plan") && text.includes("plan_activity_id"));
}

// Recurrence weekday labels, stored 0=Monday..6=Sunday to match the DB.
const recurWeekdayLabels = ["M", "T", "O", "T", "F", "L", "S"];

// JS getDay() is 0=Sunday..6=Saturday; the series model uses 0=Monday..6=Sunday.
function dbWeekdayFromLocal(dateTimeLocal: string): number {
  const parsed = new Date(dateTimeLocal);
  if (Number.isNaN(parsed.getTime())) return 0;
  return (parsed.getDay() + 6) % 7;
}

const recurWeekdayFullLabels = ["mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag", "søndag"];

function recurrenceSummary(
  freq: "daily" | "weekly",
  interval: number,
  weekdays: number[],
  endKind: "never" | "until" | "count",
  until: string,
  count: number,
  startTimeLabel: string
): string {
  let cadence: string;
  if (freq === "daily") {
    cadence = interval <= 1 ? "hver dag" : `hver ${interval}. dag`;
  } else {
    const days = [...weekdays].sort((a, b) => a - b).map((d) => recurWeekdayFullLabels[d]).join(", ");
    const every = interval <= 1 ? "hver" : interval === 2 ? "annenhver" : `hver ${interval}.`;
    cadence = `${every} ${days || "uke"}`;
  }
  let ending = "";
  if (endKind === "until" && until) {
    ending = ` – til ${new Date(until).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}`;
  } else if (endKind === "count") {
    ending = ` – ${count} ganger`;
  } else {
    ending = " – uten sluttdato";
  }
  return `Gjentas ${cadence} kl. ${startTimeLabel}${ending}.`;
}

type CreatePlanDraft = {
  activityId: ActivityId; title: string; description: string; startsAt: string;
  durationHours: number; durationMinutes: number; showCustomDuration: boolean;
  placeName: string; selectedPlace: PlaceOption | null; audience: "all_friends" | "selected_friends" | "gjeng";
  selectedGjengId: string | null; recurring: boolean; recurFreq: "daily" | "weekly"; recurInterval: number;
  recurWeekdays: number[]; recurEndKind: "never" | "until" | "count"; recurUntil: string; recurCount: number;
  openInvite: boolean; selected: string[]; selectedFriendListIds: string[]; routeId: string | null;
};
function CreatePlanPage({
  currentUserId,
  friends,
  friendLists,
  gjenger,
  initialGjengId,
  navigate,
  refresh,
  afterMeaningfulAction,
  onMessage
}: {
  currentUserId: string;
  friends: Friend[];
  friendLists: FriendList[];
  gjenger: GjengSummary[];
  initialGjengId: string | null;
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  afterMeaningfulAction: (reason: string) => void;
  onMessage: (message: string) => void;
}) {
  const initialGjeng = initialGjengId
    ? gjenger.find((gjeng) => gjeng.id === initialGjengId) ?? null
    : null;
  const [draft] = useState(() => consumePlanDraft<CreatePlanDraft>(currentUserId));
  useEffect(() => { clearStravaDrafts(); }, []);
  const [activityId, setActivityId] = useState<ActivityId>(draft?.activityId ?? initialGjeng?.activity_id ?? "run");
  const [title, setTitle] = useState(draft?.title ?? "");
  const [description, setDescription] = useState(draft?.description ?? "");
  const [startsAt, setStartsAt] = useState(() => draft?.startsAt ?? toDateTimeLocal(defaultCreatePlanStart()));
  const [durationHours, setDurationHours] = useState(draft?.durationHours ?? 2);
  const [durationMinutes, setDurationMinutes] = useState(draft?.durationMinutes ?? 0);
  const [showCustomDuration, setShowCustomDuration] = useState(draft?.showCustomDuration ?? false);
  const [placeName, setPlaceName] = useState(draft?.placeName ?? "");
  const [selectedPlace, setSelectedPlace] = useState<PlaceOption | null>(draft?.selectedPlace ?? null);
  const [selectedRoute, setSelectedRoute] = useState<RouteSuggestion | null>(null);
  const [restoredRouteId, setRestoredRouteId] = useState(draft?.routeId ?? null);
  const [placeResults, setPlaceResults] = useState<PlaceOption[]>([]);
  const [isSearchingForPlace, setIsSearchingForPlace] = useState(false);
  const [placeSearchMessage, setPlaceSearchMessage] = useState<string | null>(null);
  const [audience, setAudience] = useState<"all_friends" | "selected_friends" | "gjeng">(
    draft?.audience ?? (initialGjeng ? "gjeng" : "all_friends")
  );
  const [selectedGjengId, setSelectedGjengId] = useState<string | null>(draft?.selectedGjengId ?? initialGjeng?.id ?? null);
  const [recurring, setRecurring] = useState(draft?.recurring ?? false);
  const [recurFreq, setRecurFreq] = useState<"daily" | "weekly">(draft?.recurFreq ?? "weekly");
  const [recurInterval, setRecurInterval] = useState(draft?.recurInterval ?? 1);
  // Empty means "the start date's own weekday"; stored 0=Monday..6=Sunday.
  const [recurWeekdays, setRecurWeekdays] = useState<number[]>(draft?.recurWeekdays ?? []);
  const [recurEndKind, setRecurEndKind] = useState<"never" | "until" | "count">(draft?.recurEndKind ?? "never");
  const [recurUntil, setRecurUntil] = useState(draft?.recurUntil ?? "");
  const [recurCount, setRecurCount] = useState(draft?.recurCount ?? 8);
  const [openInvite, setOpenInvite] = useState(draft?.openInvite ?? true);
  const [selected, setSelected] = useState<string[]>(draft?.selected ?? []);
  const [selectedFriendListIds, setSelectedFriendListIds] = useState<string[]>(draft?.selectedFriendListIds ?? []);
  const [busy, setBusy] = useState(false);
  const selectedActivity = activityOptions.find((activity) => activity.id === activityId) ?? activityOptions[0];
  const activityRequiresCustomTitle =
    "requiresCustomTitle" in selectedActivity && selectedActivity.requiresCustomTitle === true;
  const durationTotalMinutes = durationHours * 60 + durationMinutes;
  const planTitle = resolveActivityPlanTitle(
    title,
    selectedActivity.title,
    activityRequiresCustomTitle
  );
  const selectedRecipientIds = useMemo(
    () => selectedAudienceFriendIds(selected, selectedFriendListIds, friendLists, friends),
    [friendLists, friends, selected, selectedFriendListIds]
  );
  const trimmedPlaceName = placeName.trim();
  const selectedPlaceName = selectedPlace?.name ?? null;
  const visiblePlaceResults = trimmedPlaceName === selectedPlaceName ? [] : placeResults;
  const showEmptyPlaceState =
    trimmedPlaceName.length >= 2 &&
    !isSearchingForPlace &&
    !placeSearchMessage &&
    visiblePlaceResults.length === 0 &&
    !selectedPlace;
  const selectedGjeng = selectedGjengId
    ? gjenger.find((gjeng) => gjeng.id === selectedGjengId) ?? null
    : null;
  // Recurring only applies to gjeng plans; it collapses to a single plan otherwise.
  const isRecurring = recurring && audience === "gjeng" && selectedGjeng !== null;
  const anchorWeekday = dbWeekdayFromLocal(startsAt);
  const effectiveWeekdays = recurWeekdays.length > 0 ? recurWeekdays : [anchorWeekday];
  const startTimeLabel = startsAt.slice(11, 16);
  const recurrenceValid =
    !isRecurring ||
    ((recurFreq !== "weekly" || effectiveWeekdays.length > 0) &&
      (recurEndKind !== "until" || (recurUntil !== "" && new Date(recurUntil) >= new Date(startsAt))) &&
      (recurEndKind !== "count" || recurCount >= 1));
  const canPublish =
    durationTotalMinutes > 0 &&
    trimmedPlaceName.length > 0 &&
    isActivityTitleReady(title, activityRequiresCustomTitle) &&
    (audience !== "selected_friends" || selectedRecipientIds.length > 0) &&
    (audience !== "gjeng" || selectedGjeng !== null) &&
    recurrenceValid;

  useEffect(() => {
    if (selectedPlace && trimmedPlaceName !== selectedPlace.name) {
      setSelectedPlace(null);
    }

    if (trimmedPlaceName.length < 2 || trimmedPlaceName === selectedPlaceName) {
      setPlaceResults([]);
      setPlaceSearchMessage(null);
      setIsSearchingForPlace(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setIsSearchingForPlace(true);
      setPlaceSearchMessage(null);
      searchPlaceOptions(trimmedPlaceName, controller.signal)
        .then((response) => {
          setPlaceResults(response.places);
          setPlaceSearchMessage(
            response.usesOfflineFallback ? "Kartsøket er utilgjengelig. Viser lagrede steder." : null
          );
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError") return;
          setPlaceResults([]);
          setPlaceSearchMessage("Kartsøket er utilgjengelig. Skriv inn stedet manuelt.");
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsSearchingForPlace(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [selectedPlace, selectedPlaceName, trimmedPlaceName]);

  async function createPlan() {
    if (!canPublish) return;
    setBusy(true);
    try {
      const start = new Date(startsAt);
      const end = new Date(start.getTime() + durationTotalMinutes * 60 * 1000);
      const locationPayload = planLocationPayload(placeName, selectedPlace);

      if (isRecurring && selectedGjengId) {
        const { error } = await requireSupabase().rpc("create_plan_series", {
          ...(selectedRoute ? { plan_route_suggestion_id: selectedRoute.id } : {}),
          target_gjeng_id: selectedGjengId,
          plan_title: planTitle,
          plan_activity_id: selectedActivity.id,
          plan_description: description.trim(),
          plan_place_name: locationPayload.plan_place_name,
          plan_place_address: locationPayload.plan_place_address,
          plan_public_latitude: locationPayload.plan_public_latitude,
          plan_public_longitude: locationPayload.plan_public_longitude,
          plan_capacity: null,
          plan_duration_minutes: durationTotalMinutes,
          plan_freq: recurFreq,
          plan_interval_count: recurInterval,
          plan_byweekday: recurFreq === "weekly" ? effectiveWeekdays : [],
          plan_anchor_date: startsAt.slice(0, 10),
          plan_start_time: startsAt.slice(11, 16),
          plan_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          plan_end_kind: recurEndKind,
          plan_until_date: recurEndKind === "until" ? recurUntil : null,
          plan_occurrence_count: recurEndKind === "count" ? recurCount : null
        });
        if (error) throw error;
        await refresh();
        afterMeaningfulAction("Få beskjed når noen blir med på turene i gjengen.");
        navigate(`/gjenger/${selectedGjengId}`);
        return;
      }

      const planId = await createPlanWithActivityFallback({
        ...(selectedRoute ? { plan_route_suggestion_id: selectedRoute.id } : {}),
        plan_title: planTitle,
        plan_description: description.trim(),
        plan_starts_at: start.toISOString(),
        plan_ends_at: end.toISOString(),
        plan_audience_kind: audience,
        selected_friend_ids: audience === "selected_friends" ? selectedRecipientIds : [],
        plan_capacity: null,
        plan_participant_list_visible: true,
        ...locationPayload,
        plan_activity_id: selectedActivity.id,
        plan_allow_guest_join: audience === "all_friends" ? openInvite : false,
        plan_gjeng_id: audience === "gjeng" ? selectedGjengId : null,
      });
      await refresh();
      afterMeaningfulAction("Få beskjed når noen blir med på planen din.");
      navigate(`/plans/${planId}`);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="create-surface">
      <div className="screen-header create">
        <button className="text-button" onClick={() => navigate("/")}>Avbryt</button>
        <strong>Ny plan</strong>
        <span />
      </div>
      <fieldset className="activity-picker">
        <legend>Aktivitet</legend>
        <div className="activity-options">
          {activityOptions.map((activity) => {
            const Icon = activity.icon;
            const isSelected = activity.id === activityId;
            return (
              <button
                key={activity.id}
                type="button"
                className={isSelected ? "activity-option selected" : "activity-option"}
                aria-pressed={isSelected}
                onClick={() => {
                  if (activity.id !== activityId) {
                    setActivityId(activity.id);
                    setSelectedRoute(null);
                    setRestoredRouteId(null);
                  }
                }}
              >
                <span className="activity-icon">
                  <Icon size={20} />
                </span>
                <span>
                  <strong>{activity.label}</strong>
                  <small>{activity.hint}</small>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <label>
        Tittel
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={activityRequiresCustomTitle ? "Hva skal du gjøre?" : selectedActivity.title}
        />
      </label>
      <label>
        Kort beskrivelse
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Valgfritt"
        />
      </label>
      <div className="form-field">
        <label htmlFor="plan-starts-at">Når?</label>
        <input
          id="plan-starts-at"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
        <div className="time-chip-row" role="group" aria-label="Raske tidspunkt">
          {createPlanTimePresets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="time-chip"
              onClick={() => setStartsAt(toDateTimeLocal(createPlanStartForPreset(preset.id)))}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
      <div className="form-field">
        <span className="field-label">Varighet</span>
        <div className="time-chip-row" role="group" aria-label="Varighet">
          {durationPresets.map((preset) => {
            const isSelected = !showCustomDuration && durationTotalMinutes === preset.minutes;
            return (
              <button
                key={preset.minutes}
                type="button"
                className="time-chip"
                aria-pressed={isSelected}
                onClick={() => {
                  setDurationHours(Math.floor(preset.minutes / 60));
                  setDurationMinutes(preset.minutes % 60);
                  setShowCustomDuration(false);
                }}
              >
                {preset.label}
              </button>
            );
          })}
          <button
            type="button"
            className="time-chip"
            aria-pressed={showCustomDuration}
            onClick={() => setShowCustomDuration(true)}
          >
            Egendefinert
          </button>
        </div>
        {showCustomDuration && (
          <div className="duration-picker" role="group" aria-label="Egendefinert varighet">
            <label>
              Timer
              <select value={durationHours} onChange={(event) => setDurationHours(Number(event.target.value))}>
                {durationHourOptions.map((hour) => (
                  <option key={hour} value={hour}>{hour}</option>
                ))}
              </select>
            </label>
            <label>
              Minutter
              <select value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))}>
                {durationMinuteOptions.map((minute) => (
                  <option key={minute} value={minute}>{minute.toString().padStart(2, "0")}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
      <div className="form-field place-field">
        <label htmlFor="plan-place">Hvor?</label>
        <div className="place-input-wrap">
          <Search size={18} aria-hidden="true" />
          <input
            id="plan-place"
            value={placeName}
            onChange={(event) => setPlaceName(event.target.value)}
            placeholder="Søk etter sted eller skriv fritt"
          />
        </div>
        {isSearchingForPlace && <p className="muted compact">Søker etter steder...</p>}
        {placeSearchMessage && <p className="muted compact">{placeSearchMessage}</p>}
        {visiblePlaceResults.length > 0 && (
          <div className="place-results" role="listbox" aria-label="Steder">
            {visiblePlaceResults.map((place) => (
              <button
                key={place.id}
                type="button"
                className="place-result"
                onClick={() => {
                  setSelectedPlace(place);
                  setPlaceName(place.name);
                  setPlaceResults([]);
                  setPlaceSearchMessage(null);
                }}
              >
                <MapPin size={18} />
                <span>
                  <strong>{place.name}</strong>
                  <small>{place.address}</small>
                </span>
              </button>
            ))}
          </div>
        )}
        {showEmptyPlaceState && (
          <p className="muted compact">Ingen steder funnet. Stedet lagres som tekst uten kart.</p>
        )}
        {selectedPlace && (
          <>
            <SelectedPlaceMapPreview place={selectedPlace} />
            <p className="muted compact">Møtepunktet vises til dem som kan se planen.</p>
          </>
        )}
      </div>
      <StravaRoutePicker
        activityId={activityId}
        meeting={selectedPlace ? [selectedPlace.latitude, selectedPlace.longitude] : null}
        selected={selectedRoute}
        restoreId={restoredRouteId}
        onSelect={(route) => { setSelectedRoute(route); setRestoredRouteId(null); }}
        beforeConnect={() => savePlanDraft(currentUserId, {
          activityId, title, description, startsAt, durationHours, durationMinutes, showCustomDuration,
          placeName, selectedPlace, audience, selectedGjengId, recurring, recurFreq, recurInterval,
          recurWeekdays, recurEndKind, recurUntil, recurCount, openInvite, selected, selectedFriendListIds,
          routeId: selectedRoute?.id ?? null
        } satisfies CreatePlanDraft)}
      />
      <fieldset className="segmented-field">
        <legend>Hvem kan se planen?</legend>
        <button
          className={audience === "all_friends" ? "selected" : ""}
          onClick={() => {
            setAudience("all_friends");
            setSelectedGjengId(null);
          }}
        >
          Alle venner
        </button>
        <button
          className={audience === "selected_friends" ? "selected" : ""}
          onClick={() => {
            setAudience("selected_friends");
            setSelectedGjengId(null);
          }}
        >
          Velg venner
        </button>
      </fieldset>
      {gjenger.length > 0 && (
        <div className="form-field">
          <span className="field-label">Eller del med en gjeng</span>
          <div className="gjeng-chip-row" role="group" aria-label="Gjenger">
            {gjenger.map((gjeng) => {
              const GjengIcon = activityForPlan(gjeng.activity_id).icon;
              const isSelected = audience === "gjeng" && selectedGjengId === gjeng.id;
              return (
                <button
                  key={gjeng.id}
                  type="button"
                  className={isSelected ? "gjeng-chip selected" : "gjeng-chip"}
                  aria-pressed={isSelected}
                  onClick={() => {
                    if (isSelected) {
                      setAudience("all_friends");
                      setSelectedGjengId(null);
                    } else {
                      setAudience("gjeng");
                      setSelectedGjengId(gjeng.id);
                    }
                  }}
                >
                  <GjengIcon size={16} />
                  <span>{gjeng.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {audience === "gjeng" && selectedGjeng && (
        <p className="muted">
          Planen deles med alle i «{selectedGjeng.name}» – også de som blir med i gjengen senere.
          Den havner i gjengens historikk når den er over.
        </p>
      )}
      {audience === "gjeng" && selectedGjeng && (
        <div className="recurrence-field">
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={recurring}
              onChange={(event) => setRecurring(event.target.checked)}
            />
            <span>
              <strong>Gjenta planen</strong>
              <small>Lag en fast tur, f.eks. hver tirsdag.</small>
            </span>
          </label>
          {recurring && (
            <div className="recurrence-body">
              <div className="time-chip-row" role="group" aria-label="Hvor ofte">
                {([
                  { id: "daily", interval: 1, label: "Hver dag" },
                  { id: "weekly", interval: 1, label: "Hver uke" },
                  { id: "weekly2", interval: 2, label: "Annenhver uke" }
                ] as const).map((option) => {
                  const isSelected =
                    option.id === "daily"
                      ? recurFreq === "daily"
                      : recurFreq === "weekly" && recurInterval === option.interval;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className="time-chip"
                      aria-pressed={isSelected}
                      onClick={() => {
                        if (option.id === "daily") {
                          setRecurFreq("daily");
                          setRecurInterval(1);
                        } else {
                          setRecurFreq("weekly");
                          setRecurInterval(option.interval);
                        }
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {recurFreq === "weekly" && (
                <div className="weekday-row" role="group" aria-label="Ukedager">
                  {recurWeekdayLabels.map((label, day) => {
                    const isSelected = effectiveWeekdays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        className={isSelected ? "weekday-pill selected" : "weekday-pill"}
                        aria-pressed={isSelected}
                        onClick={() =>
                          setRecurWeekdays((current) => {
                            const base = current.length > 0 ? current : [anchorWeekday];
                            const next = base.includes(day)
                              ? base.filter((d) => d !== day)
                              : [...base, day];
                            return next.length > 0 ? next : [anchorWeekday];
                          })
                        }
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="time-chip-row" role="group" aria-label="Varer til">
                {([
                  { id: "never", label: "Uten slutt" },
                  { id: "until", label: "Til dato" },
                  { id: "count", label: "Antall ganger" }
                ] as const).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className="time-chip"
                    aria-pressed={recurEndKind === option.id}
                    onClick={() => setRecurEndKind(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {recurEndKind === "until" && (
                <label>
                  Siste dato
                  <input
                    type="date"
                    value={recurUntil}
                    min={startsAt.slice(0, 10)}
                    onChange={(event) => setRecurUntil(event.target.value)}
                  />
                </label>
              )}
              {recurEndKind === "count" && (
                <label>
                  Antall ganger
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={recurCount}
                    onChange={(event) => setRecurCount(clampInteger(event.target.value, 1, 200))}
                  />
                </label>
              )}
              <p className="muted compact">
                {recurrenceSummary(recurFreq, recurInterval, effectiveWeekdays, recurEndKind, recurUntil, recurCount, startTimeLabel)}
              </p>
            </div>
          )}
        </div>
      )}
      {audience === "all_friends" && (
        <>
          <p className="muted">Planen deles med venner du har nå og venner du legger til senere.</p>
          <label className="toggle-row open-invite-row">
            <input
              type="checkbox"
              checked={openInvite}
              onChange={(event) => setOpenInvite(event.target.checked)}
            />
            <span>
              <strong>Åpen invitasjon</strong>
              <small>Alle du deler lenken med kan bli med – også de som ikke har Join ennå.</small>
            </span>
          </label>
        </>
      )}
      {audience === "selected_friends" && (
        <div className="choice-list">
          {friendLists.length > 0 && (
            <div className="choice-group">
              <span className="field-label">Lister</span>
              {friendLists.map((list) => (
                <label key={list.id} className="check-row list-check-row">
                  <input
                    type="checkbox"
                    checked={selectedFriendListIds.includes(list.id)}
                    onChange={(event) =>
                      setSelectedFriendListIds((current) =>
                        event.target.checked
                          ? [...current, list.id]
                          : current.filter((id) => id !== list.id)
                      )
                    }
                  />
                  <span className="list-icon"><Users size={17} /></span>
                  <span>
                    <strong>{list.name}</strong>
                    <small>{list.member_ids.length} venner</small>
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="choice-group">
            <span className="field-label">Venner</span>
            {friends.map((friend) => (
              <label key={friend.id} className="check-row">
                <input
                  type="checkbox"
                  checked={selected.includes(friend.id)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, friend.id]
                        : current.filter((id) => id !== friend.id)
                    )
                  }
                />
                <Avatar name={friend.full_name} avatarPath={friend.avatar_path} />
                <span>{friend.full_name}</span>
              </label>
            ))}
          </div>
          {friends.length === 0 && (
            <p className="muted">Du trenger minst én venn for å velge mottakere.</p>
          )}
        </div>
      )}
      <button
        className="primary-action"
        disabled={!canPublish || busy}
        onClick={createPlan}
      >
        {busy
          ? isRecurring ? "Lager fast plan..." : "Deler planen..."
          : isRecurring ? "Lag fast plan" : "Del planen"}
      </button>
    </section>
  );
}

function SelectedPlaceMapPreview({ place }: { place: PlaceOption }) {
  return (
    <div className="selected-place-map">
      <RouteMap meeting={[place.latitude, place.longitude]} label={`Kart over ${place.name}`} />
      <div className="selected-place-caption">
        <MapPin size={16} />
        <span>
          <strong>{place.name}</strong>
          <small>{place.address}</small>
        </span>
      </div>
    </div>
  );
}

type PlanMapPreviewPlan = Pick<
  PlanSummary,
  "place_name" | "place_address" | "public_latitude" | "public_longitude" | "location_visibility" | "planned_route"
>;

type PlanMapCoordinate = {
  latitude: number;
  longitude: number;
};

function PlanMapPreview({
  plan,
  variant = "feed",
  onOpen,
  onExpand
}: {
  plan: PlanMapPreviewPlan;
  variant?: "feed" | "detail";
  onOpen?: () => void;
  onExpand?: () => void;
}) {
  const coordinate = planMapCoordinate(plan);
  if (!coordinate && !plan.planned_route) return null;

  const label = `Kart over ${plan.place_name}`;

  return (
    <div className={`plan-map-frame ${variant}`}>
      <div className="plan-map-preview has-coordinates" role="img" aria-label={label}>
        <RouteMap route={plan.planned_route} meeting={coordinate ? [coordinate.latitude, coordinate.longitude] : null} label={label} />
        {onOpen && (
          <button type="button" className="plan-map-open-hit-area" onClick={onOpen} aria-label={`Åpne plan ved ${plan.place_name}`} />
        )}
      </div>
      {onExpand && (
        <button
          type="button"
          className="map-expand-button"
          onClick={onExpand}
          aria-label={`Utvid kart over ${plan.place_name}`}
        >
          <Maximize2 size={16} />
        </button>
      )}
    </div>
  );
}

function ExpandedMapModal({ plan, onClose }: { plan: PlanMapPreviewPlan; onClose: () => void }) {
  const coordinate = planMapCoordinate(plan);
  const address = plan.place_address.trim();

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-backdrop map-modal-backdrop" onClick={onClose}>
      <section className="modal map-modal" aria-modal="true" role="dialog" aria-labelledby="expanded-map-title" onClick={(event) => event.stopPropagation()}>
        <div className="map-modal-header">
          <div>
            <span className="location-label">Kart</span>
            <h2 id="expanded-map-title">{plan.place_name}</h2>
            {address && <p>{address}</p>}
          </div>
          <button type="button" className="icon-only" onClick={onClose} aria-label="Lukk kart">
            <X size={18} />
          </button>
        </div>
        <RouteMap route={plan.planned_route} meeting={coordinate ? [coordinate.latitude, coordinate.longitude] : null} label={`Interaktivt kart over ${plan.place_name}`} interactive />
      </section>
    </div>
  );
}

function PlanLocationCard({ plan }: { plan: PlanDetail }) {
  const [expandedMap, setExpandedMap] = useState(false);
  const address = plan.place_address.trim();
  const coordinate = planMapCoordinate(plan);
  const precisionLabel = coordinate ? "Nøyaktig møtepunkt" : "Delt sted";
  const locationText = address || (coordinate
    ? "Koordinater er delt for planen."
    : "Stedet er lagret uten kartkoordinater.");

  return (
    <>
      <section className={`plan-location-card ${coordinate || plan.planned_route ? "" : "no-map"}`} aria-labelledby="plan-location-heading">
        {(coordinate || plan.planned_route) && <PlanMapPreview plan={plan} variant="detail" onExpand={() => setExpandedMap(true)} />}
        <div className="plan-location-copy">
          <span className="location-label">Sted</span>
          <h2 id="plan-location-heading">{plan.place_name}</h2>
          <p className="location-address">{locationText}</p>
          {plan.planned_route && <p className="location-precision"><RouteIcon size={16} /><span>{plan.planned_route.name} · ca. {(plan.planned_route.distance_m / 1000).toFixed(1).replace(".", ",")} km</span></p>}
          <p className="location-precision">
            <MapPin size={16} />
            <span>{precisionLabel}</span>
          </p>
        </div>
      </section>
      {expandedMap && <ExpandedMapModal plan={plan} onClose={() => setExpandedMap(false)} />}
    </>
  );
}

function formatPlanDetailDate(value: string): string {
  return planDetailDateFormatter.format(new Date(value));
}

function formatPlanDetailTimeRange(startsAt: string, endsAt: string): string {
  return `${planDetailTimeFormatter.format(new Date(startsAt))}–${planDetailTimeFormatter.format(new Date(endsAt))}`;
}

function planMapCoordinate(plan: PlanMapPreviewPlan): PlanMapCoordinate | null {
  if (finiteNumber(plan.public_latitude) && finiteNumber(plan.public_longitude)) {
    return { latitude: plan.public_latitude, longitude: plan.public_longitude };
  }

  return null;
}

function finiteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function PlanDetailPage({
  planId,
  currentUserId,
  currentUserName,
  currentUserIsGuest,
  friends,
  initialPlan,
  navigate,
  refresh,
  afterMeaningfulAction,
  onMessage
}: {
  planId: string;
  currentUserId: string;
  currentUserName: string;
  currentUserIsGuest: boolean;
  friends: Friend[];
  initialPlan?: PlanSummary;
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  afterMeaningfulAction: (reason: string) => void;
  onMessage: (message: string) => void;
}) {
  const [plan, setPlan] = useState<PlanDetail | null>(() =>
    initialPlan ? planDetailFromSummary(initialPlan, currentUserId) : null
  );
  const [detailLoaded, setDetailLoaded] = useState(Boolean(initialPlan));
  const [comment, setComment] = useState("");
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [mentionState, setMentionState] = useState<{ start: number; query: string } | null>(null);
  const [mentionHighlight, setMentionHighlight] = useState(0);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const [busy, setBusy] = useState(false);
  const [participantBusyId, setParticipantBusyId] = useState<string | null>(null);
  const [showingParticipants, setShowingParticipants] = useState(false);
  const [showingPlanMenu, setShowingPlanMenu] = useState(false);
  const [editingPlan, setEditingPlan] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  // For recurring plans, edit/delete first ask which occurrences to apply to.
  const [seriesAction, setSeriesAction] = useState<
    { mode: "edit"; values: PlanEditValues } | { mode: "delete" } | null
  >(null);

  const loadDetail = useCallback(async () => {
    const { data, error } = await requireSupabase().rpc("get_plan_detail", {
      target_plan_id: planId
    });
    if (error) throw error;
    const rows = Array.isArray(data) ? data : [];
    setPlan(normalizePlanDetail((rows as PlanDetailResult[])[0]));
    setDetailLoaded(true);
  }, [planId]);

  useEffect(() => {
    loadDetail().catch((error) => {
      setDetailLoaded(true);
      onMessage(errorMessage(error));
    });
  }, [loadDetail, onMessage]);

  async function toggleJoin() {
    if (!plan) return;
    if (plan.host_id === currentUserId) return;
    setBusy(true);
    try {
      const fn = isJoined ? "leave_plan" : "join_plan";
      const { error } = await requireSupabase().rpc(fn, { target_plan_id: plan.id });
      if (error) throw error;
      if (fn === "join_plan") afterMeaningfulAction("Få beskjed om kommentarer og endringer på planer du blir med på.");
      await loadDetail();
      await refresh();
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const mentionMatches = useMemo(
    () => (mentionState ? matchFriends(friends, mentionState.query) : []),
    [friends, mentionState]
  );

  function syncMentionState(value: string, caret: number) {
    const active = friends.length > 0 ? findActiveMention(value, caret) : null;
    setMentionState(active);
    setMentionHighlight(0);
  }

  function handleCommentChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = event.target.value;
    setComment(value);
    syncMentionState(value, event.target.selectionStart ?? value.length);
  }

  function selectMention(friend: Friend) {
    if (!mentionState) return;
    const caret = commentInputRef.current?.selectionStart ?? comment.length;
    const before = comment.slice(0, mentionState.start);
    const after = comment.slice(caret);
    const insertion = `@${friend.full_name} `;
    const nextValue = `${before}${insertion}${after}`;
    setComment(nextValue);
    setMentions((current) =>
      current.some((mention) => mention.id === friend.id)
        ? current
        : [...current, { id: friend.id, name: friend.full_name }]
    );
    setMentionState(null);
    const nextCaret = before.length + insertion.length;
    requestAnimationFrame(() => {
      const input = commentInputRef.current;
      if (input) {
        input.focus();
        input.setSelectionRange(nextCaret, nextCaret);
      }
    });
  }

  function handleCommentKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionState && mentionMatches.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionHighlight((index) => (index + 1) % mentionMatches.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionHighlight((index) => (index - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        selectMention(mentionMatches[mentionHighlight] ?? mentionMatches[0]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMentionState(null);
        return;
      }
    } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void addComment();
    }
  }

  async function addComment() {
    if (!comment.trim()) return;
    try {
      const { error } = await requireSupabase().rpc("add_plan_comment", {
        target_plan_id: planId,
        comment_body: encodeCommentBody(comment, mentions)
      });
      if (error) throw error;
      setComment("");
      setMentions([]);
      setMentionState(null);
      await loadDetail();
    } catch (error) {
      onMessage(errorMessage(error));
    }
  }

  async function sharePlan() {
    if (!plan) return;
    try {
      if (plan.audience_kind === "gjeng" && plan.gjeng_id && plan.gjeng_name) {
        const { data, error } = await requireSupabase().rpc("create_gjeng_invite", {
          target_gjeng_id: plan.gjeng_id
        });
        if (error) throw error;
        const token = (data as Array<{ gjeng_token: string }>)[0]?.gjeng_token;
        if (!token) throw new Error("Gjenglenken ble opprettet uten token.");
        await shareOrCopy({
          title: `Bli med i ${plan.gjeng_name} på Join`,
          text: `Bli med i «${plan.gjeng_name}» på Join for å se «${plan.title}».`,
          url: gjengUrl(token)
        });
        onMessage("Gjenglenken er klar – nye folk må inn i gjengen før de kan bli med på planen.");
        return;
      }

      let open = plan.allow_guest_join;
      // Sharing an all-friends plan turns it into an open invitation, so the link
      // actually lets people (even non-users) join. Selected-friends plans stay
      // private.
      if (viewerIsHost && plan.audience_kind === "all_friends" && !open) {
        const { error: enableError } = await requireSupabase().rpc("update_plan", {
          target_plan_id: planId,
          plan_title: plan.title,
          plan_description: plan.description,
          plan_starts_at: plan.starts_at,
          plan_ends_at: plan.ends_at,
          plan_capacity: plan.capacity,
          plan_participant_list_visible: true,
          plan_comments_locked: plan.comments_locked,
          plan_allow_guest_join: true
        });
        if (enableError) throw enableError;
        open = true;
        await loadDetail();
      }

      const { data, error } = await requireSupabase().rpc("create_plan_link", {
        target_plan_id: planId
      });
      if (error) throw error;
      const token = (data as Array<{ plan_token: string }>)[0]?.plan_token;
      const share = open
        ? {
            title: `${currentUserName} inviterer: ${plan.title}`,
            text: `Bli med på «${plan.title}» ${formatPlanTime(plan.starts_at, plan.ends_at)} · ${plan.place_name}. Bli med!`,
            url: planUrl(token)
          }
        : {
            title: `${currentUserName} inviterte deg til en plan på join`,
            text: `${currentUserName} inviterte deg til en plan på join. Åpne join for å se om du har tilgang.`,
            url: planUrl(token)
          };
      await shareOrCopy(share);
      onMessage(open ? "Delingslenken er klar – alle med lenken kan bli med." : "Planlenken er klar.");
    } catch (error) {
      onMessage(errorMessage(error));
    }
  }

  async function sharePlace() {
    if (!plan) return;
    try {
      const place = plan.place_address.trim()
        ? `${plan.place_name}\n${plan.place_address.trim()}`
        : plan.place_name;
      const result = await shareOrCopy({
        title: `Møtested for ${plan.title}`,
        text: place
      });
      onMessage(result === "copied" ? "Stedet er kopiert." : "Stedet er delt.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      onMessage(errorMessage(error));
    }
  }

  async function updatePlan(values: PlanEditValues) {
    // A recurring occurrence: ask which occurrences the edit applies to first.
    if (plan?.series_id) {
      setEditingPlan(false);
      setSeriesAction({ mode: "edit", values });
      return;
    }
    await applySingleUpdate(values);
  }

  async function applySingleUpdate(values: PlanEditValues) {
    setEditBusy(true);
    try {
      const start = new Date(values.startsAt);
      const end = new Date(start.getTime() + values.durationMinutes * 60 * 1000);
      const { error } = await requireSupabase().rpc("update_plan", {
        target_plan_id: planId,
        plan_title: values.title.trim(),
        plan_description: values.description.trim(),
        plan_starts_at: start.toISOString(),
        plan_ends_at: end.toISOString(),
        plan_capacity: values.capacityEnabled ? values.capacity : null,
        plan_participant_list_visible: true,
        plan_comments_locked: values.commentsLocked,
        plan_allow_guest_join: values.openInvite
      });
      if (error) throw error;
      await loadDetail();
      await refresh();
      setEditingPlan(false);
      onMessage("Planen er oppdatert.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setEditBusy(false);
    }
  }

  // Apply an edit or cancel across a recurring series at the chosen scope.
  async function applySeriesAction(scope: "occurrence" | "following" | "all") {
    if (!seriesAction || !plan) return;
    setEditBusy(true);
    setBusy(true);
    try {
      if (seriesAction.mode === "edit") {
        const values = seriesAction.values;
        if (scope === "occurrence") {
          await applySingleUpdate(values);
        } else {
          const { error } = await requireSupabase().rpc("update_plan_series", {
            target_series_id: plan.series_id,
            from_plan_id: plan.id,
            edit_scope: scope,
            plan_title: values.title.trim(),
            plan_description: values.description.trim(),
            plan_capacity: values.capacityEnabled ? values.capacity : null,
            plan_comments_locked: values.commentsLocked,
            plan_start_time: values.startsAt.slice(11, 16),
            plan_duration_minutes: values.durationMinutes
          });
          if (error) throw error;
          await loadDetail();
          await refresh();
          onMessage("De faste turene er oppdatert.");
        }
      } else {
        if (scope === "occurrence") {
          const { error } = await requireSupabase().rpc("cancel_plan", { target_plan_id: plan.id });
          if (error) throw error;
          await refresh();
          onMessage("Turen er avlyst.");
          navigate(plan.gjeng_id ? `/gjenger/${plan.gjeng_id}` : "/");
          return;
        }
        const { error } = await requireSupabase().rpc("cancel_plan_series", {
          target_series_id: plan.series_id,
          from_plan_id: plan.id,
          cancel_scope: scope
        });
        if (error) throw error;
        await refresh();
        onMessage(scope === "all" ? "Hele serien er avlyst." : "Turene fra og med denne er avlyst.");
        navigate(plan.gjeng_id ? `/gjenger/${plan.gjeng_id}` : "/");
        return;
      }
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setEditBusy(false);
      setBusy(false);
      setSeriesAction(null);
    }
  }

  async function updateParticipantRelationship(participant: PlanParticipant) {
    if (participant.relationship_state === "none") {
      setParticipantBusyId(participant.id);
      try {
        const { error } = await requireSupabase().rpc("send_friend_request", {
          target_user_id: participant.id
        });
        if (error) throw error;
        afterMeaningfulAction("Få beskjed når venneforespørsler blir godtatt.");
        await loadDetail();
        await refresh();
        onMessage("Venneforespørsel sendt.");
      } catch (error) {
        onMessage(errorMessage(error));
      } finally {
        setParticipantBusyId(null);
      }
      return;
    }

    if (participant.relationship_state === "incoming_request" && participant.request_id) {
      setParticipantBusyId(participant.id);
      try {
        const { error } = await requireSupabase().rpc("accept_friend_request", {
          target_request_id: participant.request_id
        });
        if (error) throw error;
        await loadDetail();
        await refresh();
        onMessage("Dere er venner på Join.");
      } catch (error) {
        onMessage(errorMessage(error));
      } finally {
        setParticipantBusyId(null);
      }
    }
  }

  async function deletePlan() {
    if (!plan) return;
    // A recurring occurrence: ask which occurrences to cancel first.
    if (plan.series_id) {
      setSeriesAction({ mode: "delete" });
      return;
    }
    if (!window.confirm("Slett planen? Vennene dine vil ikke lenger se den.")) return;
    setBusy(true);
    try {
      const { error } = await requireSupabase().rpc("cancel_plan", {
        target_plan_id: plan.id
      });
      if (error) throw error;
      await refresh();
      onMessage("Planen er slettet.");
      navigate("/");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (!detailLoaded) return <LoadingScreen label="Henter plan..." />;
  if (!plan) {
    return <EmptyState title="Planen er ikke tilgjengelig" text="Den kan være privat, slettet eller utenfor delingen din." />;
  }

  const isJoined = plan.viewer_participation_state === "joined" || plan.viewer_participation_state === "confirmed";
  const viewerIsHost = plan.host_id === currentUserId;
  const audienceLabel =
    plan.audience_kind === "all_friends"
      ? "Alle venner"
      : plan.audience_kind === "gjeng"
        ? `Delt med ${plan.gjeng_name ?? "gjengen"}`
        : "Bare inviterte";
  const joinLabel = isJoined ? "Du blir med" : "Bli med";
  const openProfile = (userId: string) => navigate(`/u/${userId}`);
  const focusComments = () => {
    commentInputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    commentInputRef.current?.focus();
  };
  const hostParticipant = plan.participants.find((participant) => participant.is_host);
  const participantHeading = `${plan.participant_count} ${plan.participant_count === 1 ? "deltaker" : "deltakere"}`;
  const hasMap = planMapCoordinate(plan) !== null || !!plan.planned_route;

  return (
    <section className="detail-page plan-detail-redesign">
      <nav className="plan-detail-topbar" aria-label="Planhandlinger">
        <button type="button" className="plan-topbar-button" onClick={() => window.history.back()} aria-label="Tilbake">
          <ArrowLeft size={24} />
        </button>
        <div className="plan-topbar-actions">
          <button type="button" className="plan-topbar-button" onClick={() => void sharePlan()} aria-label="Del plan">
            <Share size={23} />
          </button>
          {viewerIsHost && plan.status === "scheduled" && (
            <div className="plan-overflow-wrap">
              <button
                type="button"
                className="plan-topbar-button"
                aria-label="Flere valg"
                aria-expanded={showingPlanMenu}
                onClick={() => setShowingPlanMenu((open) => !open)}
              >
                <Ellipsis size={25} />
              </button>
              {showingPlanMenu && (
                <div className="plan-overflow-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowingPlanMenu(false);
                      setEditingPlan(true);
                    }}
                  >
                    <Edit3 size={18} />
                    Rediger plan
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="danger"
                    disabled={busy}
                    onClick={() => {
                      setShowingPlanMenu(false);
                      void deletePlan();
                    }}
                  >
                    <Trash2 size={18} />
                    Slett plan
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      <header className="plan-invitation-heading">
        <div className="plan-invitation-context">
          {viewerIsHost ? (
            <div className="plan-host-byline">
              <Avatar name={plan.host_name} avatarPath={hostParticipant?.avatar_path} />
              <span><strong>{plan.host_name}</strong> inviterer</span>
            </div>
          ) : (
            <button type="button" className="plan-host-byline" onClick={() => openProfile(plan.host_id)}>
              <Avatar name={plan.host_name} avatarPath={hostParticipant?.avatar_path} />
              <span><strong>{plan.host_name}</strong> inviterer</span>
            </button>
          )}
          {plan.gjeng_id && plan.gjeng_name ? (
            <button
              type="button"
              className="plan-audience-label"
              onClick={() => navigate(`/gjenger/${plan.gjeng_id}`)}
            >
              <UsersRound size={17} />
              {plan.gjeng_name}
            </button>
          ) : (
            <span className="plan-audience-label">
              <Lock size={16} />
              {audienceLabel}
            </span>
          )}
        </div>
        <div className="plan-title-row">
          <h1>{plan.title}</h1>
          {plan.series_id && (
            <span className="plan-series-label">
              <Repeat size={15} /> Fast plan
            </span>
          )}
        </div>
      </header>

      <section className="plan-facts-band" aria-label="Planinformasjon">
        <div className="plan-fact">
          <span className="plan-fact-icon"><CalendarDays size={22} /></span>
          <span className="plan-fact-label">Når</span>
          <strong>{formatPlanDetailDate(plan.starts_at)}</strong>
          <div className="plan-time-row">
            <span>{formatPlanDetailTimeRange(plan.starts_at, plan.ends_at)}</span>
            <PlanTimeSuggestionControl
              plan={plan}
              currentUserId={currentUserId}
              currentUserIsGuest={currentUserIsGuest}
              onPlanChanged={async () => {
                await loadDetail();
                await refresh();
              }}
              onMessage={onMessage}
            />
          </div>
        </div>
        <div className="plan-fact">
          <span className="plan-fact-icon"><MapPin size={22} /></span>
          <span className="plan-fact-label">Hvor</span>
          <strong>{plan.place_name}</strong>
          <button type="button" className="plan-place-share" onClick={() => void sharePlace()}>
            <Share2 size={15} /> Del sted
          </button>
        </div>
        <div className="plan-fact plan-fact-details">
          <span className="plan-fact-icon"><AlignLeft size={22} /></span>
          <span className="plan-fact-label">Detaljer</span>
          <p>{plan.description || "Ta med et smil eller bare deg selv."}</p>
        </div>
      </section>

      {hasMap && <PlanLocationCard plan={plan} />}
      {plan.status !== "cancelled" && new Date(plan.ends_at).getTime() <= Date.now() && (
        <PlanStravaActivity key={`${currentUserId}:${plan.id}`} planId={plan.id} link={plan.strava_activity} isHost={viewerIsHost} onChanged={async () => { await loadDetail(); await refresh(); }} />
      )}

      <div className="plan-state-actions">
        {viewerIsHost ? (
          <div className="plan-primary-state">
            <CheckCircle2 size={23} />
            Du er vert
          </div>
        ) : (
          <button type="button" className="plan-primary-state" disabled={busy} onClick={toggleJoin}>
            <CheckCircle2 size={23} />
            {joinLabel}
          </button>
        )}
        {viewerIsHost && plan.status === "scheduled" && (
          <button type="button" className="plan-edit-inline" onClick={() => setEditingPlan(true)}>
            <Edit3 size={21} />
            Rediger plan
          </button>
        )}
      </div>
      {editingPlan && (
        <PlanEditModal
          plan={plan}
          busy={editBusy}
          onClose={() => setEditingPlan(false)}
          onSave={updatePlan}
        />
      )}
      {seriesAction && (
        <SeriesScopeSheet
          mode={seriesAction.mode}
          busy={editBusy || busy}
          onChoose={applySeriesAction}
          onClose={() => setSeriesAction(null)}
        />
      )}
      <section id="participants" className="plan-participants-section">
        <header className="plan-section-heading">
          <h2>{participantHeading}</h2>
          {plan.participant_count > 1 && (
            <button type="button" onClick={() => setShowingParticipants(true)}>Vis alle</button>
          )}
        </header>
        {plan.participants.length === 0 ? (
          <p className="muted">Ingen deltakere ennå.</p>
        ) : (
          <div className="participant-list plan-participant-list">
            {plan.participants.map((participant) => (
              <ParticipantRow
                key={participant.id}
                participant={participant}
                busy={participantBusyId === participant.id}
                onAction={() => updateParticipantRelationship(participant)}
                onOpenProfile={openProfile}
              />
            ))}
          </div>
        )}
      </section>
      {showingParticipants && (
        <ParticipantListModal
          participants={plan.participants}
          participantCount={plan.participant_count}
          busyParticipantId={participantBusyId}
          onClose={() => setShowingParticipants(false)}
          onAction={updateParticipantRelationship}
          onOpenProfile={openProfile}
        />
      )}
      <section className="comments-panel plan-coordination-panel">
        <header className="plan-coordination-heading">
          <MessageCircle size={24} />
          <div>
            <h2>Sp&oslash;r eller avtal</h2>
            <p>Noe de andre b&oslash;r vite?</p>
          </div>
        </header>
        {plan.comments.map((item) => (
          <article key={item.id} className="comment-row">
            <button
              type="button"
              className="comment-author-avatar"
              onClick={() => openProfile(item.author_id)}
              aria-label={`Åpne profilen til ${item.author_name}`}
            >
              <Avatar name={item.author_name} avatarPath={item.author_avatar_path} />
            </button>
            <div className="comment-content">
              <div className="comment-header">
                <button
                  type="button"
                  className="comment-author-name"
                  onClick={() => openProfile(item.author_id)}
                >
                  {item.author_name}
                </button>
                <span className="comment-time">{formatRelativeTime(item.created_at)}</span>
              </div>
              <p><CommentBody body={item.body} onOpenProfile={openProfile} /></p>
              <ReactionBar
                target="comment"
                targetId={item.id}
                summary={item.reaction_summary}
                viewerReaction={item.viewer_reaction}
                compact
                onMessage={onMessage}
              />
            </div>
          </article>
        ))}
        {plan.comments_locked ? (
          <p className="plan-comments-locked">
            <Lock size={16} /> Kommentarene er låst av verten
          </p>
        ) : (
          <div className="comment-composer">
            {mentionState && mentionMatches.length > 0 && (
              <ul className="mention-suggestions" role="listbox">
                {mentionMatches.map((friend, index) => (
                  <li key={friend.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === mentionHighlight}
                      className={index === mentionHighlight ? "active" : ""}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        selectMention(friend);
                      }}
                    >
                      <Avatar name={friend.full_name} avatarPath={friend.avatar_path} />
                      <span>{friend.full_name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="comment-box">
              <textarea
                ref={commentInputRef}
                value={comment}
                onChange={handleCommentChange}
                onKeyDown={handleCommentKeyDown}
                onBlur={() => setMentionState(null)}
                rows={1}
                placeholder="Skriv en kommentar... bruk @ for å nevne"
              />
              <button className="icon-only filled" disabled={!comment.trim()} onClick={addComment} aria-label="Send kommentar">
                <Send size={19} />
              </button>
            </div>
          </div>
        )}
      </section>
      <PlanEngagementBar
        plan={plan}
        onOpenComments={focusComments}
        onShare={sharePlan}
        commentsInline
        onMessage={onMessage}
      />
    </section>
  );
}

type PlanEditValues = {
  title: string;
  description: string;
  startsAt: string;
  durationMinutes: number;
  capacityEnabled: boolean;
  capacity: number;
  commentsLocked: boolean;
  openInvite: boolean;
};

function PlanEditModal({
  plan,
  busy,
  onClose,
  onSave
}: {
  plan: PlanDetail;
  busy: boolean;
  onClose: () => void;
  onSave: (values: PlanEditValues) => Promise<void>;
}) {
  const initialDuration = planDurationMinutes(plan.starts_at, plan.ends_at);
  const [title, setTitle] = useState(plan.title);
  const [description, setDescription] = useState(plan.description);
  const [startsAt, setStartsAt] = useState(toDateTimeLocal(new Date(plan.starts_at)));
  const [durationHours, setDurationHours] = useState(Math.floor(initialDuration / 60));
  const [durationMinutes, setDurationMinutes] = useState(initialDuration % 60);
  const [capacityEnabled, setCapacityEnabled] = useState(plan.capacity !== null);
  const [capacity, setCapacity] = useState(plan.capacity ?? Math.max(2, plan.participant_count));
  const [commentsLocked, setCommentsLocked] = useState(plan.comments_locked);
  const [openInvite, setOpenInvite] = useState(plan.allow_guest_join);
  const canOpenInvite = plan.audience_kind === "all_friends";
  const totalDurationMinutes = durationHours * 60 + durationMinutes;
  const canSave =
    title.trim().length > 0 &&
    totalDurationMinutes > 0 &&
    (!capacityEnabled || capacity >= Math.max(1, plan.participant_count));

  return (
    <div className="modal-backdrop">
      <section className="modal plan-edit-modal">
        <div className="modal-header">
          <div>
            <h2>Rediger plan</h2>
            <p>Endringer vises for vennene som kan se planen.</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk">
            <X size={17} />
          </button>
        </div>
        <label>
          Tittel
          <input value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label>
          Kort beskrivelse
          <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
        </label>
        <label>
          N&aring;r?
          <input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} />
        </label>
        <div className="form-field">
          <span className="field-label">Varighet</span>
          <div className="duration-picker" role="group" aria-label="Varighet">
            <label>
              Timer
              <input
                type="number"
                min="0"
                max="24"
                value={durationHours}
                onChange={(event) => setDurationHours(clampInteger(event.target.value, 0, 24))}
              />
            </label>
            <label>
              Minutter
              <input
                type="number"
                min="0"
                max="55"
                step="5"
                value={durationMinutes}
                onChange={(event) => setDurationMinutes(clampInteger(event.target.value, 0, 55))}
              />
            </label>
          </div>
        </div>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={capacityEnabled}
            onChange={(event) => setCapacityEnabled(event.target.checked)}
          />
          <span>Sett maks antall deltakere</span>
        </label>
        {capacityEnabled && (
          <label>
            Maks antall
            <input
              type="number"
              min={Math.max(1, plan.participant_count)}
              value={capacity}
              onChange={(event) => setCapacity(clampInteger(event.target.value, Math.max(1, plan.participant_count), 999))}
            />
          </label>
        )}
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={commentsLocked}
            onChange={(event) => setCommentsLocked(event.target.checked)}
          />
          <span>L&aring;s kommentarer</span>
        </label>
        {canOpenInvite && (
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={openInvite}
              onChange={(event) => setOpenInvite(event.target.checked)}
            />
            <span>&Aring;pen invitasjon &ndash; alle med lenken kan bli med</span>
          </label>
        )}
        <div className="modal-actions">
          <button className="secondary-action compact" disabled={busy} onClick={onClose}>Avbryt</button>
          <button
            className="primary-action compact"
            disabled={!canSave || busy}
            onClick={() => void onSave({
              title,
              description,
              startsAt,
              durationMinutes: totalDurationMinutes,
              capacityEnabled,
              capacity,
              commentsLocked,
              openInvite: canOpenInvite ? openInvite : false
            })}
          >
            {busy ? "Lagrer..." : "Lagre"}
          </button>
        </div>
      </section>
    </div>
  );
}

// The classic "this / this and following / all" chooser for recurring plans,
// rendered as a mobile bottom sheet.
function SeriesScopeSheet({
  mode,
  busy,
  onChoose,
  onClose
}: {
  mode: "edit" | "delete";
  busy: boolean;
  onChoose: (scope: "occurrence" | "following" | "all") => void;
  onClose: () => void;
}) {
  const verb = mode === "edit" ? "Endre" : "Avlys";
  const following =
    mode === "edit" ? "Denne og senere turer" : "Denne og senere turer";
  const scheduleNote =
    mode === "edit"
      ? "Tid og varighet oppdateres for kommende turer. Turer du har endret enkeltvis beholdes."
      : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section
        className="action-sheet"
        role="dialog"
        aria-label={mode === "edit" ? "Endre fast plan" : "Avlys fast plan"}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="action-sheet-title">
          <strong>{mode === "edit" ? "Endre fast plan" : "Avlys fast plan"}</strong>
          {scheduleNote && <span>{scheduleNote}</span>}
        </div>
        <button className="action-sheet-item" disabled={busy} onClick={() => onChoose("occurrence")}>
          {verb} bare denne turen
        </button>
        <button className="action-sheet-item" disabled={busy} onClick={() => onChoose("following")}>
          {verb} {following.toLowerCase()}
        </button>
        <button className="action-sheet-item" disabled={busy} onClick={() => onChoose("all")}>
          {verb} hele serien
        </button>
        <button className="action-sheet-item cancel" disabled={busy} onClick={onClose}>
          Avbryt
        </button>
      </section>
    </div>
  );
}

function InviteRoute({
  token,
  refresh,
  afterMeaningfulAction,
  onMessage
}: {
  token: string;
  refresh: () => Promise<void>;
  afterMeaningfulAction: (reason: string) => void;
  onMessage: (message: string) => void;
}) {
  const [invite, setInvite] = useState<NameSearchResult | null>(null);
  const [suggestions, setSuggestions] = useState<NameSearchResult[]>([]);

  useEffect(() => {
    async function loadInvite() {
      const client = requireSupabase();
      const { data, error } = await client.rpc("resolve_app_invite", { invite_token: token });
      if (error) throw error;
      setInvite(((data || []) as NameSearchResult[])[0] || null);

      const { data: suggestionData, error: suggestionError } = await client.rpc("suggest_invite_friends", {
        invite_token: token
      });
      if (suggestionError) throw suggestionError;
      setSuggestions((suggestionData || []) as NameSearchResult[]);
    }

    loadInvite().catch((error: unknown) => onMessage(errorMessage(error)));
  }, [onMessage, token]);

  async function sendRequest(userId: string) {
    try {
      const { error } = await requireSupabase().rpc("send_friend_request", {
        target_user_id: userId
      });
      if (error) throw error;
      afterMeaningfulAction("Få beskjed når venneforespørsler blir godtatt.");
      setInvite((current) =>
        current && current.id === userId ? { ...current, relationship_state: "outgoing_request" } : current
      );
      setSuggestions((current) =>
        current.map((person) => (person.id === userId ? { ...person, relationship_state: "outgoing_request" } : person))
      );
      await refresh();
      onMessage("Venneforespørsel sendt.");
    } catch (error) {
      onMessage(errorMessage(error));
    }
  }

  if (!invite) {
    return <EmptyState title="Invitasjonen er ikke tilgjengelig" text="Den kan være utløpt, trukket tilbake, eller krever at profilen din er ferdig." />;
  }

  return (
    <section className="center-panel">
      <Avatar name={invite.full_name} avatarPath={invite.avatar_path} size="large" />
      <h1>{invite.full_name} inviterte deg til Join</h1>
      <p>Du bestemmer selv om du vil sende en venneforespørsel.</p>
      <RelationshipAction state={invite.relationship_state} onClick={() => sendRequest(invite.id)} />
      {suggestions.length > 0 && (
        <section className="list-section">
          <h2>Kanskje du kjenner disse</h2>
          {suggestions.map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              action={<RelationshipAction state={person.relationship_state} onClick={() => sendRequest(person.id)} />}
            />
          ))}
        </section>
      )}
    </section>
  );
}

function activityIconFor(activityId: PlanActivityId | null): LucideIcon {
  const match = activityId
    ? activityOptions.find((activity) => activity.id === activityId)
    : undefined;
  return match?.icon ?? CalendarDays;
}

type PublicGjengInviteResult = Omit<PublicGjengInvitePreview, "activity_id" | "member_preview" | "viewer_state"> & {
  activity_id: string | null;
  member_preview: unknown;
  viewer_state: string;
};

function normalizePublicGjengInvite(row: PublicGjengInviteResult | null | undefined): PublicGjengInvitePreview | null {
  if (!row) return null;
  return {
    ...row,
    activity_id: normalizeActivityId(row.activity_id),
    member_preview: normalizeGjengMemberPreviews(row.member_preview),
    viewer_state: row.viewer_state === "member" ? "member" : "none"
  };
}

function PublicGjengInvitePage({
  token,
  navigate,
  onMessage
}: {
  token: string;
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
}) {
  const [preview, setPreview] = useState<PublicGjengInvitePreview | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [joining, setJoining] = useState(false);
  const [viewerHasAccount, setViewerHasAccount] = useState(false);
  const authPath = `/auth?next=/g/${encodeURIComponent(token)}`;

  const loadPreview = useCallback(async () => {
    const client = requireSupabase();
    const [{ data: sessionData }, { data, error }] = await Promise.all([
      client.auth.getSession(),
      client.rpc("resolve_gjeng_invite", { gjeng_token: token })
    ]);
    if (error) throw error;
    const row = normalizePublicGjengInvite(((data || []) as PublicGjengInviteResult[])[0]);
    setViewerHasAccount(Boolean(sessionData.session && sessionData.session.user.is_anonymous !== true));
    setPreview(row);
    setLoaded(true);
    return row;
  }, [token]);

  useEffect(() => {
    loadPreview().catch((error: unknown) => {
      setLoaded(true);
      onMessage(errorMessage(error));
    });
  }, [loadPreview, onMessage]);

  useEffect(() => {
    if (preview?.viewer_state === "member" && viewerHasAccount) {
      navigate(`/gjenger/${preview.id}`);
    }
  }, [navigate, preview, viewerHasAccount]);

  async function joinNow() {
    setJoining(true);
    try {
      const client = requireSupabase();
      const { data: sessionData } = await client.auth.getSession();
      if (!sessionData.session || sessionData.session.user.is_anonymous === true) {
        navigate(authPath);
        return;
      }
      const { error } = await client.rpc("join_gjeng_via_invite", {
        gjeng_token: token,
        guest_name: ""
      });
      if (error) throw error;
      const row = await loadPreview();
      if (row?.viewer_state === "member") {
        navigate(`/gjenger/${row.id}`);
        return;
      }
      onMessage("Du er med i gjengen.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setJoining(false);
    }
  }

  function handleJoinClick() {
    void joinNow();
  }

  if (!loaded) return <LoadingScreen label="Henter gjengen..." />;

  if (!preview) {
    return (
      <section className="center-panel">
        <UsersRound size={34} />
        <h1>Gjenglenken virker ikke</h1>
        <p>Lenken kan være utløpt eller trukket tilbake. Be noen i gjengen sende en ny.</p>
        <button className="primary-action compact" onClick={() => navigate("/auth")}>
          Logg inn på Join
        </button>
      </section>
    );
  }

  if (preview.viewer_state === "member" && viewerHasAccount) {
    return <LoadingScreen label="Åpner gjengen..." />;
  }

  const Icon = activityIconFor(preview.activity_id);
  const memberLabel = preview.member_count === 1 ? "1 i gjengen" : `${preview.member_count} i gjengen`;

  return (
    <section className="public-plan public-gjeng">
      <div className="public-plan-card">
        <div className="public-plan-brand">join</div>
        <div className="public-plan-hero">
          <span className="public-plan-activity-icon">
            <Icon size={26} />
          </span>
          <div>
            <span className="public-plan-host">Du er invitert til en gjeng</span>
            <h1>{preview.name}</h1>
          </div>
        </div>
        <div className="public-plan-meta">
          <span><UsersRound size={16} /> {memberLabel}</span>
          <span><CalendarDays size={16} /> Planer på ett sted</span>
        </div>
        <div className="public-gjeng-members">
          <GjengAvatarStack members={preview.member_preview} />
          <p>Alle i gjengen kan legge ut planer til gjengen og se det dere gjør sammen.</p>
        </div>

        {preview.viewer_state === "member" && !viewerHasAccount ? (
          <div className="public-plan-joined">
            <div className="guest-upgrade">
              <strong>Opprett konto for å fortsette</strong>
              <p>Gjenger krever en Join-konto, så alle vet hvem som er med.</p>
              <button className="primary-action compact" onClick={() => navigate(authPath)}>
                Opprett konto
              </button>
            </div>
          </div>
        ) : viewerHasAccount ? (
          <div className="public-plan-actions single">
            <button className="primary-action" disabled={joining} onClick={handleJoinClick}>
              <CheckCircle2 size={18} />
              {joining ? "Blir med..." : "Bli med i gjengen"}
            </button>
          </div>
        ) : (
          <div className="public-plan-actions single">
            <button className="primary-action" onClick={() => navigate(authPath)}>
              <CheckCircle2 size={18} />
              Opprett konto for å bli med
            </button>
            <button className="secondary-action compact" onClick={() => navigate(authPath)}>
              Logg inn
            </button>
            <p className="micro-copy">Du trenger konto for å bli med i en gjeng.</p>
          </div>
        )}
      </div>
    </section>
  );
}

// The public, link-scoped plan experience. Renders for everyone who opens a
// plan share link: logged-out visitors, guests with an anonymous session, and
// signed-in members who are not (yet) recipients. Members who can already see
// the plan are forwarded to the full plan detail.
function PublicPlanLinkPage({
  token,
  navigate,
  onMessage
}: {
  token: string;
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
}) {
  const [preview, setPreview] = useState<PublicPlanPreview | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [joining, setJoining] = useState(false);
  const [showNameForm, setShowNameForm] = useState(false);
  const [name, setName] = useState("");

  const loadPreview = useCallback(async () => {
    const { data, error } = await requireSupabase().rpc("resolve_public_plan_preview", {
      plan_token: token
    });
    if (error) throw error;
    const row = ((data || []) as PublicPlanPreview[])[0] || null;
    setPreview(row);
    setLoaded(true);
    return row;
  }, [token]);

  useEffect(() => {
    loadPreview().catch((error: unknown) => {
      setLoaded(true);
      onMessage(errorMessage(error));
    });
  }, [loadPreview, onMessage]);

  // A member who can already fully see the plan goes straight to the real detail.
  useEffect(() => {
    if (preview?.viewer_can_fully_view) navigate(`/plans/${preview.plan_id}`);
  }, [navigate, preview?.viewer_can_fully_view, preview?.plan_id]);

  async function joinNow(guestName: string) {
    setJoining(true);
    try {
      const client = requireSupabase();
      const { data: sessionData } = await client.auth.getSession();
      if (!sessionData.session) {
        const { error: signInError } = await client.auth.signInAnonymously();
        if (signInError) throw signInError;
      }
      const { error } = await client.rpc("join_plan_via_link", {
        plan_token: token,
        guest_name: guestName
      });
      if (error) throw error;
      rememberGuestPlan(token);
      const row = await loadPreview();
      if (row?.viewer_can_fully_view) {
        navigate(`/plans/${row.plan_id}`);
        return;
      }
      onMessage("Du blir med! 🎉");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setJoining(false);
      setShowNameForm(false);
    }
  }

  function handleJoinClick() {
    void (async () => {
      const { data } = await requireSupabase().auth.getSession();
      if (data.session) void joinNow("");
      else setShowNameForm(true);
    })();
  }

  if (!loaded) return <LoadingScreen label="Henter plan..." />;

  if (!preview) {
    return (
      <section className="center-panel">
        <Link2 size={34} />
        <h1>Planlenken er ikke tilgjengelig</h1>
        <p>Lenken kan være utløpt eller trukket tilbake, eller så er planen privat.</p>
        <button className="primary-action compact" onClick={() => navigate("/auth")}>
          Logg inn på Join
        </button>
      </section>
    );
  }

  // The forwarding effect handles this; show a calm placeholder meanwhile.
  if (preview.viewer_can_fully_view) return <LoadingScreen label="Åpner plan..." />;

  const Icon = activityIconFor(preview.activity_id);
  const joined = preview.viewer_state === "joined" || preview.viewer_state === "confirmed";
  const cancelled = preview.status === "cancelled";
  const ended = new Date(preview.ends_at).getTime() <= Date.now();
  const full = preview.capacity != null && preview.participant_count >= preview.capacity;
  const goingLabel =
    preview.participant_count === 1 ? "1 blir med" : `${preview.participant_count} blir med`;
  const mapPlan = {
    place_name: preview.place_name,
    place_address: preview.place_address,
    public_latitude: preview.public_latitude,
    public_longitude: preview.public_longitude,
    location_visibility: "public_place" as const
  };

  return (
    <section className="public-plan">
      <div className="public-plan-card">
        <div className="public-plan-brand">join</div>
        <div className="public-plan-hero">
          <span className="public-plan-activity-icon">
            <Icon size={26} />
          </span>
          <div>
            <span className="public-plan-host">{preview.host_name} inviterer</span>
            <h1>{preview.title}</h1>
          </div>
        </div>
        <div className="public-plan-meta">
          <span><CalendarDays size={16} /> {formatPlanTime(preview.starts_at, preview.ends_at)}</span>
          <span><MapPin size={16} /> {preview.place_name}</span>
          <span><Users size={16} /> {goingLabel}</span>
        </div>
        {preview.description && <p className="public-plan-description">{preview.description}</p>}
        {planMapCoordinate(mapPlan) && <PlanMapPreview plan={mapPlan} variant="detail" />}

        {joined ? (
          <div className="public-plan-joined">
            <p className="public-plan-confirm"><CheckCircle2 size={20} /> Du blir med!</p>
            <div className="guest-upgrade">
              <strong>Få med deg resten</strong>
              <p>Opprett en gratis Join-konto for å få beskjed om endringer, se hvem som kommer, og lage dine egne planer.</p>
              <button className="primary-action compact" onClick={() => navigate("/auth")}>
                Opprett konto
              </button>
            </div>
          </div>
        ) : cancelled ? (
          <p className="public-plan-status muted">Denne planen er avlyst.</p>
        ) : ended ? (
          <p className="public-plan-status muted">Denne planen er allerede over.</p>
        ) : full ? (
          <p className="public-plan-status muted">Planen er full.</p>
        ) : showNameForm ? (
          <div className="public-plan-name-form">
            <label>
              Hva heter du?
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Fornavn og etternavn"
                autoFocus
              />
            </label>
            <p className="micro-copy">Vises for verten og de andre som blir med.</p>
            <div className="public-plan-actions">
              <button className="secondary-action compact" onClick={() => setShowNameForm(false)} disabled={joining}>
                Avbryt
              </button>
              <button
                className="primary-action compact"
                disabled={name.trim().length < 2 || joining}
                onClick={() => void joinNow(name.trim())}
              >
                {joining ? "Blir med..." : "Bli med"}
              </button>
            </div>
          </div>
        ) : (
          <div className="public-plan-actions single">
            <button className="primary-action" disabled={joining} onClick={handleJoinClick}>
              <CheckCircle2 size={18} />
              Bli med
            </button>
            <p className="micro-copy">Du trenger ikke konto for å bli med.</p>
          </div>
        )}
      </div>
    </section>
  );
}

// Where a guest lands if they reopen Join without a specific plan link.
function GuestLanding({ navigate }: { navigate: (path: string) => void }) {
  const guestToken = peekGuestPlan();
  return (
    <section className="center-panel">
      <span className="avatar-picker"><Users size={34} /></span>
      <h1>Du er med som gjest</h1>
      <p>Opprett en gratis konto for å lage egne planer, få varsler og legge til venner.</p>
      <button className="primary-action compact" onClick={() => navigate("/auth")}>
        Opprett konto
      </button>
      {guestToken && (
        <button className="text-button" onClick={() => navigate(`/p/${guestToken}`)}>
          Se planen du ble med på
        </button>
      )}
    </section>
  );
}

function NotificationsPage({
  notifications,
  refresh,
  navigate,
  onMessage
}: {
  notifications: NotificationItem[];
  refresh: () => Promise<void>;
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
}) {
  async function openNotification(item: NotificationItem) {
    if (!item.read_at) {
      try {
        const { error } = await requireSupabase().rpc("mark_notification_read", {
          target_notification_id: item.id
        });
        if (error) throw error;
        await refresh();
      } catch (error) {
        onMessage(errorMessage(error));
      }
    }
    const targetPath = notificationTargetPath(item);
    if (targetPath) navigate(targetPath);
  }

  return (
    <section className="stack-page">
      <PageTitle title="Varsler" />
      <div className="timeline-list">
        {notifications.map((item) => (
          <button
            key={item.id}
            className={`notification-row ${item.read_at ? "" : "unread"}`}
            onClick={() => openNotification(item)}
          >
            <span className="status-dot" />
            <div>
              <strong>{item.title}</strong>
              <span className="notification-time">{formatRelativeTime(item.created_at)}</span>
              <p>{item.body}</p>
            </div>
          </button>
        ))}
        {notifications.length === 0 && <EmptyState title="Ingen varsler" text="Oppdateringer om venner og planer vises her." />}
      </div>
    </section>
  );
}

export function notificationTargetPath(item: NotificationItem) {
  if (item.plan_id) return `/plans/${item.plan_id}`;
  // A gjeng invite lands on the gjenger list, where the accept/decline card
  // lives -- the invited person cannot open the gjeng detail before accepting.
  if (item.kind === "gjeng_invite") return "/gjenger";
  if (item.gjeng_id) return `/gjenger/${item.gjeng_id}`;
  if (item.kind === "friend_request" || item.kind === "friend_accepted") return "/friends";
  return null;
}

const PAST_PLANS_PAGE_SIZE = 20;

// Groups completed plans (already newest-first) into month buckets, preserving
// order. Returns [{ key, label, plans }] for sectioned rendering.
function groupPlansByMonth(plans: PlanSummary[]) {
  const groups: { key: string; label: string; plans: PlanSummary[] }[] = [];
  for (const plan of plans) {
    const key = monthBucketKey(plan.ends_at);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.plans.push(plan);
    } else {
      groups.push({ key, label: formatMonthLabel(plan.ends_at), plans: [plan] });
    }
  }
  return groups;
}

function PastPlansPage({
  navigate,
  onMessage
}: {
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
}) {
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadState("loading");
    loadPastPlans(null, PAST_PLANS_PAGE_SIZE)
      .then((rows) => {
        if (!active) return;
        setPlans(rows);
        setHasMore(rows.length === PAST_PLANS_PAGE_SIZE);
        setLoadState("ready");
      })
      .catch((error) => {
        if (!active) return;
        onMessage(errorMessage(error));
        setLoadState("error");
      });
    return () => {
      active = false;
    };
  }, [onMessage]);

  async function loadMore() {
    const last = plans[plans.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const rows = await loadPastPlans({ ends_at: last.ends_at, id: last.id }, PAST_PLANS_PAGE_SIZE);
      setPlans((current) => [...current, ...rows]);
      setHasMore(rows.length === PAST_PLANS_PAGE_SIZE);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setLoadingMore(false);
    }
  }

  const groups = groupPlansByMonth(plans);

  return (
    <section className="stack-page">
      <div className="profile-topbar">
        <button type="button" className="icon-only" onClick={() => window.history.back()} aria-label="Tilbake">
          <ArrowLeft size={18} />
        </button>
      </div>
      <PageTitle title="Nylig" />
      {loadState === "loading" && plans.length === 0 ? (
        <LoadingScreen label="Laster planer..." />
      ) : plans.length === 0 ? (
        <EmptyState
          title="Ingen tidligere planer ennå"
          text="Når en plan er over, havner den her – din egen historikk og det vennene dine har vært med på."
          action={
            <button className="primary-action compact" onClick={() => navigate("/plans/new")}>
              <PlusCircle size={17} />
              Lag en plan
            </button>
          }
        />
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.key} className="home-section">
              <SectionHeading title={group.label} />
              <div className="plan-list">
                {group.plans.map((plan) => (
                    <MemoryPlanRow
                      key={plan.id}
                      plan={plan}
                      onOpen={() => navigate(`/plans/${plan.id}`)}
                      onMessage={onMessage}
                    />
                ))}
              </div>
            </section>
          ))}
          {hasMore && (
            <button className="secondary-action compact load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Laster..." : "Last inn flere"}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function ProfilePage({
  profile,
  onMessage,
  onProfileUpdated,
  onSignedOut
}: {
  profile: Profile;
  onMessage: (message: string) => void;
  onProfileUpdated: (profile: Profile) => void;
  onSignedOut: () => void;
}) {
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [showWhyJoin, setShowWhyJoin] = useState(false);

  async function signOut() {
    clearStravaDrafts();
    await requireSupabase().auth.signOut();
    onSignedOut();
  }

  async function updateAvatar(file: File | null) {
    if (!file) return;
    setAvatarBusy(true);
    try {
      const avatarPath = await uploadAvatarFile(profile.id, file);
      const { data, error } = await requireSupabase()
        .from("profiles")
        .update({ avatar_path: avatarPath })
        .eq("id", profile.id)
        .select(PROFILE_COLUMNS)
        .maybeSingle();
      if (error) throw error;
      onProfileUpdated(normalizeProfile(data) ?? { ...profile, avatar_path: avatarPath });
      onMessage("Profilbildet er oppdatert.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setAvatarBusy(false);
    }
  }

  function deferSetting() {
    onMessage("Denne innstillingen kommer etter første PWA-validering.");
  }

  return (
    <section className="profile-page">
      <label className="profile-avatar-action">
        <input
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif"
          disabled={avatarBusy}
          onChange={(event) => {
            void updateAvatar(event.target.files?.[0] ?? null);
            event.currentTarget.value = "";
          }}
        />
        <Avatar name={profile.full_name} avatarPath={profile.avatar_path} size="large" />
        <span>{avatarBusy ? "Laster opp..." : "Endre bilde"}</span>
      </label>
      <h1>{profile.full_name}</h1>
      <p>@{profile.invite_code.toLowerCase()}</p>
      <StravaSettings key={profile.id} />
      <div className="settings-list">
        <SettingsRow
          label="Hvorfor join"
          expanded={showWhyJoin}
          onClick={() => setShowWhyJoin((current) => !current)}
        />
        {showWhyJoin && (
          <section className="profile-motivation">
            <p>
              Join er bygget fordi mange av oss vil se vennene våre oftere, men små
              planer blir fort for tunge når de må organiseres i meldinger. Appen
              skal gjøre det lett å dele det du uansett skal gjøre, med lav terskel
              for å bli med og uten press når det ikke passer.
            </p>
          </section>
        )}
        <SettingsRow label="Rediger profil" onClick={deferSetting} />
        <SettingsRow label="Kontoinnstillinger" onClick={deferSetting} />
        <SettingsRow label="Personvern" onClick={deferSetting} />
        <SettingsRow label="Hjelp og støtte" onClick={deferSetting} />
        <button className="danger-row" onClick={signOut}>
          <LogOut size={17} />
          Logg ut
        </button>
      </div>
      <span className="version">Join 1.0.0</span>
    </section>
  );
}

function ProfileFriendList({
  title,
  people,
  emptyText,
  onOpenProfile,
  renderAction
}: {
  title: string;
  people: NameSearchResult[];
  emptyText: string;
  onOpenProfile: (userId: string) => void;
  renderAction?: (person: NameSearchResult) => React.ReactNode;
}) {
  return (
    <section className="list-section profile-friends">
      <h2>{title}</h2>
      {people.length === 0 ? (
        <p className="muted">{emptyText}</p>
      ) : (
        people.map((person) => (
          <PersonRow
            key={person.id}
            person={person}
            onOpen={() => onOpenProfile(person.id)}
            action={renderAction ? renderAction(person) : undefined}
          />
        ))
      )}
    </section>
  );
}

function ProfileRelationshipAction({
  state,
  requestId,
  busy,
  onSend,
  onAccept
}: {
  state: RelationshipState;
  requestId: string | null;
  busy?: boolean;
  onSend: () => void;
  onAccept: (requestId: string) => void;
}) {
  if (state === "none") {
    return (
      <button className="secondary-action compact" disabled={busy} onClick={onSend}>
        {busy ? "..." : "Legg til"}
      </button>
    );
  }
  if (state === "incoming_request" && requestId) {
    return (
      <button className="secondary-action compact" disabled={busy} onClick={() => onAccept(requestId)}>
        {busy ? "..." : "Godta"}
      </button>
    );
  }
  return <span className="state-chip">{relationshipLabel(state)}</span>;
}

function UserProfilePage({
  userId,
  navigate,
  refresh,
  afterMeaningfulAction,
  onMessage
}: {
  userId: string;
  navigate: (path: string) => void;
  refresh: () => Promise<void>;
  afterMeaningfulAction: (reason: string) => void;
  onMessage: (message: string) => void;
}) {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [friends, setFriends] = useState<NameSearchResult[]>([]);
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const client = requireSupabase();
      const [profileResult, friendResult, planRows] = await Promise.all([
        client.rpc("get_public_profile", { target_user_id: userId }),
        client.rpc("list_profile_friends", { target_user_id: userId }),
        loadProfilePlans(userId)
      ]);
      if (profileResult.error) throw profileResult.error;
      if (friendResult.error) throw friendResult.error;
      const profileRow = (profileResult.data as PublicProfile[] | null)?.[0] ?? null;
      setProfile(profileRow);
      setFriends((friendResult.data as NameSearchResult[] | null) ?? []);
      setPlans(planRows);
      setLoadState(profileRow ? "ready" : "error");
    } catch (error) {
      onMessage(errorMessage(error));
      setLoadState("error");
    }
  }, [userId, onMessage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function sendRequest(targetId: string) {
    setActionBusyId(targetId);
    try {
      const { error } = await requireSupabase().rpc("send_friend_request", { target_user_id: targetId });
      if (error) throw error;
      onMessage("Venneforespørsel sendt.");
      await Promise.all([load(), refresh()]);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setActionBusyId(null);
    }
  }

  async function acceptRequest(requestId: string, targetId: string) {
    setActionBusyId(targetId);
    try {
      const { error } = await requireSupabase().rpc("accept_friend_request", { target_request_id: requestId });
      if (error) throw error;
      onMessage("Dere er venner på Join.");
      afterMeaningfulAction("Få beskjed når venner inviterer deg til planer.");
      await Promise.all([load(), refresh()]);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setActionBusyId(null);
    }
  }

  if (loadState === "loading" && !profile) {
    return <LoadingScreen label="Laster profil..." />;
  }

  if (!profile) {
    return (
      <section className="profile-page">
        <div className="profile-topbar">
          <button type="button" className="icon-only" onClick={() => window.history.back()} aria-label="Tilbake">
            <ArrowLeft size={18} />
          </button>
        </div>
        <EmptyState
          title="Profilen er ikke tilgjengelig"
          text="Den kan være privat, blokkert eller utenfor delingen din."
        />
      </section>
    );
  }

  return (
    <section className="profile-page">
      <div className="profile-topbar">
        <button type="button" className="icon-only" onClick={() => window.history.back()} aria-label="Tilbake">
          <ArrowLeft size={18} />
        </button>
      </div>
      <Avatar name={profile.full_name} avatarPath={profile.avatar_path} size="large" />
      <h1>{profile.full_name}</h1>
      <p>@{profile.invite_code.toLowerCase()}</p>
      {profile.relationship_state !== "self" && (
        <div className="profile-primary-action">
          <ProfileRelationshipAction
            state={profile.relationship_state}
            requestId={profile.request_id}
            busy={actionBusyId === profile.id}
            onSend={() => sendRequest(profile.id)}
            onAccept={(requestId) => acceptRequest(requestId, profile.id)}
          />
        </div>
      )}
      <ProfilePlanHistory plans={plans} navigate={navigate} onMessage={onMessage} />
      <ProfileFriendList
        title={`Venner · ${profile.friend_count}`}
        people={friends}
        emptyText="Ingen venner å vise ennå."
        onOpenProfile={(id) => navigate(`/u/${id}`)}
        renderAction={(person) => (
          <ProfileRelationshipAction
            state={person.relationship_state}
            requestId={person.request_id ?? null}
            busy={actionBusyId === person.id}
            onSend={() => sendRequest(person.id)}
            onAccept={(requestId) => acceptRequest(requestId, person.id)}
          />
        )}
      />
    </section>
  );
}

// A friend's plans the viewer is allowed to see (from list_profile_plans),
// split into what's coming up and what's already happened.
function ProfilePlanHistory({
  plans,
  navigate,
  onMessage
}: {
  plans: PlanSummary[];
  navigate: (path: string) => void;
  onMessage: (message: string) => void;
}) {
  const now = Date.now();
  const upcoming = plans
    .filter((plan) => plan.status === "scheduled" && new Date(plan.ends_at).getTime() > now)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  const past = plans.filter(
    (plan) => plan.status !== "cancelled" && new Date(plan.ends_at).getTime() <= now
  );

  if (upcoming.length === 0 && past.length === 0) return null;

  return (
    <>
      {upcoming.length > 0 && (
        <section className="list-section">
          <SectionHeading title="Planer fremover" />
          <div className="plan-list">
            {upcoming.map((plan) => (
              <PlanRow key={plan.id} plan={plan} onOpen={() => navigate(`/plans/${plan.id}`)} onMessage={onMessage} />
            ))}
          </div>
        </section>
      )}
      {past.length > 0 && (
        <section className="list-section">
          <SectionHeading title="Tidligere planer" />
          <div className="plan-list">
            {past.map((plan) => (
              <MemoryPlanRow key={plan.id} plan={plan} onOpen={() => navigate(`/plans/${plan.id}`)} onMessage={onMessage} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function PushPrompt({
  reason,
  onClose,
  onEnabled
}: {
  reason: string;
  onClose: () => void;
  onEnabled: (message: string, registered: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function enable() {
    setBusy(true);
    try {
      await requestAndRegisterWebPush();
      onEnabled("Varsler er slått på for denne nettleseren.", true);
    } catch (error) {
      onEnabled(errorMessage(error), false);
    }
  }

  return (
    <div className="modal-backdrop">
      <section className="modal">
        <Bell size={26} />
        <h2>Slå på varsler?</h2>
        <p>{reason}</p>
        <div className="modal-actions">
          <button className="secondary-action compact" onClick={onClose}>Ikke nå</button>
          <button className="primary-action compact" disabled={busy} onClick={enable}>Slå på</button>
        </div>
      </section>
    </div>
  );
}

function PlanEngagementBar({
  plan,
  onOpenComments,
  onShare,
  compact = false,
  commentsInline = false,
  onMessage
}: {
  plan: Pick<
    PlanSummary,
    | "id"
    | "host_name"
    | "title"
    | "starts_at"
    | "ends_at"
    | "place_name"
    | "allow_guest_join"
    | "comment_count"
    | "reaction_summary"
    | "viewer_reaction"
  >;
  onOpenComments: () => void;
  onShare?: () => Promise<void>;
  compact?: boolean;
  commentsInline?: boolean;
  onMessage: (message: string) => void;
}) {
  const commentCount = Math.max(0, plan.comment_count);
  const [localSummary, setLocalSummary] = useState<ReactionSummaryItem[]>(plan.reaction_summary);
  const [localViewerReaction, setLocalViewerReaction] = useState<ReactionKey | null>(plan.viewer_reaction);
  const [busyReaction, setBusyReaction] = useState<ReactionKey | null>(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const total = reactionTotal(localSummary);

  useEffect(() => {
    setLocalSummary(plan.reaction_summary);
    setLocalViewerReaction(plan.viewer_reaction);
    setPickerOpen(false);
    setDetailsOpen(false);
  }, [plan.id, plan.reaction_summary, plan.viewer_reaction]);

  async function react(reactionKey: ReactionKey) {
    if (busyReaction) return;
    setBusyReaction(reactionKey);
    try {
      const next = await toggleTargetReaction("plan", plan.id, reactionKey);
      setLocalSummary(next.summary);
      setLocalViewerReaction(next.viewerReaction);
      setPickerOpen(false);
      if (next.summary.length === 0) setDetailsOpen(false);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusyReaction(null);
    }
  }

  async function reactWithMessage(reactionKey: ReactionKey, message: string) {
    if (busyReaction) return;
    setBusyReaction(reactionKey);
    try {
      const next = await setTargetReaction("plan", plan.id, reactionKey, message);
      setLocalSummary(next.summary);
      setLocalViewerReaction(next.viewerReaction);
      setPickerOpen(false);
      if (next.summary.length === 0) setDetailsOpen(false);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusyReaction(null);
    }
  }

  async function share() {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      if (onShare) {
        await onShare();
        return;
      }

      const { data, error } = await requireSupabase().rpc("create_plan_link", {
        target_plan_id: plan.id
      });
      if (error) throw error;
      const token = (data as Array<{ plan_token: string }>)[0]?.plan_token;
      if (!token) throw new Error("Planlenken ble opprettet uten token.");
      const result = await shareOrCopy(
        plan.allow_guest_join
          ? {
              title: `${plan.host_name} inviterer: ${plan.title}`,
              text: `Bli med på «${plan.title}» ${formatPlanTime(plan.starts_at, plan.ends_at)} · ${plan.place_name}.`,
              url: planUrl(token)
            }
          : {
              title: "En privat plan på Join",
              text: "Åpne Join for å se om planen er delt med deg.",
              url: planUrl(token)
            }
      );
      onMessage(result === "copied" ? "Planlenken er kopiert." : "Planen er delt.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      onMessage(errorMessage(error));
    } finally {
      setShareBusy(false);
    }
  }

  return (
    <div
      className={`plan-engagement-bar ${compact ? "compact" : ""} ${commentsInline ? "comments-inline" : ""}`}
      onClick={(event) => event.stopPropagation()}
    >
      {(total > 0 || commentCount > 0) && (
        <div className="engagement-summary-row">
          {total > 0 ? (
            <button
              type="button"
              className="engagement-summary-button"
              onClick={() => {
                setPickerOpen(false);
                setDetailsOpen((open) => !open);
              }}
              aria-expanded={detailsOpen}
              aria-label={`Se ${total} reaksjoner`}
            >
              <span className="reaction-summary-emojis" aria-hidden="true">
                {localSummary.slice(0, 3).map((item) => (
                  <span key={item.reaction_key}>{reactionEmoji(item.reaction_key)}</span>
                ))}
              </span>
              <span>{total}</span>
            </button>
          ) : (
            <span />
          )}
          {commentCount > 0 && (
            <button
              type="button"
              className="engagement-summary-button engagement-comment-summary"
              onClick={onOpenComments}
              aria-label={`${commentCount} kommentarer`}
            >
              <MessageCircle size={compact ? 15 : 16} />
              <span>{commentCount}</span>
            </button>
          )}
        </div>
      )}

      <div className="engagement-actions">
        <div className="engagement-reaction-action">
          <button
            type="button"
            className={`engagement-action ${localViewerReaction ? "selected" : ""}`}
            onClick={() => {
              setDetailsOpen(false);
              setPickerOpen((open) => !open);
            }}
            aria-expanded={pickerOpen}
            aria-label={localViewerReaction ? `Endre reaksjon ${reactionEmoji(localViewerReaction)}` : "Reager"}
          >
            {localViewerReaction ? (
              <span className="engagement-action-emoji" aria-hidden="true">
                {reactionEmoji(localViewerReaction)}
              </span>
            ) : (
              <Heart size={compact ? 18 : 20} />
            )}
            <span>{localViewerReaction ? "Reagert" : "Reager"}</span>
          </button>
          {pickerOpen && (
            <ReactionPicker
              selectedReaction={localViewerReaction}
              summary={localSummary}
              busyReaction={busyReaction}
              onQuickReaction={react}
              onMessageReaction={reactWithMessage}
            />
          )}
        </div>

        {!commentsInline && (
          <button type="button" className="engagement-action" onClick={onOpenComments}>
            <MessageCircle size={compact ? 18 : 20} />
            <span>Kommenter</span>
          </button>
        )}

        <button type="button" className="engagement-action" disabled={shareBusy} onClick={() => void share()}>
          <Share2 size={compact ? 18 : 20} />
          <span>Del</span>
        </button>
      </div>

      {detailsOpen && total > 0 && (
        <ReactionDetailsCard summary={localSummary} onClose={() => setDetailsOpen(false)} />
      )}
    </div>
  );
}

const REACTION_MESSAGE_MAX_LENGTH = 280;
const REACTION_MESSAGE_DRAG_THRESHOLD = 28;
const REACTION_MESSAGE_DRAG_MAX = 44;

type ReactionDragState = {
  key: ReactionKey;
  pointerId: number;
  startY: number;
  distance: number;
};

function ReactionPicker({
  selectedReaction,
  summary,
  busyReaction,
  onQuickReaction,
  onMessageReaction
}: {
  selectedReaction: ReactionKey | null;
  summary: ReactionSummaryItem[];
  busyReaction: ReactionKey | null;
  onQuickReaction: (reactionKey: ReactionKey) => Promise<void>;
  onMessageReaction: (reactionKey: ReactionKey, message: string) => Promise<void>;
}) {
  const [composerReaction, setComposerReaction] = useState<ReactionKey | null>(null);
  const [message, setMessage] = useState("");
  const [drag, setDrag] = useState<ReactionDragState | null>(null);
  const dragRef = useRef<ReactionDragState | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!composerReaction) return;
    messageInputRef.current?.focus();
    messageInputRef.current?.setSelectionRange(message.length, message.length);
  }, [composerReaction]);

  function openComposer(reactionKey: ReactionKey) {
    const currentMessage = selectedReaction === reactionKey
      ? summary.find((item) => item.reaction_key === reactionKey)?.viewer_message ?? ""
      : "";
    setMessage(currentMessage);
    setComposerReaction(reactionKey);
    dragRef.current = null;
    setDrag(null);
  }

  function handlePointerDown(event: React.PointerEvent<HTMLButtonElement>, reactionKey: ReactionKey) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    const nextDrag = {
      key: reactionKey,
      pointerId: event.pointerId,
      startY: event.clientY,
      distance: 0
    };
    dragRef.current = nextDrag;
    setDrag(nextDrag);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLButtonElement>) {
    const currentDrag = dragRef.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const distance = Math.min(
      REACTION_MESSAGE_DRAG_MAX,
      Math.max(0, currentDrag.startY - event.clientY)
    );
    const nextDrag = { ...currentDrag, distance };
    dragRef.current = nextDrag;
    setDrag(nextDrag);
  }

  function handlePointerUp(event: React.PointerEvent<HTMLButtonElement>, reactionKey: ReactionKey) {
    const currentDrag = dragRef.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const distance = Math.max(
      currentDrag.distance,
      Math.max(0, currentDrag.startY - event.clientY)
    );
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setDrag(null);
    if (distance >= REACTION_MESSAGE_DRAG_THRESHOLD) {
      openComposer(reactionKey);
      return;
    }
    void onQuickReaction(reactionKey);
  }

  function handlePointerCancel(
    event: React.PointerEvent<HTMLButtonElement>,
    reactionKey: ReactionKey
  ) {
    const currentDrag = dragRef.current;
    if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
    const shouldOpenComposer = currentDrag.distance >= REACTION_MESSAGE_DRAG_THRESHOLD;
    dragRef.current = null;
    setDrag(null);
    if (shouldOpenComposer) openComposer(reactionKey);
  }

  function handleReactionKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, reactionKey: ReactionKey) {
    if (event.repeat || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    void onQuickReaction(reactionKey);
  }

  async function submitMessage(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!composerReaction || busyReaction) return;
    await onMessageReaction(composerReaction, message);
  }

  if (composerReaction) {
    return (
      <form className="reaction-picker reaction-composer" onSubmit={(event) => void submitMessage(event)}>
        <div className="reaction-composer-header">
          <span className="reaction-composer-emoji" aria-hidden="true">
            {reactionEmoji(composerReaction)}
          </span>
          <strong>Legg til melding</strong>
          <button
            type="button"
            className="reaction-composer-close"
            onClick={() => setComposerReaction(null)}
            aria-label="Tilbake til reaksjoner"
          >
            <X size={15} />
          </button>
        </div>
        <textarea
          ref={messageInputRef}
          value={message}
          maxLength={REACTION_MESSAGE_MAX_LENGTH}
          rows={2}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setComposerReaction(null);
          }}
          placeholder="Skriv en melding (valgfritt)"
          aria-label="Valgfri melding til reaksjonen"
        />
        <div className="reaction-composer-footer">
          <span className="reaction-message-visibility">
            <Eye size={15} aria-hidden="true" />
            Synlig for alle som kan se planen
          </span>
          <span className={message.length >= 240 ? "reaction-message-count visible" : "reaction-message-count"}>
            {message.length}/{REACTION_MESSAGE_MAX_LENGTH}
          </span>
          <button type="submit" className="reaction-message-send" disabled={busyReaction !== null}>
            {busyReaction ? "Sender…" : "Send"}
          </button>
        </div>
      </form>
    );
  }

  const showDragHint = Boolean(drag && drag.distance >= REACTION_MESSAGE_DRAG_THRESHOLD);

  return (
    <div className="reaction-picker reaction-picker-choice-panel" role="group" aria-label="Velg reaksjon">
      <span className={showDragHint ? "reaction-drag-hint visible" : "reaction-drag-hint"}>
        {showDragHint ? "Slipp for melding" : "Dra opp for melding"}
      </span>
      <div className="reaction-message-targets" aria-label="Legg til melding">
        {reactionOptions.map((option) => {
          const isDragTarget = drag?.key === option.key && drag.distance >= REACTION_MESSAGE_DRAG_THRESHOLD;
          const hasViewerMessage = selectedReaction === option.key
            && Boolean(summary.find((item) => item.reaction_key === option.key)?.viewer_message);
          return (
            <button
              key={`message-${option.key}`}
              type="button"
              className={`${isDragTarget ? "drag-target " : ""}${hasViewerMessage ? "has-message" : ""}`.trim()}
              disabled={busyReaction !== null}
              onClick={() => openComposer(option.key)}
              aria-label={`Reager med ${option.emoji} og legg til melding`}
              title="Legg til melding"
            >
              <MessageCircle size={18} strokeWidth={1.9} aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <div className="reaction-emoji-options">
        {reactionOptions.map((option) => {
          const lift = drag?.key === option.key ? drag.distance : 0;
          const selected = selectedReaction === option.key;
          return (
            <button
              key={option.key}
              type="button"
              className={`${selected ? "selected " : ""}${lift >= REACTION_MESSAGE_DRAG_THRESHOLD ? "message-intent" : ""}`.trim()}
              disabled={busyReaction !== null}
              onPointerDown={(event) => handlePointerDown(event, option.key)}
              onPointerMove={handlePointerMove}
              onPointerUp={(event) => handlePointerUp(event, option.key)}
              onPointerCancel={(event) => handlePointerCancel(event, option.key)}
              onKeyDown={(event) => handleReactionKeyDown(event, option.key)}
              aria-label={selected ? `Fjern reaksjon ${option.emoji}` : `Reager med ${option.emoji}`}
              title={selected ? "Fjern reaksjon" : "Reager"}
            >
              <span
                className="reaction-picker-emoji-glyph"
                style={{ transform: `translateY(-${lift}px)` }}
                aria-hidden="true"
              >
                {option.emoji}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReactionBar({
  target,
  targetId,
  summary,
  viewerReaction,
  compact = false,
  onMessage
}: {
  target: ReactionTarget;
  targetId: string;
  summary: ReactionSummaryItem[];
  viewerReaction: ReactionKey | null;
  compact?: boolean;
  onMessage: (message: string) => void;
}) {
  const [localSummary, setLocalSummary] = useState<ReactionSummaryItem[]>(summary);
  const [localViewerReaction, setLocalViewerReaction] = useState<ReactionKey | null>(viewerReaction);
  const [busyReaction, setBusyReaction] = useState<ReactionKey | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const total = reactionTotal(localSummary);

  useEffect(() => {
    setLocalSummary(summary);
    setLocalViewerReaction(viewerReaction);
    setDetailsOpen(false);
  }, [summary, targetId, viewerReaction]);

  async function react(reactionKey: ReactionKey) {
    if (busyReaction) return;
    setBusyReaction(reactionKey);
    try {
      const next = await toggleTargetReaction(target, targetId, reactionKey);
      setLocalSummary(next.summary);
      setLocalViewerReaction(next.viewerReaction);
      if (next.summary.length === 0) setDetailsOpen(false);
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusyReaction(null);
    }
  }

  return (
    <div className={`reaction-surface ${compact ? "compact" : ""}`} onClick={(event) => event.stopPropagation()}>
      <div className="reaction-options" role="group" aria-label="Reaksjoner">
        {reactionOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            className={localViewerReaction === option.key ? "selected" : ""}
            disabled={busyReaction !== null}
            onClick={() => void react(option.key)}
            aria-label={localViewerReaction === option.key ? `Fjern reaksjon ${option.emoji}` : `Reager med ${option.emoji}`}
            title={localViewerReaction === option.key ? "Fjern reaksjon" : "Reager"}
          >
            <span>{option.emoji}</span>
          </button>
        ))}
      </div>
      {total > 0 && (
        <button
          type="button"
          className="reaction-summary-button"
          onClick={() => setDetailsOpen((open) => !open)}
          aria-expanded={detailsOpen}
          aria-label={`Se ${total} reaksjoner`}
        >
          <span className="reaction-summary-emojis">
            {localSummary.slice(0, 3).map((item) => (
              <span key={item.reaction_key}>{reactionEmoji(item.reaction_key)}</span>
            ))}
          </span>
          <span>{total}</span>
        </button>
      )}
      {detailsOpen && total > 0 && (
        <ReactionDetailsCard summary={localSummary} onClose={() => setDetailsOpen(false)} />
      )}
    </div>
  );
}

function ReactionDetailsCard({
  summary,
  onClose
}: {
  summary: ReactionSummaryItem[];
  onClose: () => void;
}) {
  const total = reactionTotal(summary);

  return (
    <div className="reaction-details-card" role="dialog" aria-label="Reaksjoner">
      <div className="reaction-details-header">
        <div>
          <strong>Reaksjoner</strong>
          <span>{total} totalt</span>
        </div>
        <button type="button" className="reaction-details-close" onClick={onClose} aria-label="Lukk reaksjoner">
          <X size={14} />
        </button>
      </div>
      <div className="reaction-details-list">
        {summary.map((item) => {
          const hiddenCount = Math.max(0, item.count - item.reactors.length);
          return (
            <section key={item.reaction_key} className="reaction-detail-group">
              <header>
                <span>{reactionEmoji(item.reaction_key)}</span>
                <small>{item.count}</small>
              </header>
              <div>
                {item.reactors.map((actor) => (
                  <div key={`${item.reaction_key}-${actor.id}`} className="reaction-actor-row">
                    <Avatar name={actor.full_name} avatarPath={actor.avatar_path} />
                    <div className="reaction-actor-copy">
                      <span>{actor.full_name}</span>
                      {actor.message ? <p>{actor.message}</p> : null}
                    </div>
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <p className="muted reaction-hidden-count">
                    +{hiddenCount} til
                  </p>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function reactionTotal(summary: ReactionSummaryItem[]): number {
  return summary.reduce((total, item) => total + item.count, 0);
}

function reactionEmoji(key: ReactionKey): string {
  return reactionOptionByKey.get(key)?.emoji ?? ".";
}

function PlanRow({
  plan,
  onOpen,
  onMessage
}: {
  plan: PlanSummary;
  onOpen: () => void;
  onMessage: (message: string) => void;
}) {
  const participationText = planParticipationText(plan);
  const activity = activityForPlan(plan.activity_id);
  const Icon = activity.icon;
  const participantText = participantSummaryText(plan);

  return (
    <article className="plan-row">
      <button type="button" className="plan-row-open" onClick={onOpen}>
      <div className="plan-row-time">
        <span>{formatPlanTime(plan.starts_at)}</span>
      </div>
      <span className="plan-activity-icon compact" aria-hidden="true">
        <Icon size={18} />
      </span>
      <div className="plan-row-copy">
        <strong>{plan.title}</strong>
        <small>
          {plan.host_name} inviterer · {plan.gjeng_name ? `${plan.gjeng_name} · ` : ""}{plan.place_name}
        </small>
      </div>
      <div className="plan-row-meta">
        <span className="participant-summary compact">
          <AvatarCluster participants={plan.participant_preview} count={plan.participant_count} />
          <span>{participantText}</span>
        </span>
        <span className="join-pill">{participationText}</span>
      </div>
      </button>
      <PlanEngagementBar plan={plan} compact onOpenComments={onOpen} onMessage={onMessage} />
    </article>
  );
}

function PlanTimeSuggestionControl({
  plan,
  currentUserId,
  currentUserIsGuest,
  variant = "card",
  onPlanChanged,
  onMessage
}: {
  plan: PlanSummary | PlanDetail;
  currentUserId: string;
  currentUserIsGuest: boolean;
  variant?: "card" | "hero";
  onPlanChanged: () => Promise<void>;
  onMessage: (message: string) => void;
}) {
  const viewerIsHost = plan.host_id === currentUserId;
  const isUpcoming = plan.status === "scheduled" && new Date(plan.ends_at).getTime() > Date.now();
  const [suggestions, setSuggestions] = useState<PlanTimeSuggestion[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSuggestions = useCallback(async () => {
    if (!isUpcoming || currentUserIsGuest) {
      setSuggestions([]);
      return;
    }
    const { data, error } = await requireSupabase().rpc("list_plan_time_suggestions", {
      target_plan_id: plan.id
    });
    if (error) throw error;
    setSuggestions((Array.isArray(data) ? data : []) as PlanTimeSuggestion[]);
  }, [currentUserIsGuest, isUpcoming, plan.id]);

  useEffect(() => {
    loadSuggestions().catch((error) => onMessage(errorMessage(error)));
  }, [loadSuggestions, onMessage]);

  if (!isUpcoming || currentUserIsGuest) return null;
  if (viewerIsHost && (!suggestions || suggestions.length === 0)) return null;

  const hasPendingSuggestion = !viewerIsHost && Boolean(suggestions?.length);
  const label = viewerIsHost
    ? `${suggestions?.length ?? 0} foreslåtte ${suggestions?.length === 1 ? "tid" : "tider"}`
    : hasPendingSuggestion
      ? "Endre foreslått tid"
      : "Foreslå ny tid";

  async function submit(proposedStartsAt: string) {
    setBusy(true);
    try {
      const { error } = await requireSupabase().rpc("suggest_plan_time", {
        target_plan_id: plan.id,
        proposed_starts_at: proposedStartsAt
      });
      if (error) throw error;
      await loadSuggestions();
      await onPlanChanged();
      setOpen(false);
      onMessage(hasPendingSuggestion ? "Tidsforslaget er oppdatert." : "Tidsforslaget er sendt.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function respond(suggestionId: string, acceptSuggestion: boolean) {
    setBusy(true);
    try {
      const { error } = await requireSupabase().rpc("respond_to_plan_time_suggestion", {
        target_suggestion_id: suggestionId,
        accept_suggestion: acceptSuggestion
      });
      if (error) throw error;
      await loadSuggestions();
      await onPlanChanged();
      if (acceptSuggestion) setOpen(false);
      onMessage(acceptSuggestion ? "Planen har fått ny tid." : "Forslaget er fjernet.");
    } catch (error) {
      onMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`time-suggestion-trigger ${variant} ${hasPendingSuggestion ? "active" : ""}`}
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <CalendarClock size={17} />
      </button>
      {open && suggestions && (
        <PlanTimeSuggestionSheet
          plan={plan}
          viewerIsHost={viewerIsHost}
          suggestions={suggestions}
          busy={busy}
          onClose={() => setOpen(false)}
          onSubmit={submit}
          onRespond={respond}
        />
      )}
    </>
  );
}

function PlanTimeSuggestionSheet({
  plan,
  viewerIsHost,
  suggestions,
  busy,
  onClose,
  onSubmit,
  onRespond
}: {
  plan: PlanSummary | PlanDetail;
  viewerIsHost: boolean;
  suggestions: PlanTimeSuggestion[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (proposedStartsAt: string) => Promise<void>;
  onRespond: (suggestionId: string, acceptSuggestion: boolean) => Promise<void>;
}) {
  const options = useMemo(() => buildTimeSuggestionOptions(plan.starts_at), [plan.starts_at]);
  const ownSuggestion = !viewerIsHost ? suggestions[0] : null;
  const initialQuickOption = options.find((option) =>
    ownSuggestion
      ? Math.abs(new Date(option.value).getTime() - new Date(ownSuggestion.proposed_starts_at).getTime()) < 60_000
      : option.id === "tomorrow"
  );
  const [selection, setSelection] = useState<"tomorrow" | "next-week" | "custom">(
    initialQuickOption?.id ?? (ownSuggestion ? "custom" : "tomorrow")
  );
  const [customValue, setCustomValue] = useState(
    ownSuggestion ? toLocalDateTimeValue(new Date(ownSuggestion.proposed_starts_at)) : options[0].value
  );
  const selectedValue = selection === "custom"
    ? customValue
    : options.find((option) => option.id === selection)?.value ?? "";
  const validationMessage = validateSuggestedTime(selectedValue, plan.starts_at);
  const minimumValue = toLocalDateTimeValue(new Date(Date.now() + 15 * 60_000));

  return (
    <div className="modal-backdrop time-suggestion-backdrop" onClick={onClose}>
      <section
        className="time-suggestion-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="time-suggestion-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="time-suggestion-header">
          <div>
            <h2 id="time-suggestion-title">{viewerIsHost ? "Foreslåtte tider" : "Foreslå ny tid"}</h2>
            <p>
              {viewerIsHost
                ? "Velg et forslag for å flytte planen. Varigheten beholdes."
                : "Når passer det bedre for deg?"}
            </p>
          </div>
          <button type="button" className="icon-only" onClick={onClose} aria-label="Lukk">
            <X size={17} />
          </button>
        </div>

        {viewerIsHost ? (
          <div className="time-suggestion-list">
            {suggestions.map((suggestion) => (
              <article className="time-suggestion-review" key={suggestion.id}>
                <div className="time-suggestion-person">
                  <Avatar
                    name={suggestion.proposer_name}
                    avatarPath={suggestion.proposer_avatar_path}
                  />
                  <span>
                    <strong>{suggestion.proposer_name}</strong>
                    <small>{formatSuggestedTime(suggestion.proposed_starts_at)}</small>
                  </span>
                </div>
                <div className="time-suggestion-actions">
                  <button
                    type="button"
                    className="primary-action compact"
                    disabled={busy}
                    onClick={() => void onRespond(suggestion.id, true)}
                  >
                    <CheckCircle2 size={16} />
                    Bruk tiden
                  </button>
                  <button
                    type="button"
                    className="secondary-action compact"
                    disabled={busy}
                    onClick={() => void onRespond(suggestion.id, false)}
                  >
                    Ikke nå
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <>
            <div className="time-suggestion-options" role="radiogroup" aria-label="Velg ny tid">
              {options.map((option) => (
                <label className={`time-option ${selection === option.id ? "selected" : ""}`} key={option.id}>
                  <input
                    type="radio"
                    name="time-suggestion"
                    value={option.id}
                    checked={selection === option.id}
                    onChange={() => setSelection(option.id)}
                  />
                  <span>
                    <strong>{option.label}</strong>
                    <small>{formatSuggestedTime(option.value)}</small>
                  </span>
                </label>
              ))}
              <label className={`time-option custom ${selection === "custom" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="time-suggestion"
                  value="custom"
                  checked={selection === "custom"}
                  onChange={() => setSelection("custom")}
                />
                <span>
                  <strong>Velg selv</strong>
                  <input
                    type="datetime-local"
                    min={minimumValue}
                    value={customValue}
                    onFocus={() => setSelection("custom")}
                    onChange={(event) => {
                      setSelection("custom");
                      setCustomValue(event.target.value);
                    }}
                  />
                </span>
              </label>
            </div>
            {validationMessage && <p className="time-suggestion-validation">{validationMessage}</p>}
            <button
              type="button"
              className="primary-action time-suggestion-submit"
              disabled={busy || Boolean(validationMessage) || !fromLocalDateTimeValue(selectedValue)}
              onClick={() => {
                const proposed = fromLocalDateTimeValue(selectedValue);
                if (proposed) void onSubmit(proposed.toISOString());
              }}
            >
              <Send size={17} />
              {busy ? "Sender..." : ownSuggestion ? "Oppdater forslag" : "Send forslag"}
            </button>
            <p className="time-suggestion-note">Verten får forslaget ditt. Tiden endres først hvis verten godtar.</p>
          </>
        )}
      </section>
    </div>
  );
}

function FeaturedPlanCard({
  plan,
  currentUserId,
  currentUserIsGuest,
  onOpen,
  onPlanChanged,
  onMessage
}: {
  plan: PlanSummary;
  currentUserId: string;
  currentUserIsGuest: boolean;
  onOpen: () => void;
  onPlanChanged: () => Promise<void>;
  onMessage: (message: string) => void;
}) {
  const [expandedMap, setExpandedMap] = useState(false);
  const [showingAttendees, setShowingAttendees] = useState(false);
  const coordinate = planMapCoordinate(plan);
  const participationText = planParticipationText(plan);
  const activity = activityForPlan(plan.activity_id);
  const Icon = activity.icon;
  const participantText = participantSummaryText(plan);
  const locationDetail = plan.place_address || "Delt sted";

  return (
    <article className={`featured-plan-card ${coordinate ? "" : "no-map"}`}>
      {coordinate && <PlanMapPreview plan={plan} onOpen={onOpen} onExpand={() => setExpandedMap(true)} />}
      <div className="featured-body">
        <div className="featured-context">
          <span><CalendarDays size={15} /> {formatPlanTime(plan.starts_at, plan.ends_at)}</span>
          <PlanTimeSuggestionControl
            plan={plan}
            currentUserId={currentUserId}
            currentUserIsGuest={currentUserIsGuest}
            onPlanChanged={onPlanChanged}
            onMessage={onMessage}
          />
        </div>
        <button className="plain-title featured-title" onClick={onOpen}>
          <span className="plan-activity-icon" aria-hidden="true">
            <Icon size={21} />
          </span>
          <strong>{plan.title}</strong>
          <span>
            {plan.host_name} inviterer
            {plan.gjeng_name ? ` · ${plan.gjeng_name}` : ""}
          </span>
        </button>
        {plan.description && <p className="featured-description">{plan.description}</p>}
        <p className="featured-location">
          <MapPin size={16} />
          <span>
            <strong>{plan.place_name}</strong>
            <small>{locationDetail}</small>
          </span>
        </p>
        <div className="featured-footer">
          <button
            type="button"
            className="featured-participants"
            onClick={() => setShowingAttendees(true)}
            aria-label="Se hvem som blir med"
          >
            <AvatarCluster participants={plan.participant_preview} count={plan.participant_count} />
            <span>{participantText}</span>
          </button>
          <button className="join-pill" onClick={onOpen}>{participationText}</button>
        </div>
        <PlanEngagementBar plan={plan} onOpenComments={onOpen} onMessage={onMessage} />
      </div>
      {coordinate && expandedMap && <ExpandedMapModal plan={plan} onClose={() => setExpandedMap(false)} />}
      {showingAttendees && (
        <PlanAttendeesPeek
          participants={plan.participant_preview}
          participantCount={plan.participant_count}
          onClose={() => setShowingAttendees(false)}
        />
      )}
    </article>
  );
}

function activityForPlan(activityId: PlanActivityId | null | undefined): ActivityOption {
  return activityOptions.find((activity) => activity.id === activityId) ?? fallbackActivityOption;
}

function participantSummaryText(plan: Pick<PlanSummary, "participant_count" | "participant_preview">) {
  return participantSummaryFromFirst(plan.participant_preview[0], plan.participant_count);
}

function participantSummaryFromFirst(firstParticipant: PlanParticipant | undefined, participantCount: number) {
  if (!firstParticipant) {
    return `${participantCount} blir med`;
  }

  const otherCount = Math.max(0, participantCount - 1);
  if (otherCount === 0) return `${firstParticipant.full_name} blir med`;
  if (otherCount === 1) return `${firstParticipant.full_name} og 1 annen blir med`;
  return `${firstParticipant.full_name} og ${otherCount} andre blir med`;
}

function planParticipationText(plan: PlanSummary) {
  if (plan.viewer_participation_state === "joined" || plan.viewer_participation_state === "confirmed") {
    return "Du er med";
  }

  if (plan.capacity !== null && plan.participant_count >= plan.capacity) {
    return "Fullt";
  }

  return "Bli med";
}

function MemoryPlanRow({
  plan,
  onOpen,
  onMessage
}: {
  plan: PlanSummary;
  onOpen: () => void;
  onMessage: (message: string) => void;
}) {
  const status = plan.status === "cancelled" ? "Avlyst" : "Gjennomført";
  const participants =
    plan.participant_count === 1 ? "1 var med" : `${plan.participant_count} var med`;
  return (
    <article className="memory-row">
      <button type="button" className="memory-row-open" onClick={onOpen}>
      <div className="memory-thumb">
        <CalendarDays size={24} />
      </div>
      <div>
        <span>{status} · {formatPlanDate(plan.ends_at)}</span>
        <strong>{plan.title}</strong>
        <small>
          {plan.host_name} · {plan.gjeng_name ? `${plan.gjeng_name} · ` : ""}{participants}
        </small>
        {(plan.strava_activity_count ?? 0) > 0 && <StravaIndicator />}
      </div>
      <ChevronRight size={16} />
      </button>
      <PlanEngagementBar plan={plan} compact onOpenComments={onOpen} onMessage={onMessage} />
    </article>
  );
}

function SectionHeading({
  title,
  action,
  onAction
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) {
  return (
    <header className="section-heading">
      <h2>{title}</h2>
      {action && onAction && (
        <button type="button" className="text-button" onClick={onAction}>
          {action}
        </button>
      )}
    </header>
  );
}

function ParticipantRow({
  participant,
  busy,
  onAction,
  onOpenProfile
}: {
  participant: PlanParticipant;
  busy: boolean;
  onAction: () => void;
  onOpenProfile?: (userId: string) => void;
}) {
  const canAct = participant.relationship_state === "none" || participant.relationship_state === "incoming_request";
  const actionLabel = participant.relationship_state === "incoming_request" ? "Godta" : relationshipLabel(participant.relationship_state);
  const stateLabel = participant.is_host ? `Vert · ${relationshipLabel(participant.relationship_state)}` : relationshipLabel(participant.relationship_state);
  const joinedLabel = participant.joined_at ? `Ble med ${formatRelativeTime(participant.joined_at)}` : null;
  const identity = (
    <>
      <Avatar name={participant.full_name} avatarPath={participant.avatar_path} />
      <div>
        <strong>{participant.full_name}</strong>
        <span>{joinedLabel ? `${stateLabel} · ${joinedLabel}` : stateLabel}</span>
      </div>
    </>
  );

  return (
    <div className={`person-row ${onOpenProfile ? "person-row--linked" : ""}`}>
      {onOpenProfile ? (
        <button type="button" className="person-row-identity" onClick={() => onOpenProfile(participant.id)}>
          {identity}
        </button>
      ) : (
        identity
      )}
      {canAct ? (
        <button className="secondary-action compact" disabled={busy} onClick={onAction}>
          {busy ? "..." : actionLabel}
        </button>
      ) : (
        <span className="state-chip">{actionLabel}</span>
      )}
    </div>
  );
}

function ParticipantListModal({
  participants,
  participantCount,
  busyParticipantId,
  onClose,
  onAction,
  onOpenProfile
}: {
  participants: PlanParticipant[];
  participantCount: number;
  busyParticipantId: string | null;
  onClose: () => void;
  onAction: (participant: PlanParticipant) => void;
  onOpenProfile?: (userId: string) => void;
}) {
  const participantText = participantSummaryFromFirst(participants[0], participantCount);

  return (
    <div className="modal-backdrop">
      <section className="modal participants-modal">
        <div className="modal-header">
          <div>
            <h2>Deltakere</h2>
            <p>{participantText}</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk deltakere">
            <X size={17} />
          </button>
        </div>
        {participants.length === 0 ? (
          <p className="muted">Ingen deltakere ennå.</p>
        ) : (
          <div className="participant-list">
            {participants.map((participant) => (
              <ParticipantRow
                key={participant.id}
                participant={participant}
                busy={busyParticipantId === participant.id}
                onAction={() => onAction(participant)}
                onOpenProfile={onOpenProfile}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PlanAttendeesPeek({
  participants,
  participantCount,
  onClose
}: {
  participants: PlanParticipant[];
  participantCount: number;
  onClose: () => void;
}) {
  const hiddenCount = Math.max(0, participantCount - participants.length);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section
        className="modal attendees-peek"
        role="dialog"
        aria-label="Hvem blir med"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2>Blir med</h2>
            <p>{participantSummaryFromFirst(participants[0], participantCount)}</p>
          </div>
          <button className="icon-only" onClick={onClose} aria-label="Lukk">
            <X size={17} />
          </button>
        </div>
        {participants.length === 0 ? (
          <p className="muted">Ingen blir med ennå.</p>
        ) : (
          <div className="attendees-peek-list">
            {participants.map((participant) => (
              <div key={participant.id} className="attendees-peek-row">
                <Avatar name={participant.full_name} avatarPath={participant.avatar_path} />
                <span>
                  {participant.full_name}
                  {participant.is_host && <span className="host-tag"> · vert</span>}
                </span>
              </div>
            ))}
            {hiddenCount > 0 && (
              <p className="muted attendees-peek-more">
                +{hiddenCount} {hiddenCount === 1 ? "annen" : "andre"} blir med
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function PersonRow({
  person,
  action,
  onOpen,
  subtitle
}: {
  person: NameSearchResult | FriendRequest;
  action?: React.ReactNode;
  onOpen?: () => void;
  subtitle?: string;
}) {
  const identity = (
    <>
      <Avatar name={person.full_name} avatarPath={person.avatar_path} />
      <div>
        <strong>{person.full_name}</strong>
        {subtitle ? (
          <span>{subtitle}</span>
        ) : (
          "relationship_state" in person && <span>{relationshipLabel(person.relationship_state)}</span>
        )}
      </div>
    </>
  );

  return (
    <div className={`person-row ${onOpen ? "person-row--linked" : ""}`}>
      {onOpen ? (
        <button type="button" className="person-row-identity" onClick={onOpen}>
          {identity}
        </button>
      ) : (
        identity
      )}
      {action}
    </div>
  );
}

function RelationshipAction({
  state,
  onClick
}: {
  state: RelationshipState;
  onClick: () => void;
}) {
  const disabled = state !== "none";
  return (
    <button className="secondary-action compact" disabled={disabled} onClick={onClick}>
      {relationshipLabel(state)}
    </button>
  );
}

function friendSuggestionLabel(suggestion: FriendSuggestion) {
  if (suggestion.suggestion_reason === "mutual_friend") return "Felles venner";
  return "Foreslatt for nye pa Join";
}

function InviteFallback({ onCreate, inviteLink }: { onCreate: () => void; inviteLink: string | null }) {
  return (
    <div className="invite-fallback">
      <h3>Ingen nye treff</h3>
      <p>Søket viser personer du ikke allerede er venner med. Send en privat invitasjonslenke i stedet.</p>
      <button className="secondary-action compact" onClick={onCreate}>
        <Link2 size={17} />
        Inviter med lenke
      </button>
      {inviteLink && <code>{inviteLink}</code>}
    </div>
  );
}

function PageTitle({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <header className="page-title">
      <h1>{title}</h1>
      {action}
    </header>
  );
}

function EmptyState({
  title,
  text,
  action
}: {
  title: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <section className="empty-state">
      <p className="empty-title">{title}</p>
      <p>{text}</p>
      {action}
    </section>
  );
}

function LoadingScreen({ label = "Laster Join..." }: { label?: string }) {
  return (
    <main className="loading-screen">
      <div className="loader" />
      <p>{label}</p>
    </main>
  );
}

function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, 4200);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);

  return (
    <button className="toast" onClick={onDismiss}>
      {message}
    </button>
  );
}

function BrandBlock() {
  return (
    <div className="brand-block">
      <div className="brand-name">join</div>
      <h1>Gjør det enklere å joine</h1>
    </div>
  );
}

function Feature({
  icon: Icon,
  title,
  text
}: {
  icon: LucideIcon;
  title: string;
  text: string;
}) {
  return (
    <article className="feature">
      <Icon size={24} />
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </article>
  );
}

function NavButton({
  item,
  active,
  unreadCount,
  onClick
}: {
  item: (typeof navItems)[number];
  active: boolean;
  unreadCount: number;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button className={`nav-button ${active ? "active" : ""}`} onClick={onClick}>
      <span>
        <Icon size={19} />
        {unreadCount > 0 && <i>{unreadCount}</i>}
      </span>
      {item.label}
    </button>
  );
}

function CommentBody({
  body,
  onOpenProfile
}: {
  body: string;
  onOpenProfile: (userId: string) => void;
}) {
  return (
    <>
      {parseCommentSegments(body).map((segment, index) =>
        segment.type === "mention" ? (
          <button
            key={index}
            type="button"
            className="comment-mention"
            onClick={() => onOpenProfile(segment.id)}
          >
            @{segment.name}
          </button>
        ) : (
          <span key={index}>{segment.value}</span>
        )
      )}
    </>
  );
}

function Avatar({
  name,
  avatarPath,
  size
}: {
  name?: string | null;
  avatarPath?: string | null;
  size?: "large";
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const normalizedPath = normalizeAvatarPath(avatarPath);

  useEffect(() => {
    let cancelled = false;
    setImageUrl(null);
    setImageFailed(false);

    if (!normalizedPath || !isSupabaseConfigured) return;

    signedAvatarUrl(normalizedPath)
      .then((url) => {
        if (!cancelled) setImageUrl(url);
      })
      .catch(() => {
        if (!cancelled) setImageFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedPath]);

  return (
    <span className={`avatar ${size === "large" ? "large" : ""}`} aria-label={name || "Profilbilde"}>
      {imageUrl && !imageFailed ? (
        <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />
      ) : (
        initials(name)
      )}
    </span>
  );
}

function AvatarCluster({
  participants = [],
  count
}: {
  participants?: PlanParticipant[];
  count: number;
}) {
  const visibleParticipants = participants.slice(0, 3);
  const overflowCount = Math.max(0, count - visibleParticipants.length);

  return (
    <span className="avatar-cluster">
      {visibleParticipants.length > 0 ? (
        visibleParticipants.map((participant) => (
          <Avatar
            key={participant.id}
            name={participant.full_name}
            avatarPath={participant.avatar_path}
          />
        ))
      ) : (
        <>
          <span />
          <span />
          <span />
        </>
      )}
      {overflowCount > 0 && <em>+{overflowCount}</em>}
    </span>
  );
}

function FriendMini({ friend }: { friend: Friend }) {
  return (
    <div className="friend-mini">
      <Avatar name={friend.full_name} avatarPath={friend.avatar_path} />
      <div>
        <strong>{friend.full_name}</strong>
        <span>Inviter til plan</span>
      </div>
    </div>
  );
}

function IconButton({
  label,
  icon: Icon,
  onClick
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button className="icon-only" onClick={onClick} aria-label={label} title={label}>
      <Icon size={20} />
    </button>
  );
}

function SettingsRow({
  label,
  expanded,
  onClick
}: {
  label: string;
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`settings-row ${expanded ? "expanded" : ""}`}
      onClick={onClick}
      aria-expanded={expanded}
    >
      {label}
      <ChevronRight size={17} />
    </button>
  );
}

function toDateTimeLocal(date: Date): string {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

function defaultCreatePlanStart(now: Date = new Date()): Date {
  const afterWork = sameDayAt(now, 17, 0);
  if (afterWork.getTime() > now.getTime()) return afterWork;

  const tonight = sameDayAt(now, 19, 0);
  if (tonight.getTime() > now.getTime()) return tonight;

  return startInMinutes(now, 30);
}

function createPlanStartForPreset(presetId: CreatePlanTimePresetId, now: Date = new Date()): Date {
  if (presetId === "afterWork") return sameDayOrTomorrowAt(now, 17, 0);
  if (presetId === "tonight") return sameDayOrTomorrowAt(now, 19, 0);
  return startInMinutes(now, 30);
}

function sameDayOrTomorrowAt(now: Date, hours: number, minutes: number): Date {
  const date = sameDayAt(now, hours, minutes);
  if (date.getTime() > now.getTime()) return date;
  date.setDate(date.getDate() + 1);
  return date;
}

function sameDayAt(now: Date, hours: number, minutes: number): Date {
  const date = new Date(now);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function startInMinutes(now: Date, minutes: number): Date {
  const date = new Date(now.getTime() + minutes * 60 * 1000);
  date.setSeconds(0, 0);
  return date;
}

function planDurationMinutes(startsAt: string, endsAt: string): number {
  const duration = Math.round((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60000);
  return Number.isFinite(duration) && duration > 0 ? duration : 60;
}

function clampInteger(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

function errorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const normalized = message.toLowerCase();

  if (normalized.includes("email rate limit exceeded")) {
    return "For mange e-postforsøk. Vent litt før du prøver igjen, eller logg inn med Google i stedet.";
  }

  if (normalized.includes("email address") && normalized.includes("invalid")) {
    return "Denne e-postadressen kan ikke brukes til innlogging.";
  }

  if (message) return message;
  return "Noe gikk galt.";
}
